import React, { useState, useEffect } from 'react';
import {
    ChevronDown, ChevronUp, ChevronRight, Lock, User, Cpu, Plus, Trash2, Edit3, Eye, Save,
    Video, Type, Check, Loader, PlayCircle, BookOpen, RefreshCw, HelpCircle, Sparkles,
    AlertCircle, ArrowRight, CheckCircle2, Flag, Target, Lightbulb, CheckSquare, Square, Award,
    Layers, LayoutGrid, ExternalLink, Download, FileText, X, GraduationCap, Building, School, Copy
} from 'lucide-react';
import CrapEditorApi from '../../api/crapEditor';
import UserApi from '../../api/user';

/* ─────────────── DEFAULT DATA ─────────────── */
const DEFAULT_TIMELINE = [
    { id: 1, subtitle: 'Start Here  1 min', subtitleColor: '#4F46E5', title: 'WHAT IS C-RAP', description: '1 min intro how your journey works', status: 'Up Next', isLocked: false },
    { id: 2, subtitle: '4 modules  ~15 min each', subtitleColor: '#4F46E5', title: 'PERSONALITY DEVELOPMENT', description: 'Time Management, Active Listening, Speaking Up, Ownership', status: 'In Progress', isLocked: false },
    { id: 3, subtitle: '14 quick Questions  ~5 min', subtitleColor: '#4F46E5', title: 'ASSESSMENT', description: 'Your answers shape your personal road map', status: 'Completed', isLocked: false },
    { id: 4, subtitle: 'Career path  ~Instant', subtitleColor: '#4F46E5', title: 'ROADMAP', description: 'Get your personalized learning path based on your responses.', status: 'Not Started', isLocked: true },
    { id: 5, subtitle: 'Career path  •  Self-paced', subtitleColor: '#4F46E5', title: 'ENTRY LEVEL', description: 'Build core skills and complete beginner projects to create a strong foundation.', status: 'Not Started', isLocked: true },
    { id: 6, subtitle: 'Career path  •  Self-paced', subtitleColor: '#4F46E5', title: 'MEDIUM LEVEL', description: 'Develop practical experience through real-world projects and intermediate concepts.', status: 'Not Started', isLocked: true },
    { id: 7, subtitle: 'Career path  •  Self-paced', subtitleColor: '#4F46E5', title: 'ADVANCED LEVEL', description: 'Master specialized industry concepts, system design, and production workflows.', status: 'Not Started', isLocked: true },
    { id: 8, subtitle: 'Job Simulation  ~20 min each', subtitleColor: '#4F46E5', title: 'INTERNSHIPS', description: 'Experience real workplace tasks and test your job readiness.', status: 'Not Started', isLocked: true },
    { id: 9, subtitle: 'Resume  ~20 min each', subtitleColor: '#4F46E5', title: 'YOUR CV', description: 'Experience real workplace tasks and test your job readiness.', status: 'Not Started', isLocked: true },
];

const DEFAULT_SECTION1 = {
    introVideoUrl: '',
    introVideoR2Key: '',
    introDescription: 'How your journey works, and what you unlock at each stage — from character building to your personal roadmap.',
};

const STATIC_CAREER_CARDS = [
    { label: 'Personal Development', icon: '👤' },
    { label: 'Assessment', icon: '👥' },
    { label: 'Roadmap', icon: '🎯' },
    { label: 'Get the Job', icon: '🧭' },
];

/* 
  Module structure:
  {
    name, slug, type: 'content',
    introVideoR2Key: '',   // <-- 1 intro video per module
    introVideoUrl: '',     // local preview URL
    exercises: [           // each exercise = a Topic in the backend
      {
        name, slug,
        questions: [       // MCQs per exercise = Questions in the backend
          { question, options: [4], correctAnswer, difficulty }
        ]
      }
    ]
  }
*/
const DEFAULT_MODULES = [];

const STATUS_PILL = {
    'Up Next': { bg: '#f5f3ff', text: '#4C2A85', border: '#D9CBF0' },
    'In Progress': { bg: '#eff6ff', text: '#2563EB', border: '#BFDBFE' },
    'Completed': { bg: '#f0fdf4', text: '#059669', border: '#A7F3D0' },
    'Not Started': { bg: '#f9fafb', text: '#9ca3af', border: '#e5e7eb' },
};

const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/* ─────────────── HELPERS ─────────────── */
function InputField({ label, value, onChange, placeholder, type = 'text' }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {label && <label style={{ fontSize: 11, fontWeight: 700, color: '#5a5f7d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>}
            {type === 'textarea' ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e8e4f2', fontSize: 13, fontFamily: 'Inter, sans-serif', resize: 'vertical', outline: 'none', boxSizing: 'border-box', color: '#1e2478' }}
                    onFocus={(e) => (e.target.style.borderColor = '#612D9B')}
                    onBlur={(e) => (e.target.style.borderColor = '#e8e4f2')} />
            ) : (
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e8e4f2', fontSize: 13, fontFamily: 'Inter, sans-serif', outline: 'none', boxSizing: 'border-box', color: '#1e2478' }}
                    onFocus={(e) => (e.target.style.borderColor = '#612D9B')}
                    onBlur={(e) => (e.target.style.borderColor = '#e8e4f2')} />
            )}
        </div>
    );
}

/* ─────────────── S1 VIDEO PREVIEW (handles presigned URL errors gracefully) ─────────────── */
function S1VideoPreview({ src, fileName }) {
    const [canPlay, setCanPlay] = useState(true);

    if (!canPlay) {
        return (
            <div style={{ borderRadius: 8, padding: '12px 14px', background: '#f0fdf4', border: '1.5px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Check size={14} color="#059669" />
                <div>
                    <div style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>Video uploaded ✓</div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2, wordBreak: 'break-all' }}>{fileName}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>In-browser preview unavailable for cloud-stored videos</div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e8e4f2', background: '#0f172a' }}>
            <video
                src={src}
                controls
                crossOrigin="anonymous"
                style={{ width: '100%', display: 'block', maxHeight: 140 }}
                onError={() => setCanPlay(false)}
            >
                <source src={src} type="video/mp4" />
                <source src={src} type="video/webm" />
            </video>
        </div>
    );
}

/* ─────────────── SECTION 1 VIDEO UPLOADER ─────────────── */
function S1VideoUploader({ section1, setSection1, s1Uploading, s1Progress, s1Error, s1Success, handleS1Upload }) {
    const fileInputRef = React.useRef(null);
    const hasVideo = Boolean(section1.introVideoR2Key);

    useEffect(() => {
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    return (
        <div style={{ background: '#f9f6fb', borderRadius: 10, padding: 14, border: '1px solid #f0ebfa', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#612D9B', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Video size={12} /> Section 1 Intro Video
                </label>
                {hasVideo && !s1Uploading && (
                    <button onClick={() => fileInputRef.current?.click()}
                        style={{ fontSize: 10, fontWeight: 700, color: '#612D9B', background: '#f3eefa', border: '1px solid #d0c7e3', borderRadius: 20, padding: '3px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Video size={9} /> Replace Video
                    </button>
                )}
            </div>

            {!s1Uploading && section1.introVideoUrl && (
                <S1VideoPreview src={section1.introVideoUrl} fileName={section1.introVideoR2Key?.split('/').pop()} />
            )}
            {!s1Uploading && !section1.introVideoUrl && section1.introVideoR2Key && (
                <div style={{ borderRadius: 8, padding: '10px 12px', background: '#f0fdf4', border: '1.5px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Check size={13} color="#059669" />
                    <span style={{ fontSize: 11, color: '#059669', fontWeight: 600, wordBreak: 'break-all' }}>
                        {section1.introVideoR2Key.split('/').pop()}
                    </span>
                </div>
            )}

            {s1Success && (
                <div style={{ fontSize: 11, color: '#059669', background: '#f0fdf4', borderRadius: 6, padding: '6px 10px', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Check size={12} /> Section 1 video uploaded and saved to cloud!
                </div>
            )}

            {s1Error && (
                <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', borderRadius: 6, padding: '8px 10px', border: '1px solid #fca5a5', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 700 }}>✗ Upload Error:</span>
                    <span>{s1Error}</span>
                </div>
            )}

            {s1Uploading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#612D9B', fontWeight: 600 }}>
                            <Loader size={11} className="spin" /> Uploading to R2...
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#612D9B' }}>{s1Progress}%</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 99, background: '#e8e4f2', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${s1Progress}%`, background: 'linear-gradient(90deg, #612D9B, #9F6FD5)', borderRadius: 99, transition: 'width 0.2s' }} />
                    </div>
                </div>
            )}

            {!hasVideo && !s1Uploading && (
                <input
                    key={`s1-file-${section1.introVideoR2Key || 'empty'}`}
                    ref={fileInputRef}
                    type="file" accept="video/*"
                    onChange={handleS1Upload}
                    onClick={(e) => { e.target.value = ''; }}
                    style={{ fontSize: 12, cursor: 'pointer' }}
                />
            )}
            {hasVideo && (
                <input
                    key={`s1-file-${section1.introVideoR2Key}`}
                    ref={fileInputRef}
                    type="file" accept="video/*"
                    onChange={handleS1Upload}
                    onClick={(e) => { e.target.value = ''; }}
                    style={{ display: 'none' }}
                />
            )}
        </div>
    );
}

/* ─────────────── VIDEO UPLOADER ─────────────── */
function VideoUploader({ r2Key, localUrl, onUploaded }) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const fileInputRef = React.useRef(null);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validation
        if (!file.type.startsWith('video/')) {
            setUploadError('Please select a valid video file (.mp4, .webm, .mov, etc.)');
            return;
        }

        setUploading(true);
        setProgress(0);
        setUploadError(null);
        setUploadSuccess(false);

        try {
            console.log('[VideoUploader] 1. Requesting presigned URL for:', file.name, file.type, `${(file.size / 1024 / 1024).toFixed(2)} MB`);
            const presignRes = await CrapEditorApi.getVideoPresignedUrl({ fileName: file.name, contentType: file.type || 'video/mp4' });
            const presignData = presignRes.data?.data || presignRes.data;
            const url = presignData?.uploadUrl || presignData?.url || presignData?.presignedUrl;
            const key = presignData?.r2Key || presignData?.key;

            if (!url) {
                throw new Error('Server did not return an upload URL. Check backend /crap/v2/uploads/video/presign configuration.');
            }

            console.log('[VideoUploader] 2. Uploading binary directly to Cloudflare R2...');
            const uploadRes = await CrapEditorApi.uploadFileToR2(url, file, (evt) => {
                if (evt.total) {
                    setProgress(Math.round((evt.loaded * 100) / evt.total));
                }
            });

            if (uploadRes.status < 200 || uploadRes.status >= 300) {
                throw new Error(`Cloudflare R2 rejected upload with status ${uploadRes.status}`);
            }

            console.log('[VideoUploader] 3. Binary upload to R2 completed successfully! Key:', key);
            setUploadSuccess(true);
            const blobUrl = URL.createObjectURL(file);
            onUploaded({ r2Key: key, localUrl: blobUrl });
            setTimeout(() => setUploadSuccess(false), 4000);
        } catch (err) {
            console.error('[VideoUploader] Upload failed:', err);
            const errorMsg = err?.response?.data?.message || err?.message || 'Failed to upload video to Cloudflare R2 storage.';
            setUploadError(errorMsg);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#612D9B', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Video size={12} /> {(r2Key || localUrl) ? 'Module Intro Video' : 'Upload Intro Video'}
                </label>
                {(r2Key || localUrl) && !uploading && (
                    <button onClick={() => fileInputRef.current?.click()}
                        style={{ fontSize: 10, fontWeight: 700, color: '#612D9B', background: '#f3eefa', border: '1px solid #d0c7e3', borderRadius: 20, padding: '2px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Video size={9} /> Replace
                    </button>
                )}
            </div>

            {!uploading && localUrl && (
                <S1VideoPreview src={localUrl} fileName={r2Key?.split('/').pop()} />
            )}
            {!uploading && !localUrl && r2Key && (
                <div style={{ borderRadius: 8, padding: '10px 12px', background: '#f0fdf4', border: '1.5px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Check size={13} color="#059669" />
                    <span style={{ fontSize: 11, color: '#059669', fontWeight: 600, wordBreak: 'break-all' }}>{r2Key.split('/').pop()}</span>
                </div>
            )}

            {uploadSuccess && (
                <div style={{ fontSize: 11, color: '#059669', background: '#f0fdf4', borderRadius: 6, padding: '6px 10px', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Check size={12} /> Video successfully uploaded to R2. Remember to click "Save Changes" to save to the database!
                </div>
            )}

            {uploading && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#612D9B', fontWeight: 600 }}>
                            <Loader size={11} className="spin" /> Uploading to R2...
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#612D9B' }}>{progress}%</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 99, background: '#e8e4f2', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #612D9B, #9F6FD5)', borderRadius: 99, transition: 'width 0.2s' }} />
                    </div>
                </div>
            )}

            {uploadError && (
                <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', borderRadius: 6, padding: '8px 10px', border: '1px solid #fca5a5', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 700 }}>✗ Upload Error:</span>
                    <span>{uploadError}</span>
                    <span style={{ fontSize: 10, color: '#991b1b' }}>Please check network tab or try uploading again.</span>
                </div>
            )}

            {!(r2Key || localUrl) && !uploading && (
                <input ref={fileInputRef} type="file" accept="video/*" onChange={handleUpload}
                    style={{ fontSize: 12, cursor: 'pointer' }} />
            )}
            {(r2Key || localUrl) && (
                <input ref={fileInputRef} type="file" accept="video/*" onChange={handleUpload}
                    style={{ display: 'none' }} />
            )}
        </div>
    );
}

/* ─────────────── MODULE EDITOR (SECTION 2) ─────────────── */
function ModuleEditor({ mod, mIdx, onModuleChange, onRemove }) {
    const updateMod = (key, val) => onModuleChange({ ...mod, [key]: val });

    return (
        <div style={{ borderRadius: 10, border: '1.5px solid #e8e4f2', overflow: 'hidden', background: 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#f3eefa' }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#612D9B', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                    {mod.order || mIdx + 1}
                </div>
                <input
                    value={mod.name || ''}
                    onChange={(e) => updateMod('name', e.target.value)}
                    placeholder="Module Title (e.g. Time Management)"
                    style={{ flex: 1, border: 'none', background: 'transparent', fontWeight: 700, fontSize: 13, color: '#1e2478', fontFamily: 'Inter', outline: 'none' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: '#612D9B' }}>Order:</label>
                    <input
                        type="number"
                        min="1"
                        value={mod.order ?? (mIdx + 1)}
                        onChange={(e) => updateMod('order', parseInt(e.target.value, 10) || 1)}
                        style={{ width: 42, padding: '2px 4px', fontSize: 11, fontWeight: 700, borderRadius: 6, border: '1px solid #d0c7e3', textAlign: 'center', outline: 'none' }}
                    />
                </div>
                <button onClick={onRemove} title="Delete Module" style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <Trash2 size={14} />
                </button>
            </div>

            <div style={{ padding: '14px', background: 'white', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ background: '#f9f6fb', borderRadius: 8, padding: 12, border: '1px solid #f0ebfa' }}>
                    <VideoUploader
                        r2Key={mod.videoR2Key || mod.introVideoR2Key}
                        localUrl={mod.videoUrl || mod.introVideoUrl}
                        onUploaded={({ r2Key, localUrl }) => onModuleChange({ ...mod, videoR2Key: r2Key, videoUrl: localUrl })}
                    />
                </div>

                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 800, color: '#1e2478', textTransform: 'uppercase' }}>
                        <Cpu size={13} color="#612D9B" /> DeepSeek AI Prompt Configuration
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
                        Dynamic placeholders available: <code>{'{{course}}'}</code>, <code>{'{{year}}'}</code>, <code>{'{{cgpa}}'}</code>
                    </div>

                    <InputField
                        label="Exercise Prompt (MCQs Generator)"
                        value={mod.exercisePrompt || ''}
                        onChange={(v) => updateMod('exercisePrompt', v)}
                        placeholder="Generate 5-7 multiple-choice questions to assess time management skills for a {{course}} student in year {{year}} with a CGPA of {{cgpa}}. Return ONLY a JSON object: {&quot;questions&quot;: [{&quot;id&quot;: string, &quot;question&quot;: string, &quot;options&quot;: string[4]}]}"
                        type="textarea"
                    />

                    <InputField
                        label="Path Prompt (Comparison Roadmap Generator)"
                        value={mod.pathPrompt || ''}
                        onChange={(v) => updateMod('pathPrompt', v)}
                        placeholder="Compare this {{course}} student's (year {{year}}, CGPA {{cgpa}}) exercise answers against the ideal path for their field. Return ONLY a JSON object: {&quot;yourPath&quot;: string[5], &quot;priyasPath&quot;: string[5]}"
                        type="textarea"
                    />
                </div>
            </div>
        </div>
    );
}

/* ─────────────── SECTION 3 ASSESSMENT EDITOR (AI PROMPT FOR AI QUESTIONS + TREE OVERVIEW) ─────────────── */
const LOGICAL_SECTION3_ORDER = [
    'q1',
    'q2',
    'q3',
    'q5',
    'q6',
    'career_stream_ai',
    'q7',
    'countries_ai',
    'top_or_mid_uni',
    'study_plan',
    'self_projects',
    'self_projects_detail',
    'internships',
    'internship_detail',
    'courses',
    'courses_detail',
    'job_role_known',
    'job_role_pick',
    'spend',
    'likes_ai'
];

function orderQuestionsLogically(questions) {
    if (!Array.isArray(questions)) return [];
    return [...questions].sort((a, b) => {
        const idxA = LOGICAL_SECTION3_ORDER.indexOf(a.id);
        const idxB = LOGICAL_SECTION3_ORDER.indexOf(b.id);
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
        if (a.isStart) return -1;
        if (b.isStart) return 1;
        return 0;
    });
}

/* ─────────────── SECTION 3 ASSESSMENT EDITOR (AI PROMPT CONFIGURATION HUB) ─────────────── */
const DEFAULT_SECTION3_PROMPTS = {
    career_stream_ai: 'Evaluate the student\'s prior answers: {prior_answers}. Generate 4-5 high-demand career streams tailored for a {{course}} student in year {{year}} with GPA {{cgpa}}. Return JSON: {"options": [{"id": string, "text": string}]}',
    study_plan: 'Based on the student\'s background in {{course}} and prior answers {prior_answers}, generate 4-5 relevant Master\'s / Postgraduate higher study degree programs (e.g. for Political Science: MA in International Relations, Master of Public Policy, etc.; for Engineering: MS in specialized domain; for Commerce: MBA / MS in Finance). Return JSON: {"options": [{"id": string, "text": string}]}',
    countries_ai: 'Recommend top 5 destination countries for overseas higher studies based on the student\'s profile and prior answers: {prior_answers}. Return JSON: {"options": [{"id": string, "text": string}]}',
    job_role_pick: 'Based on the student\'s major / department {{course}} and prior answers {prior_answers}, generate 4-5 high-demand job roles specifically tailored for their academic background. Return JSON: {"options": [{"id": string, "text": string}]}',
    likes_ai: 'Based on the student\'s chosen career path and interests in {prior_answers}, generate 4 specialized domain focus areas they would like most. Return JSON: {"options": [{"id": string, "text": string}]}'
};

const AI_QUESTIONS_CONFIG = [
    {
        id: 'career_stream_ai',
        title: 'Career Stream Recommendation Prompt',
        topic: 'Career Direction',
        badge: 'Triggered when student picks "Job Placements"',
        description: 'DeepSeek evaluates the student\'s college, degree, department, GPA, and graduation year to generate 4-5 tailored career paths on the fly.',
        defaultPrompt: DEFAULT_SECTION3_PROMPTS.career_stream_ai,
        tokens: ['{prior_answers}', '{{course}}', '{{year}}', '{{cgpa}}', '{{college}}']
    },
    {
        id: 'study_plan',
        title: 'Postgraduate Study Plan Prompt',
        topic: 'Higher Education Specialization',
        badge: 'Triggered when student picks "Further Studies"',
        description: 'DeepSeek dynamically generates relevant Master\'s / Postgraduate degree paths tailored to the student\'s specific branch/major (e.g. Political Science, CS, Commerce, Arts).',
        defaultPrompt: DEFAULT_SECTION3_PROMPTS.study_plan,
        tokens: ['{prior_answers}', '{{course}}', '{{year}}', '{{cgpa}}', '{{college}}']
    },
    {
        id: 'countries_ai',
        title: 'Top Overseas Destination Countries Prompt',
        topic: 'Overseas Higher Education',
        badge: 'Triggered when student picks "Further Studies ➔ Overseas"',
        description: 'DeepSeek evaluates the student\'s branch and target degree to recommend the top 5 destination countries worldwide for higher education.',
        defaultPrompt: DEFAULT_SECTION3_PROMPTS.countries_ai,
        tokens: ['{prior_answers}', '{{course}}', '{{year}}', '{{cgpa}}']
    },
    {
        id: 'job_role_pick',
        title: 'Department Job Role Selection Prompt',
        topic: 'Field-Specific Dream Roles',
        badge: 'Triggered when student identifies target job role',
        description: 'DeepSeek generates 4-5 in-demand job roles tailored specifically to the student\'s department (e.g., Policy Analyst for Political Science, Financial Analyst for Commerce, AI Engineer for Tech).',
        defaultPrompt: DEFAULT_SECTION3_PROMPTS.job_role_pick,
        tokens: ['{prior_answers}', '{{course}}', '{{year}}', '{{cgpa}}']
    },
    {
        id: 'likes_ai',
        title: 'Interests & Passion Analysis Prompt',
        topic: 'Domain Specialization',
        badge: 'Triggered in the final step of Skills assessment',
        description: 'DeepSeek analyzes the student\'s self-projects, internships, budget, and desired role to generate 4 customized interest areas they would enjoy most.',
        defaultPrompt: DEFAULT_SECTION3_PROMPTS.likes_ai,
        tokens: ['{prior_answers}', '{{targetRole}}', '{{interests}}']
    }
];

function Section3AssessmentEditor({
    section3Questions,
    setSection3Questions,
    section3Prompts,
    setSection3Prompts,
    loadingS3,
    onRefreshS3,
    onSavePrompt,
    onSaveAllPrompts
}) {
    const [activePromptTab, setActivePromptTab] = useState('career_stream_ai');
    const [savingTab, setSavingTab] = useState(null);
    const [saveSuccessTab, setSaveSuccessTab] = useState(null);
    const [saveErrorTab, setSaveErrorTab] = useState(null);

    const handlePromptChange = (questionId, newPrompt) => {
        setSection3Prompts(prev => ({
            ...prev,
            [questionId]: newPrompt
        }));
    };

    const handleInsertToken = (questionId, token) => {
        const current = section3Prompts[questionId] || DEFAULT_SECTION3_PROMPTS[questionId] || '';
        handlePromptChange(questionId, current + ` ${token}`);
    };

    const handleSaveCurrent = async (questionId, promptVal) => {
        setSavingTab(questionId);
        setSaveSuccessTab(null);
        setSaveErrorTab(null);
        try {
            if (onSavePrompt) {
                await onSavePrompt(questionId, promptVal);
            }
            setSaveSuccessTab(questionId);
            setTimeout(() => setSaveSuccessTab(null), 3500);
        } catch (err) {
            console.error('[Section3AssessmentEditor] Save failed:', err);
            const msg = err?.response?.data?.message || err?.message || 'Failed to save prompt to backend';
            setSaveErrorTab(msg);
        } finally {
            setSavingTab(null);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header info card */}
            <div style={{ background: '#f5f0fb', borderRadius: 12, padding: '16px 18px', border: '1.5px solid #e8daf5', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase' }}>
                        <Sparkles size={16} color="#612D9B" />
                        Master AI Prompt Configuration (Section 3 Assessment)
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            onClick={onRefreshS3}
                            disabled={loadingS3}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#612D9B', background: 'white', border: '1px solid #d0c7e3', borderRadius: 20, padding: '4px 12px', cursor: loadingS3 ? 'not-allowed' : 'pointer' }}
                        >
                            <RefreshCw size={11} className={loadingS3 ? 'spin' : ''} /> Refresh Bank
                        </button>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                    Configure the <strong>Master AI Prompts</strong> that generate real-time adaptive choices for students in Section 3. These prompts are evaluated by DeepSeek along with the student's prior answers.
                </div>
            </div>

            {/* Prompt Selector Tabs */}
            <div style={{ display: 'flex', background: 'white', padding: 4, borderRadius: 12, border: '1.5px solid #E8E4F2', gap: 4 }}>
                {AI_QUESTIONS_CONFIG.map((qCfg, idx) => {
                    const isActive = activePromptTab === qCfg.id;
                    const hasCustom = Boolean(section3Prompts[qCfg.id]);

                    return (
                        <button
                            key={qCfg.id}
                            onClick={() => {
                                setActivePromptTab(qCfg.id);
                                setSaveSuccessTab(null);
                                setSaveErrorTab(null);
                            }}
                            style={{
                                flex: 1,
                                padding: '10px 14px',
                                borderRadius: 8,
                                border: 'none',
                                background: isActive ? '#581C87' : 'transparent',
                                color: isActive ? 'white' : '#475569',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <span>{idx + 1}. {qCfg.title.split(' ')[0]} {qCfg.title.split(' ')[1]}</span>
                            {hasCustom && (
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? '#A7F3D0' : '#10B981' }} />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Active AI Prompt Card */}
            {(() => {
                const currentCfg = AI_QUESTIONS_CONFIG.find(c => c.id === activePromptTab) || AI_QUESTIONS_CONFIG[0];
                const currentPromptVal = section3Prompts[currentCfg.id] !== undefined ? section3Prompts[currentCfg.id] : currentCfg.defaultPrompt;
                const isSavingThis = savingTab === currentCfg.id;
                const isSuccessThis = saveSuccessTab === currentCfg.id;

                return (
                    <div style={{ background: 'white', borderRadius: 14, border: '2px solid #D8B4FE', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 4px 20px rgba(88,28,135,0.06)' }}>
                        {/* Title & Badge */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <span style={{ fontSize: 10, fontWeight: 800, background: '#F3EEFA', color: '#581C87', padding: '3px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
                                        Question ID: {currentCfg.id}
                                    </span>
                                    <span style={{ fontSize: 10, fontWeight: 700, background: '#DCFCE7', color: '#166534', padding: '3px 8px', borderRadius: 6 }}>
                                        Topic: {currentCfg.topic}
                                    </span>
                                </div>
                                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#1E2478' }}>
                                    {currentCfg.title}
                                </h3>
                                <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                                    {currentCfg.description}
                                </div>
                            </div>

                            <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 99, background: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
                                {currentCfg.badge}
                            </span>
                        </div>

                        {/* Prompt Input Textarea */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: 11, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase' }}>
                                    DeepSeek Evaluation Prompt Template:
                                </label>
                                <span style={{ fontSize: 11, color: '#94A3B8' }}>
                                    {currentPromptVal.length} characters
                                </span>
                            </div>

                            <textarea
                                value={currentPromptVal}
                                onChange={(e) => handlePromptChange(currentCfg.id, e.target.value)}
                                rows={6}
                                placeholder={currentCfg.defaultPrompt}
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: 10,
                                    border: '1.5px solid #C084FC',
                                    fontSize: 12,
                                    fontFamily: 'monospace',
                                    outline: 'none',
                                    resize: 'vertical',
                                    lineHeight: 1.6,
                                    color: '#1E2478',
                                    background: '#FAF8FC',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Tokens Helper Bar */}
                        <div style={{ background: '#F8FAFC', padding: '10px 14px', borderRadius: 8, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748B' }}>Insert Context Tags:</span>
                                {currentCfg.tokens.map(token => (
                                    <button
                                        key={token}
                                        onClick={() => handleInsertToken(currentCfg.id, token)}
                                        style={{ background: 'white', border: '1px solid #CBD5E1', padding: '2px 8px', borderRadius: 6, fontSize: 11, color: '#581C87', fontWeight: 700, cursor: 'pointer' }}
                                    >
                                        + {token}
                                    </button>
                                ))}
                            </div>
                            <span style={{ fontSize: 11, color: '#94A3B8' }}>
                                Tokens are auto-filled with student profile values
                            </span>
                        </div>

                        {/* Error message display if any */}
                        {saveErrorTab && (
                            <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <AlertCircle size={14} color="#DC2626" /> {saveErrorTab}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #F1F5F9' }}>
                            <button
                                onClick={() => handlePromptChange(currentCfg.id, currentCfg.defaultPrompt)}
                                style={{ padding: '8px 18px', borderRadius: 99, border: '1px solid #CBD5E1', background: 'white', color: '#64748B', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                            >
                                Reset to Default
                            </button>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {isSuccessThis && (
                                    <span style={{ fontSize: 12, fontWeight: 700, color: '#166534', background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '6px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <CheckCircle2 size={14} color="#166534" /> Saved successfully to backend!
                                    </span>
                                )}

                                <button
                                    onClick={() => handleSaveCurrent(currentCfg.id, currentPromptVal)}
                                    disabled={isSavingThis}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        padding: '9px 24px',
                                        borderRadius: 99,
                                        border: 'none',
                                        background: isSavingThis ? '#9ca3af' : '#581C87',
                                        color: 'white',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: isSavingThis ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 2px 8px rgba(88,28,135,0.25)'
                                    }}
                                >
                                    {isSavingThis ? <><Loader size={13} className="spin" /> Saving...</> : <><Save size={13} /> Save Prompt to Backend</>}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

/* ─────────────── SECTION 4 / 5 ROADMAP EDITOR (CAREER ROADMAP CONFIG & STAGES) ─────────────── */
const DEFAULT_ROADMAP_PROMPT = `You are an expert career and learning path coach for engineering and technology students in India.

Student Profile & Assessment Context:
- Career Stream: {{career_stream}}
- Degree & Branch: {{degree}} in {{specialization}}
- Academic Year / Semester: {{year}}
- Current CGPA: {{cgpa}}
- Assessment Strengths: {{assessment_strengths}}
- Assessment Growth Areas: {{assessment_growth_areas}}
- Target Career Domain: {{target_domain}}
- Prior Assessment Answers: {{prior_answers}}

Generate a personalized 4-Stage Career Roadmap structured as an S-Curve progression:
1. Stage 1: Build the Foundation (core concepts, programming basics, essential foundational skills)
2. Stage 2: Build Working Confidence (practical pipelines, frameworks, and guided application projects)
3. Stage 3: Reach Advanced Level (production scale, advanced architecture, optimization, cloud deployment)
4. Stage 4: First Job Readiness (ATS portfolio showcase, resume refinement, interview simulation rounds)

For each stage, produce:
- title: string (Stage header title)
- roadLabel: string (Short highway label, e.g. "Build the Foundation")
- goalTitle: string (Clear milestone goal headline)
- goalDescription: string (What the student achieves in this stage)
- focusAreas: array of 4-6 specific skill tags / technologies
- actions: array of 3-4 actionable checklist tasks

Output must be valid JSON matching the schema:
{
  "targetRole": "{{target_domain}}",
  "stages": [
    {
      "id": 1,
      "number": "1",
      "title": "Building the Foundation",
      "roadLabel": "Build the Foundation",
      "goalTitle": "Move from Basic to Medium",
      "goalDescription": "What you'll achieve in this stage",
      "focusAreas": ["RAG Fundamentals", "Python Basics", "Prompting", "Vector Databases", "Data Handling"],
      "actions": [
        { "id": "a1", "text": "Learn core concepts", "completed": false },
        { "id": "a2", "text": "Practice simple tasks weekly", "completed": false },
        { "id": "a3", "text": "Build 1 beginner mini-project", "completed": false }
      ]
    }
  ]
}`;

const DEFAULT_ROADMAP_CONFIG = {
    targetRole: 'RAG Engineer',
    roadmapPrompt: DEFAULT_ROADMAP_PROMPT,
    stages: [
        {
            id: 1,
            number: '1',
            title: 'Building the Foundation',
            subtitle: 'Stage 1',
            roadLabel: 'Build the Foundation',
            status: 'current', // 'completed' | 'current' | 'locked'
            badgeText: 'You are here',
            upcomingStatus: 'Upcoming',
            goalTitle: 'Move from Basic to Medium',
            goalDescription: 'What you\'ll achieve',
            focusAreas: ['RAG Fundamentals', 'Python Basics', 'Prompting', 'Vector Databases', 'Data Handling'],
            actions: [
                { id: 'a1', text: 'Learn core concepts', completed: false },
                { id: 'a2', text: 'Practice simple tasks weekly', completed: false },
                { id: 'a3', text: 'Build 1 beginner mini-project', completed: false }
            ]
        },
        {
            id: 2,
            number: '2',
            title: 'Build Working Confidence',
            subtitle: 'Stage 2',
            roadLabel: 'Build Working confidence',
            status: 'locked',
            upcomingStatus: 'Locked',
            goalTitle: 'Practical Application & Pipeline Design',
            goalDescription: 'Master end-to-end data ingestion & semantic retrieval',
            focusAreas: ['LangChain / LlamaIndex', 'Chunking Strategies', 'Hybrid Search', 'Evaluation Frameworks', 'FastAPI'],
            actions: [
                { id: 'b1', text: 'Build a document Q&A application', completed: false },
                { id: 'b2', text: 'Integrate rerankers and contextual compression', completed: false },
                { id: 'b3', text: 'Benchmark retrieval accuracy (Ragas/TruLens)', completed: false }
            ]
        },
        {
            id: 3,
            number: '3',
            title: 'Reach Advanced Level',
            subtitle: 'Stage 3',
            roadLabel: 'Reach Advanced Level',
            status: 'locked',
            upcomingStatus: 'Locked',
            goalTitle: 'Production Deployment & Scale',
            goalDescription: 'Scale systems with agentic workflows and fine-tuning',
            focusAreas: ['Agentic RAG', 'Graph RAG', 'Fine-Tuning Embeddings', 'Caching & Latency', 'Docker & Cloud'],
            actions: [
                { id: 'c1', text: 'Develop multi-agent research assistant', completed: false },
                { id: 'c2', text: 'Deploy production pipeline on cloud with monitoring', completed: false },
                { id: 'c3', text: 'Mock interviews and portfolio showcase', completed: false }
            ]
        },
        {
            id: 4,
            number: '4',
            title: 'First Job Readiness',
            subtitle: 'Final Stage',
            roadLabel: 'First job',
            status: 'flag',
            upcomingStatus: 'Milestone Goal',
            goalTitle: 'Get Hired as a RAG Engineer',
            goalDescription: 'Land a high-impact role with tailored resume and interview confidence',
            focusAreas: ['System Design Interviews', 'CV Tailoring', 'GitHub Showcase', 'Company Match'],
            actions: [
                { id: 'd1', text: 'Finalize ATS-optimized CV', completed: false },
                { id: 'd2', text: 'Complete technical simulation rounds', completed: false },
                { id: 'd3', text: 'Apply with verified C-RAP certificate', completed: false }
            ]
        }
    ]
};

function Section4RoadmapEditor({
    roadmapConfig,
    setRoadmapConfig,
    onSaveRoadmap
}) {
    const config = roadmapConfig || DEFAULT_ROADMAP_CONFIG;
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [copied, setCopied] = useState(false);
    const textareaRef = React.useRef(null);

    const currentPrompt = config.roadmapPrompt ?? DEFAULT_ROADMAP_PROMPT;

    const updateConfigField = (field, val) => {
        setRoadmapConfig(prev => ({
            ...(prev || DEFAULT_ROADMAP_CONFIG),
            [field]: val
        }));
    };

    const insertVariable = (varTag) => {
        if (!textareaRef.current) {
            updateConfigField('roadmapPrompt', (currentPrompt || '') + ' ' + varTag);
            return;
        }
        const el = textareaRef.current;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const text = currentPrompt || '';
        const newText = text.substring(0, start) + varTag + text.substring(end);
        updateConfigField('roadmapPrompt', newText);
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + varTag.length, start + varTag.length);
        }, 10);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveSuccess(false);
        try {
            const updated = {
                ...(config || DEFAULT_ROADMAP_CONFIG),
                roadmapPrompt: currentPrompt
            };
            if (onSaveRoadmap) {
                await onSaveRoadmap(updated);
            } else {
                await CrapEditorApi.saveRoadmapPrompt(currentPrompt);
                localStorage.setItem('crap_v2_roadmap_config', JSON.stringify(updated));
            }
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3500);
        } catch (err) {
            console.error('Failed to save roadmap prompt:', err);
            alert('Failed to save prompt: ' + (err.response?.data?.message || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleResetPrompt = () => {
        if (window.confirm('Reset the Roadmap AI Prompt to the recommended default template?')) {
            updateConfigField('roadmapPrompt', DEFAULT_ROADMAP_PROMPT);
        }
    };

    const handleCopyPrompt = () => {
        navigator.clipboard.writeText(currentPrompt);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const STUDENT_VARS = [
        { label: 'Course / Branch', tag: '{{course}}' },
        { label: 'Academic Year', tag: '{{year}}' },
        { label: 'Current CGPA', tag: '{{cgpa}}' },
        { label: 'Career / Job Role', tag: '{{career}}' },
        { label: 'Career Stream', tag: '{{career_stream}}' },
        { label: 'Degree', tag: '{{degree}}' },
        { label: 'Specialization', tag: '{{specialization}}' },
        { label: 'Prior Answers', tag: '{{prior_answers}}' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Top Overview Banner */}
            <div style={{ background: '#f5f0fb', borderRadius: 14, padding: '16px 20px', border: '1.5px solid #e8daf5', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <Sparkles size={16} color="#612D9B" />
                        Section 4: Career Roadmap — AI Prompt Configuration
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#EDE9FE', color: '#581C87', border: '1px solid #DDD6FE' }}>
                            PUT /crap/v2/sections/4/prompt
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0' }}>
                            Step 4 Active
                        </span>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                    Configure the prompt that generates a personalized career roadmap for the student. The backend substitutes <code>{'{{course}}'}</code>, <code>{'{{year}}'}</code>, <code>{'{{cgpa}}'}</code>, and <code>{'{{career}}'}</code> (from Section 3 job role answer) at generation time.
                </div>
            </div>

            {/* Master AI Prompt Card */}
            <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1.5px solid #e8e4f2', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                {/* Header with quick stats */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e2478' }}>
                            Roadmap AI Generator Prompt
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            Click student variable chips below to insert placeholder tags into your prompt.
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                            {currentPrompt.length} characters | {currentPrompt.split(/\s+/).filter(Boolean).length} words
                        </span>
                    </div>
                </div>

                {/* Student Context Variables Toolbelt */}
                <div style={{ background: '#faf8fc', borderRadius: 10, padding: '12px 14px', border: '1px solid #eedfee', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Target size={13} color="#612D9B" /> Insert Student Profile Variables:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {STUDENT_VARS.map(v => (
                            <button
                                key={v.tag}
                                type="button"
                                onClick={() => insertVariable(v.tag)}
                                title={`Click to insert ${v.tag}`}
                                style={{
                                    background: 'white',
                                    color: '#612D9B',
                                    border: '1px solid #d8b4fe',
                                    borderRadius: 6,
                                    padding: '4px 10px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    fontFamily: 'monospace',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#612D9B'; e.currentTarget.style.color = 'white'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#612D9B'; }}
                            >
                                <Plus size={10} /> {v.tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* The Prompt Editor */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                            Prompt Template Body:
                        </label>
                        <button
                            type="button"
                            onClick={handleCopyPrompt}
                            style={{ background: 'none', border: 'none', color: '#612D9B', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                            <Copy size={12} /> {copied ? 'Copied to Clipboard!' : 'Copy Prompt'}
                        </button>
                    </div>
                    <textarea
                        ref={textareaRef}
                        value={currentPrompt}
                        onChange={(e) => updateConfigField('roadmapPrompt', e.target.value)}
                        rows={14}
                        placeholder="Generate a personalized career roadmap in json format for a {{course}} student in year {{year}} with a CGPA of {{cgpa}}, aiming for a career as a {{career}}. Return ONLY a JSON object: {&quot;milestones&quot;: [{&quot;title&quot;: string, &quot;description&quot;: string, &quot;timeframe&quot;: string}]}"
                        style={{
                            padding: '14px 16px',
                            borderRadius: 10,
                            border: '1.5px solid #d8b4fe',
                            fontSize: 12,
                            fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                            outline: 'none',
                            resize: 'vertical',
                            lineHeight: 1.65,
                            color: '#1e2478',
                            background: '#fafafc'
                        }}
                    />
                </div>

                {/* Action Buttons Row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <button
                        type="button"
                        onClick={handleResetPrompt}
                        style={{
                            padding: '8px 18px',
                            borderRadius: 8,
                            border: '1.5px solid #e2e8f0',
                            background: 'white',
                            color: '#64748b',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
                    >
                        Reset to Default
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {saveSuccess && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#166534', background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '6px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <CheckCircle2 size={14} color="#166534" /> Saved to backend successfully!
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                padding: '9px 24px',
                                borderRadius: 8,
                                border: 'none',
                                background: saving ? '#a855f7' : '#612D9B',
                                color: 'white',
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: saving ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                boxShadow: '0 2px 8px rgba(97,45,155,0.25)',
                                transition: 'all 0.2s'
                            }}
                        >
                            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                            {saving ? 'Saving to Backend...' : 'Save Roadmap Prompt'}
                        </button>
                    </div>
                </div>

                {/* Architecture Reminder Card */}
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0', fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                    💡 <strong>Backend Specification:</strong> Saving this prompt calls <code>PUT /crap/v2/sections/4/prompt</code>. When a student opens Section 4, the backend invokes the LLM using this prompt to generate their personalized milestones. Switch to <strong>Preview</strong> mode at the top to test the live student experience.
                </div>
            </div>
        </div>
    );
}

/* ─────────────── LIVE PREVIEW — SECTION 4 / 5 (S-CURVE ROADMAP & ACTION STAGES) ─────────────── */
function PreviewRoadmapSection({ roadmapConfig }) {
    const config = roadmapConfig || DEFAULT_ROADMAP_CONFIG;
    const [selectedStageIdx, setSelectedStageIdx] = useState(0);
    const [completedActionIds, setCompletedActionIds] = useState(new Set());

    const activeStage = config.stages[selectedStageIdx] || config.stages[0];

    const toggleAction = (actId) => {
        const nextSet = new Set(completedActionIds);
        if (nextSet.has(actId)) {
            nextSet.delete(actId);
        } else {
            nextSet.add(actId);
        }
        setCompletedActionIds(nextSet);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: '10px 0' }}>
            {/* Top Roadmap Highway Card */}
            <div style={{ background: '#f5f0fb', borderRadius: 20, padding: '32px 28px', border: '1.5px solid #EEDFEE', display: 'flex', flexDirection: 'column', gap: 24, position: 'relative' }}>
                {/* Title */}
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1E2478', letterSpacing: '-0.3px' }}>
                    Your road to becoming a <span style={{ color: '#612D9B', textDecoration: 'none' }}>{config.targetRole || 'RAG Engineer'}</span>
                </h2>

                {/* S-Curve Highway Container */}
                <div style={{ position: 'relative', width: '100%', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '20px 0' }}>
                    {/* SVG Highway Road Path with dashed road center */}
                    <svg
                        viewBox="0 0 900 200"
                        style={{ width: '100%', height: 'auto', overflow: 'visible' }}
                    >
                        {/* Shadow Road Bed */}
                        <path
                            d="M 20 160 C 220 160, 240 60, 450 60 C 660 60, 680 160, 880 140"
                            fill="none"
                            stroke="#8E8E93"
                            strokeWidth="48"
                            strokeLinecap="round"
                        />
                        {/* Inner Dashed White Dividing Line */}
                        <path
                            d="M 30 160 C 220 160, 240 60, 450 60 C 660 60, 680 160, 870 140"
                            fill="none"
                            stroke="#FFFFFF"
                            strokeWidth="3.5"
                            strokeDasharray="14 14"
                            strokeLinecap="round"
                        />
                    </svg>

                    {/* Node 1: Stage 1 (You are here - Circle '1') */}
                    <div
                        onClick={() => setSelectedStageIdx(0)}
                        style={{ position: 'absolute', left: '13%', top: '55%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 10 }}
                    >
                        {/* Top Label */}
                        <span style={{ position: 'absolute', bottom: 68, fontSize: 12, fontWeight: 800, color: '#1E2478', whiteSpace: 'nowrap' }}>
                            Build the Foundation
                        </span>

                        {/* Node Bubble */}
                        <div style={{
                            width: 58, height: 58, borderRadius: '50%',
                            background: '#4C2A85', color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 22, fontWeight: 900,
                            boxShadow: selectedStageIdx === 0 ? '0 0 0 6px rgba(97,45,155,0.25), 0 8px 20px rgba(76,42,133,0.4)' : '0 4px 14px rgba(0,0,0,0.15)',
                            transition: 'all 0.2s'
                        }}>
                            1
                        </div>

                        {/* Badge Below */}
                        <div style={{
                            marginTop: 10,
                            background: '#242C5B', color: 'white',
                            fontSize: 10, fontWeight: 800,
                            padding: '4px 12px', borderRadius: 99,
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 6px rgba(36,44,91,0.3)'
                        }}>
                            You are here
                        </div>
                    </div>

                    {/* Node 2: Stage 2 (Lock at peak) */}
                    <div
                        onClick={() => setSelectedStageIdx(1)}
                        style={{ position: 'absolute', left: '38%', top: '15%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 10 }}
                    >
                        {/* Node Bubble */}
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: '#E9E4F0',
                            border: '3px solid white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: selectedStageIdx === 1 ? '0 0 0 6px rgba(97,45,155,0.2)' : '0 4px 12px rgba(0,0,0,0.08)',
                            transition: 'all 0.2s'
                        }}>
                            <div style={{ width: 34, height: 34, borderRadius: 8, background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                <Lock size={18} color="white" />
                            </div>
                        </div>

                        {/* Label Below */}
                        <span style={{ marginTop: 12, fontSize: 12, fontWeight: 800, color: '#1E2478', whiteSpace: 'nowrap' }}>
                            Build Working confidence
                        </span>
                    </div>

                    {/* Node 3: Stage 3 (Lock at valley) */}
                    <div
                        onClick={() => setSelectedStageIdx(2)}
                        style={{ position: 'absolute', left: '64%', top: '56%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 10 }}
                    >
                        {/* Label Above */}
                        <span style={{ position: 'absolute', bottom: 66, fontSize: 12, fontWeight: 800, color: '#1E2478', whiteSpace: 'nowrap' }}>
                            Reach Advanced Level
                        </span>

                        {/* Node Bubble */}
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: '#E9E4F0',
                            border: '3px solid white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: selectedStageIdx === 2 ? '0 0 0 6px rgba(97,45,155,0.2)' : '0 4px 12px rgba(0,0,0,0.08)',
                            transition: 'all 0.2s'
                        }}>
                            <div style={{ width: 34, height: 34, borderRadius: 8, background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                <Lock size={18} color="white" />
                            </div>
                        </div>
                    </div>

                    {/* Node 4: Stage 4 (Flag at Destination) */}
                    <div
                        onClick={() => setSelectedStageIdx(3)}
                        style={{ position: 'absolute', left: '89%', top: '48%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 10 }}
                    >
                        {/* Node Bubble */}
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: '#FAF7FE',
                            border: '3px solid #612D9B',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: selectedStageIdx === 3 ? '0 0 0 6px rgba(97,45,155,0.25)' : '0 4px 14px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s'
                        }}>
                            <Flag size={22} color="#EAB308" fill="#EAB308" />
                        </div>

                        {/* Label Below */}
                        <span style={{ marginTop: 10, fontSize: 12, fontWeight: 800, color: '#1E2478', whiteSpace: 'nowrap' }}>
                            First job
                        </span>
                    </div>
                </div>
            </div>

            {/* Bottom Action / Goal Detail Card (Matches Screenshot exactly) */}
            {activeStage && (
                <div style={{ background: 'white', borderRadius: 20, padding: '24px 28px', border: '1.5px solid #F0EBFA', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Header with Navigation arrows */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F0EBFA', paddingBottom: 14 }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1E2478' }}>
                                {activeStage.title}
                            </h3>
                            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{activeStage.subtitle}</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: '#6B7280', background: '#F9FAFB', padding: '6px 12px', borderRadius: 99, border: '1px solid #E5E7EB' }}>
                                <span>{activeStage.upcomingStatus || 'Upcoming'}</span>
                                <Lock size={12} color="#9CA3AF" />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button
                                    disabled={selectedStageIdx <= 0}
                                    onClick={() => setSelectedStageIdx(prev => Math.max(0, prev - 1))}
                                    style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #E5E7EB', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: selectedStageIdx <= 0 ? 'not-allowed' : 'pointer', color: selectedStageIdx <= 0 ? '#D1D5DB' : '#374151' }}
                                >
                                    ←
                                </button>
                                <button
                                    disabled={selectedStageIdx >= config.stages.length - 1}
                                    onClick={() => setSelectedStageIdx(prev => Math.min(config.stages.length - 1, prev + 1))}
                                    style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #E5E7EB', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: selectedStageIdx >= config.stages.length - 1 ? 'not-allowed' : 'pointer', color: selectedStageIdx >= config.stages.length - 1 ? '#D1D5DB' : '#374151' }}
                                >
                                    →
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 3 Columns: GOAL | Focus Areas | Actions */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.3fr', gap: 28 }}>
                        {/* Column 1: GOAL */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: '#612D9B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <Target size={14} color="#612D9B" /> GOAL
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: '#1E2478', lineHeight: 1.3 }}>
                                {activeStage.goalTitle || 'Move from Basic to Medium'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#6B7280', marginTop: 12 }}>
                                <Lightbulb size={14} color="#612D9B" />
                                <span>{activeStage.goalDescription || 'What you\'ll achieve'}</span>
                            </div>
                        </div>

                        {/* Column 2: Focus Areas */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: '#612D9B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <Lightbulb size={14} color="#612D9B" /> Focus Areas
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                                {activeStage.focusAreas?.map((fa, faIdx) => (
                                    <div
                                        key={faIdx}
                                        style={{
                                            background: '#F3EEFA',
                                            borderRadius: 10,
                                            padding: '10px 14px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: '#4C2A85',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {fa}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Column 3: Actions Checklist */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: '#612D9B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                <CheckSquare size={14} color="#612D9B" /> Actions
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {activeStage.actions?.map((act) => {
                                    const isDone = completedActionIds.has(act.id);
                                    return (
                                        <div
                                            key={act.id}
                                            onClick={() => toggleAction(act.id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                cursor: 'pointer',
                                                fontSize: 13,
                                                color: isDone ? '#059669' : '#374151',
                                                fontWeight: 600,
                                                userSelect: 'none'
                                            }}
                                        >
                                            {isDone ? (
                                                <CheckSquare size={18} color="#059669" />
                                            ) : (
                                                <div style={{ width: 16, height: 16, borderRadius: 4, border: '2px solid #D1D5DB', background: 'white' }} />
                                            )}
                                            <span style={{ textDecoration: isDone ? 'line-through' : 'none' }}>
                                                {act.text}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─────────────── SECTION 5 PROMPT EDITOR (FOCUS AREAS, SELF PROJECTS, CERTIFICATIONS) ─────────────── */
const DEFAULT_FOCUS_AREAS_EASY_PROMPT = `You are an expert curriculum designer and universal learning engine.
Your task is to generate 4-6 foundational, introductory-level learning modules tailored specifically to a student in {{course}} (Year {{year}}, CGPA {{cgpa}}) pursuing a career as a {{career}}.

CRITICAL DOMAIN ADAPTATION RULES:
1. Tailor the terminology, methods, and examples directly to {{course}} and {{career}}:
   - For Science/Engineering: focus on core equations, coding/lab fundamentals, or design principles.
   - For Sociology/Psychology/Humanities/Political Science: focus on foundational theories, qualitative/quantitative research methods, survey design, or behavioral frameworks.
   - For Business/Commerce/Finance: focus on accounting principles, market fundamentals, financial models, or organizational behavior.
2. Every lesson must include a concrete, discipline-specific "whatItMeans" explanation and a hands-on "practiceLines" task or exercise relevant to their field.

Return ONLY a valid JSON object matching this exact schema:
{
  "topics": [
    {
      "id": "module-1",
      "title": "string (e.g., 'Foundations of [Core Subject Area]')",
      "tag": "BEGINNER",
      "duration": "45 mins",
      "description": "string (overview of this foundational module)",
      "lessons": [
        {
          "id": 1,
          "title": "string (lesson title)",
          "whatItMeans": "string (clear 1-2 sentence explanation of the core concept/theory/tool)",
          "practiceLines": [
            "string (step 1 or research activity to perform)",
            "string (step 2: analysis, observation, or calculation task)",
            "string (step 3: expected outcome or verification)"
          ],
          "practiceHint": "string (actionable tip or key checkpoint for this exercise)"
        }
      ]
    }
  ]
}`;
const DEFAULT_FOCUS_AREAS_MEDIUM_PROMPT = `List 8-10 MEDIUM-level topics (assume the EASY fundamentals are known) a {{course}} student (year {{year}}, CGPA {{cgpa}}) aiming to become a {{career}} should know next. Return ONLY a JSON object: {"topics": [{"title": string, "description": string}]}`;
const DEFAULT_FOCUS_AREAS_ADVANCED_PROMPT = `List 8-10 ADVANCED-level specialized topics a {{course}} student (year {{year}}, CGPA {{cgpa}}) aiming to become a {{career}} should master for high-impact industry roles. Return ONLY a JSON object: {"topics": [{"title": string, "description": string}]}`;
const DEFAULT_SELF_PROJECTS_PROMPT = `Suggest 5 self/portfolio project ideas in json format for a {{course}} student in year {{year}} with a CGPA of {{cgpa}}, aiming for a career as a {{career}}. Return ONLY a JSON object: {"projects": [{"name": string, "description": string}]}`;
const DEFAULT_CERTIFICATIONS_PROMPT = `Recommend 5 certificate courses (e.g. Coursera, Udemy) in json format for a {{course}} student in year {{year}} with a CGPA of {{cgpa}}, aiming for a career as a {{career}}. Return ONLY a JSON object: {"certifications": [{"name": string, "platform": string, "description": string, "link": string}]}`;
const DEFAULT_INTERNSHIPS_PROMPT = `Tailor the internships to the user's chosen career/job: {{job}}. make sure the response is in json with each object having one intership name, link`;

function Section5EntryLevelEditor({
    entryLevelConfig,
    setEntryLevelConfig
}) {
    const [activePromptTab, setActivePromptTab] = useState('focus_easy');
    const [prompts, setPrompts] = useState({
        focus_easy: entryLevelConfig?.aiPrompt || DEFAULT_FOCUS_AREAS_EASY_PROMPT,
        self_projects: entryLevelConfig?.selfProjectsPrompt || DEFAULT_SELF_PROJECTS_PROMPT,
        certifications: entryLevelConfig?.certificationsPrompt || DEFAULT_CERTIFICATIONS_PROMPT
    });
    const [savingTab, setSavingTab] = useState(null);
    const [saveSuccessTab, setSaveSuccessTab] = useState(null);
    const [copiedTab, setCopiedTab] = useState(null);
    const textareaRef = React.useRef(null);

    // Sync external changes
    useEffect(() => {
        if (entryLevelConfig?.aiPrompt) setPrompts(p => ({ ...p, focus_easy: entryLevelConfig.aiPrompt }));
        if (entryLevelConfig?.selfProjectsPrompt) setPrompts(p => ({ ...p, self_projects: entryLevelConfig.selfProjectsPrompt }));
        if (entryLevelConfig?.certificationsPrompt) setPrompts(p => ({ ...p, certifications: entryLevelConfig.certificationsPrompt }));
    }, [entryLevelConfig]);

    const PROMPT_TABS = [
        { id: 'focus_easy', label: '1. Focus Areas (Easy)', endpoint: 'PUT /crap/v2/sections/5/focus-areas/easy/prompt', desc: 'Generates fundamental easy-level topics tailored to the student profile.' },
        { id: 'self_projects', label: '2. Self Projects List', endpoint: 'PUT /crap/v2/sections/5/self-projects/prompt', desc: 'Generates 5 tailored portfolio beginner project ideas.' },
        { id: 'certifications', label: '3. Certifications', endpoint: 'PUT /crap/v2/sections/5/certifications/prompt', desc: 'Recommends top 5 certifications/courses with direct links.' }
    ];

    const currentTabInfo = PROMPT_TABS.find(t => t.id === activePromptTab) || PROMPT_TABS[0];
    const currentPromptVal = prompts[activePromptTab] || '';

    const handlePromptChange = (val) => {
        setPrompts(prev => ({ ...prev, [activePromptTab]: val }));
    };

    const insertVariable = (varTag) => {
        if (!textareaRef.current) {
            handlePromptChange((currentPromptVal || '') + ' ' + varTag);
            return;
        }
        const el = textareaRef.current;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const text = currentPromptVal || '';
        const newText = text.substring(0, start) + varTag + text.substring(end);
        handlePromptChange(newText);
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + varTag.length, start + varTag.length);
        }, 10);
    };

    const handleSavePrompt = async (tabId) => {
        const valToSave = prompts[tabId];
        setSavingTab(tabId);
        setSaveSuccessTab(null);
        try {
            if (tabId === 'focus_easy') {
                await CrapEditorApi.saveFocusAreasPrompt('easy', valToSave);
                setEntryLevelConfig(prev => ({ ...prev, aiPrompt: valToSave }));
            } else if (tabId === 'self_projects') {
                await CrapEditorApi.saveSelfProjectsPrompt(valToSave);
                setEntryLevelConfig(prev => ({ ...prev, selfProjectsPrompt: valToSave }));
            } else if (tabId === 'certifications') {
                await CrapEditorApi.saveCertificationsPrompt(valToSave);
                setEntryLevelConfig(prev => ({ ...prev, certificationsPrompt: valToSave }));
            }
            setSaveSuccessTab(tabId);
            setTimeout(() => setSaveSuccessTab(null), 3000);
        } catch (err) {
            console.error(`Failed to save prompt for ${tabId}:`, err);
            alert('Failed to save prompt: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingTab(null);
        }
    };

    const handleCopy = (tabId) => {
        navigator.clipboard.writeText(prompts[tabId] || '');
        setCopiedTab(tabId);
        setTimeout(() => setCopiedTab(null), 2000);
    };

    const handleReset = (tabId) => {
        const defaults = {
            focus_easy: DEFAULT_FOCUS_AREAS_EASY_PROMPT,
            self_projects: DEFAULT_SELF_PROJECTS_PROMPT,
            certifications: DEFAULT_CERTIFICATIONS_PROMPT
        };
        if (window.confirm('Reset this prompt to the recommended default template?')) {
            setPrompts(prev => ({ ...prev, [tabId]: defaults[tabId] }));
        }
    };

    const STUDENT_VARS = [
        { label: 'Course', tag: '{{course}}' },
        { label: 'Year', tag: '{{year}}' },
        { label: 'CGPA', tag: '{{cgpa}}' },
        { label: 'Career / Role', tag: '{{career}}' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header Banner */}
            <div style={{ background: '#f5f0fb', borderRadius: 14, padding: '16px 20px', border: '1.5px solid #e8daf5', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <Sparkles size={16} color="#612D9B" />
                        Section 5: Entry Level — AI Prompt Configuration
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#EDE9FE', color: '#581C87', border: '1px solid #DDD6FE' }}>
                            PUT /crap/v2/sections/5/focus-areas/easy/prompt
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0' }}>
                            Step 5 Active
                        </span>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                    Configure the AI prompts that generate Entry Level foundational topics, self-projects, and certifications tailored to the student.
                </div>
            </div>

            {/* Prompt sub-tab bar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: 'white', borderRadius: 10, padding: 4, border: '1.5px solid #e8e4f2', gap: 4 }}>
                {PROMPT_TABS.map(t => {
                    const isActive = activePromptTab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setActivePromptTab(t.id)}
                            style={{
                                padding: '9px 8px',
                                borderRadius: 8,
                                border: 'none',
                                background: isActive ? '#f3e8ff' : 'transparent',
                                color: isActive ? '#6b21a8' : '#64748b',
                                fontSize: 11,
                                fontWeight: 800,
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s',
                                borderBottom: isActive ? '2px solid #9333ea' : '2px solid transparent'
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Active Prompt Box */}
            <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1.5px solid #e8e4f2', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e2478' }}>
                            {currentTabInfo.label} Prompt Editor
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            {currentTabInfo.desc}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                            {currentPromptVal.length} characters
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: '#EDE9FE', color: '#581C87', border: '1px solid #DDD6FE', fontFamily: 'monospace' }}>
                            {currentTabInfo.endpoint}
                        </span>
                    </div>
                </div>

                {/* Variables Toolbelt */}
                <div style={{ background: '#faf8fc', borderRadius: 10, padding: '10px 14px', border: '1px solid #eedfee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase' }}>Insert Variables:</span>
                        {STUDENT_VARS.map(v => (
                            <button
                                key={v.tag}
                                type="button"
                                onClick={() => insertVariable(v.tag)}
                                style={{ background: 'white', color: '#612D9B', border: '1px solid #d8b4fe', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, fontFamily: 'monospace', cursor: 'pointer' }}
                            >
                                + {v.tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Textarea */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                            Prompt Body (JSON output expected):
                        </label>
                        <button
                            type="button"
                            onClick={() => handleCopy(activePromptTab)}
                            style={{ background: 'none', border: 'none', color: '#612D9B', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                            <Copy size={12} /> {copiedTab === activePromptTab ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                    <textarea
                        ref={textareaRef}
                        value={currentPromptVal}
                        onChange={(e) => handlePromptChange(e.target.value)}
                        rows={12}
                        style={{ padding: '14px 16px', borderRadius: 10, border: '1.5px solid #d8b4fe', fontSize: 12, fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace", outline: 'none', resize: 'vertical', lineHeight: 1.65, color: '#1e2478', background: '#fafafc' }}
                    />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <button
                        type="button"
                        onClick={() => handleReset(activePromptTab)}
                        style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                        Reset to Default
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {saveSuccessTab === activePromptTab && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#166534', background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '6px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <CheckCircle2 size={14} color="#166534" /> Saved successfully to backend!
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => handleSavePrompt(activePromptTab)}
                            disabled={savingTab === activePromptTab}
                            style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: savingTab === activePromptTab ? '#a855f7' : '#612D9B', color: 'white', fontSize: 12, fontWeight: 800, cursor: savingTab === activePromptTab ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 8px rgba(97,45,155,0.25)' }}
                        >
                            {savingTab === activePromptTab ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                            {savingTab === activePromptTab ? 'Saving...' : 'Save Prompt'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Section5PromptEditor = Section5EntryLevelEditor;

/* ─────────────── SECTION 5 DEFAULT CONFIG ─────────────── */
const DEFAULT_ENTRY_LEVEL_CONFIG = {
    sectionTitle: 'Build Your Core AI Skills',
    sectionDescription: 'Master the fundamentals required before moving into real-world AI projects. Complete lessons, practice exercises, and build confidence step by step.',
    sectionStatus: 'IN PROGRESS',
    aiPrompt: DEFAULT_FOCUS_AREAS_EASY_PROMPT,
    selfProjectsPrompt: DEFAULT_SELF_PROJECTS_PROMPT,
    certificationsPrompt: DEFAULT_CERTIFICATIONS_PROMPT,
    focusAreas: {
        modules: [
            {
                id: 'm1',
                icon: '🐍',
                iconBg: '#FEF9C3',
                iconColor: '#A16207',
                title: 'Python Programming',
                level: 'BEGINNER',
                totalLessons: 6,
                duration: '',
                status: 'in_progress',
                lessons: [
                    {
                        id: 'l1',
                        number: 1,
                        title: 'Variables & Data Types',
                        desc: '',
                        status: 'done',
                        highlightWord: 'Variables',
                        whatItMeans: 'Variables store information. Data types tell Python what kind of information it is — text, number, decimal, or true/false.',
                        exercise: "Open Python on your laptop. Write 4 lines:\nname = 'Rahul'\nage = 20\ncgpa = 8.5\nis_student = True\nThen print all 4. Run the file. You just created your first variables!"
                    },
                    {
                        id: 'l2',
                        number: 2,
                        title: 'Control Flow',
                        desc: '',
                        status: 'resume'
                    },
                    {
                        id: 'l3',
                        number: 3,
                        title: 'Functions',
                        desc: '',
                        status: 'in_progress'
                    },
                    {
                        id: 'l4',
                        number: 4,
                        title: 'Data Structures',
                        desc: '',
                        status: 'locked'
                    },
                    {
                        id: 'l5',
                        number: 5,
                        title: 'Object Oriented Programming',
                        desc: '',
                        status: 'locked'
                    },
                    {
                        id: 'l6',
                        number: 6,
                        title: 'File Handling & Error Handling',
                        desc: '',
                        status: 'locked'
                    },
                ]
            },
            {
                id: 'm2',
                icon: '➗',
                iconBg: '#FEF9C3',
                iconColor: '#D97706',
                title: 'Mathematics Foundations',
                level: 'BEGINNER',
                totalLessons: 5,
                duration: '70 min',
                status: 'locked',
                lessons: []
            }
        ]
    },
    selfProjects: {
        categories: ['Healthcare', 'FinTech', 'E-Commerce', 'Cybersecurity', 'AI & ML'],
        projects: [
            {
                id: 'p1',
                category: 'Healthcare',
                title: 'Credit Card Fraud Detection using',
                titleHighlight: 'Ensemble Learning',
                level: 'BEGINNER',
                description: 'Build a binary classifier to detect fraudulent transactions in real time,addressing severe class imbalance (~0.17% fraud) using SMOTE and ensemble methods. Simulate a production scoring pipeline.',
                techStack: ['Python 3.11', 'XGBoost', 'LightGBM', 'SMOTE', 'Flask API', 'Docker', 'SHAP'],
                aiPrompt: '',
                steps: [
                    {
                        id: 's1',
                        number: 1,
                        title: '1. Problem Understanding',
                        desc: 'Study payment fraud types and define the right evaluation metrics for imbalanced classification.',
                        status: 'done'
                    },
                    {
                        id: 's2',
                        number: 2,
                        title: '2. Environment Setup',
                        desc: 'Create a virtual environment and organise the project folder structure.',
                        status: 'done'
                    },
                    {
                        id: 's3',
                        number: 3,
                        title: '3. Data Acquisition',
                        desc: 'Download and inspect the Kaggle Credit Card Fraud Dataset (284,807 rows, 30 features).',
                        status: 'done'
                    },
                    {
                        id: 's4',
                        number: 4,
                        title: '4. Exploratory Data Analysis (EDA)',
                        desc: 'Visualise class imbalance, feature distributions, and identify outliers.',
                        status: 'active',
                        bullets: [
                            'Plot class distribution with pie chart + bar chart — confirm 99.83% vs 0.17% split',
                            'Correlation heatmap of V1-V28 PCA features using Seaborn',
                            'Box plots of Amount and Time by class (fraud vs non-fraud)',
                            'Identify outliers using IQR method on the Amount column'
                        ],
                        keyCDAFinding: 'Fraudulent transactions tend to have lower Amount values and cluster at specific Time ranges. V14 and V17 are typically the most discriminative PCA features.'
                    },
                    {
                        id: 's5',
                        number: 5,
                        title: 'Feature Engineering',
                        desc: 'Train Logistic Regression, Random Forest, XGBoost, LightGBM with StratifiedKFold CV.',
                        status: 'upcoming'
                    },
                    {
                        id: 's6',
                        number: 6,
                        title: 'Handling class imbalance',
                        desc: 'Train Logistic Regression, Random Forest, XGBoost, LightGBM with StratifiedKFold CV.',
                        status: 'upcoming'
                    },
                    {
                        id: 's7',
                        number: 7,
                        title: 'Model Training',
                        desc: 'Train Logistic Regression, Random Forest, XGBoost, LightGBM with StratifiedKFold CV.',
                        status: 'upcoming'
                    },
                    {
                        id: 's8',
                        number: 8,
                        title: 'Evaluation',
                        desc: 'Measure Precision, Recall, F1, ROC-AUC, PR-AUC. Apply business cost metric.',
                        status: 'upcoming'
                    },
                    {
                        id: 's9',
                        number: 9,
                        title: 'Explainability (SHAP)',
                        desc: 'Use SHAP TreeExplainer to show which features drive each fraud prediction.',
                        status: 'upcoming'
                    },
                    {
                        id: 's10',
                        number: 10,
                        title: 'API Deployment (Flask)',
                        desc: 'Wrap the best model in a Flask REST API: POST /predict -> fraud_probability + decision.',
                        status: 'upcoming'
                    },
                    {
                        id: 's11',
                        number: 11,
                        title: 'Dockerize',
                        desc: 'Containerise the Flask API with Docker for reproducible deployment.',
                        status: 'upcoming'
                    },
                    {
                        id: 's12',
                        number: 12,
                        title: 'Documentation',
                        desc: 'Write README with architecture diagram, API docs, and full Jupyter narrative.',
                        status: 'upcoming'
                    }
                ],
                references: [
                    { title: 'Kaggle Credit Card Fraud Dataset', url: 'kaggle.com' },
                    { title: 'SMOTE: Synthetic Minority Over-sampling Technique (Paper)', url: 'arxiv.org' },
                    { title: 'Imbalanced-learn Documentation', url: 'imbalanced-learn.org' },
                    { title: 'XGBoost Model Guide', url: 'xgboost.readthedocs.io' },
                    { title: 'SHAP Library Documentation', url: 'shap.readthedocs.io' },
                    { title: 'Flask REST API Quickstart', url: 'flask.palletsprojects.com' },
                    { title: 'Docker Get Started Guide', url: 'docs.docker.com' },
                    { title: 'Scikit-learn Model Evaluation Metrics', url: 'scikit-learn.org' },
                ]
            }
        ]
    },
    certifications: {
        items: [
            { id: 'c1', initial: 'G', initialBg: '#E0F2FE', initialColor: '#0369A1', title: 'Google Data Analytics Professional Certificate', provider: 'Google', url: 'coursera.org/professional-certificates/googl...', duration: '6 months', price: 'Free (Coursera)', status: 'Completed' },
            { id: 'c2', initial: 'C', initialBg: '#F3E8FF', initialColor: '#7E22CE', title: 'Machine Learning Specialization', provider: 'Coursera / Stanford', url: 'coursera.org/specializations/machine-learnin...', duration: '3 months', price: '$49/month', status: 'In Progress' },
            { id: 'c3', initial: 'A', initialBg: '#E2E8F0', initialColor: '#334155', title: 'AWS Cloud Practitioner Essentials', provider: 'Amazon Web Services', url: 'aws.training/details/class?id=27058', duration: '6 hours', price: 'Free', status: 'Completed' },
            { id: 'c4', initial: 'C', initialBg: '#F3E8FF', initialColor: '#7E22CE', title: 'UI/UX Design Specialization', provider: 'Coursera / CalArts', url: 'coursera.org/specializations/ui-ux-design', duration: '4 months', price: '$49/month', status: 'Not Started' },
            { id: 'c5', initial: 'M', initialBg: '#E0F2FE', initialColor: '#0369A1', title: 'Azure Fundamentals AZ-900', provider: 'Microsoft Learn', url: 'learn.microsoft.com/en-us/credentials/certifi...', duration: '12 hours', price: 'Free', status: 'Not Started' },
        ]
    }
};

/* ─────────────── SECTION 6 DEFAULT CONFIG ─────────────── */
const DEFAULT_INTERNSHIPS_CONFIG = {
    aiPrompt: DEFAULT_INTERNSHIPS_PROMPT,
    listings: [
        {
            id: 'int_1',
            company: 'Google',
            jobDescription: 'Build scalable machine learning infrastructure and data pipelines for Google Search and Cloud AI.',
            role: 'Software Engineer Intern - Machine Learning',
            location: 'Bengaluru / Hyderabad, India',
            duration: '6 Months (Summer 2026)',
            stipend: '₹1,20,000 / month',
            applyUrl: 'https://careers.google.com/jobs/results/'
        },
        {
            id: 'int_2',
            company: 'Microsoft',
            jobDescription: 'Develop next-gen AI copilot features, fine-tune transformer models, and optimize inference latencies.',
            role: 'AI / Data Science Research Intern',
            location: 'Hyderabad, India (Hybrid)',
            duration: '3-6 Months',
            stipend: '₹1,10,000 / month',
            applyUrl: 'https://careers.microsoft.com/'
        },
        {
            id: 'int_3',
            company: 'Amazon',
            jobDescription: 'Design high-throughput backend services and analytics dashboards powering AWS intelligent search.',
            role: 'Software Development Engineer Intern (SDE)',
            location: 'Chennai / Bengaluru, India',
            duration: '6 Months',
            stipend: '₹90,000 / month',
            applyUrl: 'https://amazon.jobs/'
        },
        {
            id: 'int_4',
            company: 'Flipkart',
            jobDescription: 'Work with the personalization algorithms team on multi-armed bandit recommendation engines.',
            role: 'Applied Science Intern',
            location: 'Bengaluru, India',
            duration: '6 Months',
            stipend: '₹80,000 / month',
            applyUrl: 'https://flipkartcareers.com/'
        }
    ]
};

/* ─────────────── PREVIEW — SECTION 5: ENTRY LEVEL (PIXEL PERFECT AS PER SCREENSHOTS) ─────────────── */
function PreviewSection5EntryLevel({ entryLevelConfig }) {
    const cfg = entryLevelConfig || DEFAULT_ENTRY_LEVEL_CONFIG;
    const [activeTab, setActiveTab] = useState('focus');
    const [expandedModuleId, setExpandedModuleId] = useState('m1');
    const [expandedLessonId, setExpandedLessonId] = useState('l1');
    const [activeCategory, setActiveCategory] = useState(cfg.selfProjects?.categories?.[0] || 'Healthcare');
    const [expandedStepId, setExpandedStepId] = useState('s4');

    const tabs = [
        { id: 'focus', label: 'Focus Areas' },
        { id: 'projects', label: 'Self Projects' },
        { id: 'certs', label: 'Courses/ Certifications' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: "'Outfit','Inter',sans-serif" }}>
            {/* 3 Prominent Card Tabs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
                {tabs.map(t => {
                    const isActive = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                background: isActive ? '#F3EDFC' : 'white',
                                color: '#581C87',
                                borderRadius: 14,
                                border: isActive ? '1.5px solid #E9D5FF' : '1.5px solid #F0EBFA',
                                padding: '16px 16px',
                                fontWeight: 800,
                                fontSize: 14,
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: isActive ? '0 2px 8px rgba(88,28,135,0.06)' : '0 1px 3px rgba(0,0,0,0.02)'
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* ═════════════════ TAB 1: FOCUS AREAS ═════════════════ */}
            {activeTab === 'focus' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Top Banner Card */}
                    <div style={{ background: 'white', borderRadius: 16, padding: '24px 28px', border: '1.5px solid #F0EBFA', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#111827', lineHeight: 1.2 }}>
                                Build Your <span style={{ color: '#581C87' }}>{cfg.sectionTitle?.replace('Build Your ', '') || 'Core AI Skills'}</span>
                            </h2>
                            <span style={{ fontSize: 10, fontWeight: 800, padding: '5px 14px', borderRadius: 999, background: '#1E293B', color: 'white', letterSpacing: '0.5px' }}>
                                {cfg.sectionStatus || 'IN PROGRESS'}
                            </span>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.6, maxWidth: 800 }}>
                            {cfg.sectionDescription}
                        </p>
                    </div>

                    {/* Learning Modules Section */}
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 14 }}>Learning Modules</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {cfg.focusAreas?.modules?.map(mod => {
                                const isModExpanded = expandedModuleId === mod.id;
                                return (
                                    <div key={mod.id} style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                        {/* Module Header */}
                                        <div
                                            onClick={() => mod.status !== 'locked' && setExpandedModuleId(isModExpanded ? null : mod.id)}
                                            style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: mod.status !== 'locked' ? 'pointer' : 'default' }}>
                                            {/* Icon */}
                                            <div style={{ width: 44, height: 44, borderRadius: 12, background: mod.iconBg || '#FEF9C3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900, color: mod.iconColor || '#111', flexShrink: 0 }}>
                                                {mod.icon}
                                            </div>
                                            {/* Title + Badges */}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{mod.title}</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: '#DBEAFE', color: '#1E40AF', letterSpacing: '0.4px' }}>
                                                        {mod.level}
                                                    </span>
                                                    <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>
                                                        {mod.totalLessons} Lessons{mod.duration ? `   ${mod.duration}` : ''}
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Right status */}
                                            {mod.status === 'locked' ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 700, color: '#6B7280' }}>
                                                    Locked <ChevronDown size={16} />
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '5px 14px', borderRadius: 999, background: '#1E293B', color: 'white', letterSpacing: '0.4px' }}>
                                                        IN PROGRESS
                                                    </span>
                                                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>
                                                        {isModExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Individual Lesson Cards List */}
                                        {isModExpanded && mod.lessons?.length > 0 && (
                                            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                {mod.lessons.map((lesson, lIdx) => {
                                                    const isLessonExpanded = expandedLessonId === lesson.id && lesson.status === 'done';
                                                    return (
                                                        <div key={lesson.id}>
                                                            {/* Standalone Lesson Row Card */}
                                                            <div
                                                                onClick={() => lesson.status === 'done' && setExpandedLessonId(isLessonExpanded ? null : lesson.id)}
                                                                style={{
                                                                    border: '1.5px solid #E5E7EB',
                                                                    borderRadius: 12,
                                                                    padding: '13px 16px',
                                                                    background: 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 12,
                                                                    cursor: lesson.status === 'done' ? 'pointer' : 'default',
                                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.01)'
                                                                }}>
                                                                {/* Status circle */}
                                                                {lesson.status === 'done' ? (
                                                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                        <Check size={14} color="white" strokeWidth={3} />
                                                                    </div>
                                                                ) : lesson.status === 'locked' ? (
                                                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#F1F5F9', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                        <Lock size={12} color="#94A3B8" />
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#F1F5F9', border: '1px solid #E2E8F0', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                                                                        {lIdx + 1}
                                                                    </div>
                                                                )}

                                                                {/* Title */}
                                                                <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#111827' }}>
                                                                    {lesson.title}
                                                                </span>

                                                                {/* Right Badges */}
                                                                {lesson.status === 'done' && (
                                                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 10px', borderRadius: 999, background: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0', letterSpacing: '0.4px' }}>
                                                                        DONE
                                                                    </span>
                                                                )}
                                                                {lesson.status === 'resume' && (
                                                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '4px 14px', borderRadius: 999, background: '#581C87', color: 'white', letterSpacing: '0.4px' }}>
                                                                        RESUME
                                                                    </span>
                                                                )}
                                                                {lesson.status === 'in_progress' && (
                                                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '4px 14px', borderRadius: 999, background: '#1E293B', color: 'white', letterSpacing: '0.4px' }}>
                                                                        IN PROGRESS
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Expanded Lesson Detail (WHAT IT MEANS + PRACTICE EXERCISE) */}
                                                            {isLessonExpanded && lesson.whatItMeans && (
                                                                <div style={{ padding: '14px 16px 6px 16px' }}>
                                                                    {/* WHAT IT MEANS */}
                                                                    <div style={{ fontSize: 10, fontWeight: 800, color: '#6B7280', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 6 }}>
                                                                        WHAT IT MEANS
                                                                    </div>
                                                                    <div style={{ background: '#F8FAFC', borderRadius: 10, padding: '14px 18px', border: '1px solid #E2E8F0', fontSize: 13, color: '#334155', lineHeight: 1.6, marginBottom: 14 }}>
                                                                        {lesson.highlightWord ? (
                                                                            <>
                                                                                <span style={{ color: '#6D28D9', fontWeight: 700 }}>{lesson.highlightWord}</span>
                                                                                {lesson.whatItMeans.replace(lesson.highlightWord, '')}
                                                                            </>
                                                                        ) : lesson.whatItMeans}
                                                                    </div>

                                                                    {/* PRACTICE EXERCISE */}
                                                                    {lesson.exercise && (
                                                                        <>
                                                                            <div style={{ fontSize: 10, fontWeight: 800, color: '#6B7280', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 6 }}>
                                                                                PRACTICE EXERCISE
                                                                            </div>
                                                                            <div style={{ background: '#F8FAFC', borderRadius: 10, padding: '16px 18px', border: '1px solid #E2E8F0', fontSize: 13, color: '#1E293B', lineHeight: 1.8, whiteSpace: 'pre-line', fontFamily: 'inherit' }}>
                                                                                {lesson.exercise}
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════ TAB 2: SELF PROJECTS ═════════════════ */}
            {activeTab === 'projects' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {/* Category Filter Pills */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {cfg.selfProjects?.categories?.map(cat => {
                            const isCatActive = activeCategory.toLowerCase() === cat.toLowerCase();
                            return (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    style={{
                                        padding: '6px 18px',
                                        borderRadius: 999,
                                        border: isCatActive ? '1.5px solid #581C87' : '1.5px solid #E5E7EB',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        background: isCatActive ? '#581C87' : 'white',
                                        color: isCatActive ? 'white' : '#4B5563',
                                        boxShadow: isCatActive ? '0 2px 6px rgba(88,28,135,0.15)' : 'none'
                                    }}
                                >
                                    {cat}
                                </button>
                            );
                        })}
                    </div>

                    {/* Project Card */}
                    {cfg.selfProjects?.projects?.map(proj => (
                        <div key={proj.id} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            {/* Project Header Banner Card */}
                            <div style={{ background: 'linear-gradient(180deg, #FBF9FE 0%, #FFFFFF 100%)', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '22px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#111827', lineHeight: 1.4, flex: 1, paddingRight: 16 }}>
                                        {proj.title} <span style={{ color: '#581C87' }}>{proj.titleHighlight}</span>
                                    </h3>
                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '5px 14px', borderRadius: 999, background: '#1E293B', color: 'white', whiteSpace: 'nowrap', letterSpacing: '0.4px' }}>
                                        IN PROGRESS
                                    </span>
                                </div>
                                <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                                    {proj.description}
                                </p>
                                {/* Tech Stack Tags */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {proj.techStack?.map((t, i) => (
                                        <span key={i} style={{ background: '#F1F5F9', color: '#334155', fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 8 }}>
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Step-by-Step Guide Card */}
                            <div style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                    <Layers size={18} color="#581C87" />
                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Step-by-Step Guide</div>
                                    <span style={{ background: '#F3E8FF', color: '#6B21A8', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>
                                        {proj.steps?.length} Steps
                                    </span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                    {proj.steps?.map((step, sIdx) => {
                                        const isActive = step.status === 'active';
                                        const isDone = step.status === 'done';
                                        const isExpanded = expandedStepId === step.id;

                                        return (
                                            <div key={step.id} style={{ borderBottom: sIdx < proj.steps.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                                                {/* Step Row */}
                                                <div
                                                    onClick={() => setExpandedStepId(isExpanded ? null : step.id)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', cursor: 'pointer' }}>
                                                    {/* Step Circle */}
                                                    {isDone ? (
                                                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <Check size={14} color="white" strokeWidth={3} />
                                                        </div>
                                                    ) : isActive ? (
                                                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#1E293B', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                                                            {step.number}
                                                        </div>
                                                    ) : (
                                                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                                                            {step.number}
                                                        </div>
                                                    )}

                                                    {/* Title & Desc */}
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: isDone ? '#1E3A8A' : isActive ? '#1E293B' : '#111827' }}>
                                                            {step.title}
                                                        </div>
                                                        {!isActive && (
                                                            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, lineHeight: 1.5 }}>
                                                                {step.desc}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Badge & Chevron */}
                                                    {step.status === 'upcoming' && (
                                                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', whiteSpace: 'nowrap' }}>
                                                            UPCOMING
                                                        </span>
                                                    )}
                                                    <ChevronDown size={14} color="#9CA3AF" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                                </div>

                                                {/* Expanded Active Step Detail */}
                                                {isExpanded && isActive && step.bullets && (
                                                    <div style={{ paddingBottom: 18, paddingLeft: 38 }}>
                                                        <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>{step.desc}</div>

                                                        {/* Bullets boxes */}
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                            {step.bullets.map((b, bi) => (
                                                                <div key={bi} style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 14px', border: '1px solid #F1F5F9', fontSize: 12, color: '#334155', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                                    <span style={{ color: '#111' }}>•</span>
                                                                    <span>{b}</span>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Yellow Key EDA Finding Callout */}
                                                        {step.keyCDAFinding && (
                                                            <div style={{ marginTop: 14, background: '#FEF9C3', border: '1px solid #FDE047', borderRadius: 10, padding: '14px 16px' }}>
                                                                <div style={{ fontSize: 12, fontWeight: 800, color: '#92400E', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    💡 Key EDA finding
                                                                </div>
                                                                <div style={{ fontSize: 12, color: '#78350F', lineHeight: 1.6 }}>
                                                                    {step.keyCDAFinding}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Mark Step Complete Button */}
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                                                            <button style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8, background: '#581C87', color: 'white', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(88,28,135,0.2)' }}>
                                                                <Check size={14} /> Mark Step Complete
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Expanded non-active step */}
                                                {isExpanded && !isActive && (
                                                    <div style={{ paddingBottom: 14, paddingLeft: 38 }}>
                                                        <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>{step.desc}</div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Reference Material Card */}
                            {proj.references && proj.references.length > 0 && (
                                <div style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                        <LayoutGrid size={18} color="#581C87" />
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Reference Material</div>
                                        <span style={{ background: '#F3E8FF', color: '#6B21A8', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>
                                            {proj.references.length} Links
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                        {proj.references.map((ref, rIdx) => (
                                            <div key={rIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 0', borderBottom: rIdx < proj.references.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ color: '#111', fontSize: 13 }}>•</span>
                                                    <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{ref.title}</span>
                                                </div>
                                                <span style={{ fontSize: 11, color: '#581C87', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 12, cursor: 'pointer' }}>
                                                    {ref.url} ↗
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ═════════════════ TAB 3: COURSES / CERTIFICATIONS ═════════════════ */}
            {activeTab === 'certs' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <span style={{ fontSize: 18, fontWeight: 900, color: '#111827' }}>Certifications</span>
                        <span style={{ background: '#EDE9FE', color: '#4C2A85', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>
                            {cfg.certifications?.items?.length || 3} Programs
                        </span>
                    </div>

                    {/* Certification list Card */}
                    <div style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                        {cfg.certifications?.items?.map((cert, cIdx) => (
                            <div key={cert.id} style={{
                                display: 'flex', alignItems: 'center', gap: 16, padding: '18px 22px',
                                borderBottom: cIdx < cfg.certifications.items.length - 1 ? '1px solid #F1F5F9' : 'none',
                                background: 'white'
                            }}>
                                {/* Avatar */}
                                <div style={{ width: 40, height: 40, borderRadius: '50%', background: cert.initialBg, color: cert.initialColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
                                    {cert.initial}
                                </div>

                                {/* Title + provider */}
                                <div style={{ flex: 1.5, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{cert.title}</div>
                                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{cert.provider}</div>
                                </div>

                                {/* Link */}
                                <div style={{ flex: 1.2, minWidth: 0 }}>
                                    <span style={{ fontSize: 12, color: '#581C87', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                        {cert.url}
                                    </span>
                                </div>

                                {/* Duration + price */}
                                <div style={{ minWidth: 90, textAlign: 'right' }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{cert.duration}</div>
                                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{cert.price}</div>
                                </div>

                                {/* Status badge */}
                                <div style={{ minWidth: 90, display: 'flex', justifyContent: 'center' }}>
                                    <span style={{
                                        fontSize: 11, fontWeight: 700, padding: '4px 14px', borderRadius: 999, whiteSpace: 'nowrap',
                                        background: cert.status === 'Completed' ? '#DCFCE7' : cert.status === 'In Progress' ? '#EDE9FE' : '#F3F4F6',
                                        color: cert.status === 'Completed' ? '#166534' : cert.status === 'In Progress' ? '#581C87' : '#6B7280',
                                        border: `1px solid ${cert.status === 'Completed' ? '#BBF7D0' : cert.status === 'In Progress' ? '#DDD6FE' : '#E5E7EB'}`
                                    }}>
                                        {cert.status}
                                    </span>
                                </div>

                                {/* Arrow */}
                                <ChevronRight size={14} color="#9CA3AF" style={{ flexShrink: 0 }} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─────────────── SECTION 6: MEDIUM LEVEL DEFAULT CONFIG ─────────────── */
const DEFAULT_MEDIUM_LEVEL_CONFIG = {
    sectionTitle: 'Apply Your Skills in Real Scenarios',
    sectionDescription: 'Move beyond fundamentals — tackle real datasets, build end-to-end pipelines, and sharpen your statistical intuition with intermediate-complexity projects.',
    sectionStatus: 'LOCKED',
    aiPrompt: DEFAULT_FOCUS_AREAS_MEDIUM_PROMPT,
    selfProjectsPrompt: DEFAULT_SELF_PROJECTS_PROMPT,
    certificationsPrompt: DEFAULT_CERTIFICATIONS_PROMPT,
    focusAreas: {
        modules: [
            { id: 'm1', icon: '📊', iconBg: '#EFF6FF', title: 'Machine Learning Fundamentals', level: 'INTERMEDIATE', totalLessons: 8, status: 'locked', lessons: [] },
            { id: 'm2', icon: '📐', iconBg: '#F0FDF4', title: 'Statistics & Probability', level: 'INTERMEDIATE', totalLessons: 6, status: 'locked', lessons: [] },
            { id: 'm3', icon: '🗄️', iconBg: '#FFF7ED', title: 'SQL & Databases', level: 'INTERMEDIATE', totalLessons: 7, status: 'locked', lessons: [] },
            { id: 'm4', icon: '📈', iconBg: '#FDF4FF', title: 'Data Visualisation (Tableau / Matplotlib)', level: 'INTERMEDIATE', totalLessons: 5, status: 'locked', lessons: [] },
        ]
    },
    selfProjects: {
        categories: ['HealthCare', 'FinTech', 'E-Commerce', 'NLP', 'Computer Vision'],
        projects: [
            {
                id: 'p1',
                category: 'HealthCare',
                title: 'Hospital Readmission Prediction using',
                titleHighlight: 'Gradient Boosting',
                level: 'INTERMEDIATE',
                description: 'Predict 30-day hospital readmission for diabetic patients using EHR data. Build feature engineering pipelines, handle missing values, and deploy a risk-scoring API.',
                techStack: ['Python 3.11', 'XGBoost', 'Pandas', 'SHAP', 'FastAPI', 'PostgreSQL', 'Docker'],
                aiPrompt: '',
                steps: [
                    { id: 's1', number: 1, title: 'Problem Framing', desc: 'Define clinical readmission metrics and business impact of false positives vs false negatives.', status: 'upcoming' },
                    { id: 's2', number: 2, title: 'Data Exploration', desc: 'Explore the UCI Diabetes 130 Hospitals dataset — 100k+ admissions, 50 features.', status: 'upcoming' },
                    { id: 's3', number: 3, title: 'Feature Engineering', desc: 'Engineer admission type, discharge disposition, medication changes flags.', status: 'upcoming' },
                    { id: 's4', number: 4, title: 'Model Training', desc: 'Train XGBoost with early stopping; compare vs Logistic Regression baseline.', status: 'upcoming' },
                    { id: 's5', number: 5, title: 'SHAP Explainability', desc: 'Identify top 10 features driving readmission risk using SHAP summary plots.', status: 'upcoming' },
                    { id: 's6', number: 6, title: 'FastAPI Deployment', desc: 'Build a /predict endpoint returning risk score + top contributing factors.', status: 'upcoming' },
                    { id: 's7', number: 7, title: 'Documentation', desc: 'Write README, API docs, and model card with limitations and bias analysis.', status: 'upcoming' },
                ],
                references: [
                    { title: 'UCI Diabetes 130-US Hospitals Dataset', url: 'archive.ics.uci.edu' },
                    { title: 'XGBoost Documentation', url: 'xgboost.readthedocs.io' },
                    { title: 'FastAPI Getting Started', url: 'fastapi.tiangolo.com' },
                    { title: 'SHAP Summary Plots Guide', url: 'shap.readthedocs.io' },
                ]
            }
        ]
    },
    certifications: {
        items: [
            { id: 'c1', initial: 'I', initialBg: '#EFF6FF', initialColor: '#1D4ED8', title: 'IBM Data Science Professional Certificate', provider: 'IBM / Coursera', url: 'coursera.org/professional-certificates/ibm-data-science', duration: '5 months', price: 'Free (Audit)', status: 'Not Started' },
            { id: 'c2', initial: 'G', initialBg: '#EEF2FF', initialColor: '#4338CA', title: 'Google Advanced Data Analytics Certificate', provider: 'Google', url: 'coursera.org/professional-certificates/google-advanced-da...', duration: '6 months', price: 'Free (Coursera)', status: 'Not Started' },
            { id: 'c3', initial: 'D', initialBg: '#FFF7ED', initialColor: '#C2410C', title: 'Deep Learning Specialization', provider: 'Coursera / DeepLearning.AI', url: 'coursera.org/specializations/deep-learning', duration: '5 months', price: '$49/month', status: 'Not Started' },
            { id: 'c4', initial: 'T', initialBg: '#F0FDF4', initialColor: '#166534', title: 'TensorFlow Developer Certificate', provider: 'Google / TensorFlow', url: 'tensorflow.org/certificate', duration: '3 months', price: '$100 (exam)', status: 'Not Started' },
        ]
    }
};

/* ─────────────── SECTION 7: ADVANCED LEVEL DEFAULT CONFIG ─────────────── */
const DEFAULT_ADVANCED_LEVEL_CONFIG = {
    sectionTitle: 'Build Production-Grade AI Systems',
    sectionDescription: 'Master deep learning architectures, MLOps pipelines, and system design for large-scale AI. Work on cutting-edge projects that match industry expectations for senior roles.',
    sectionStatus: 'LOCKED',
    aiPrompt: DEFAULT_FOCUS_AREAS_ADVANCED_PROMPT,
    selfProjectsPrompt: DEFAULT_SELF_PROJECTS_PROMPT,
    certificationsPrompt: DEFAULT_CERTIFICATIONS_PROMPT,
    focusAreas: {
        modules: [
            { id: 'm1', icon: '🧠', iconBg: '#FDF4FF', title: 'Deep Learning & Neural Networks', level: 'ADVANCED', totalLessons: 10, status: 'locked', lessons: [] },
            { id: 'm2', icon: '🗣️', iconBg: '#EFF6FF', title: 'Natural Language Processing (NLP)', level: 'ADVANCED', totalLessons: 8, status: 'locked', lessons: [] },
            { id: 'm3', icon: '⚙️', iconBg: '#F0FDF4', title: 'MLOps & Model Deployment at Scale', level: 'ADVANCED', totalLessons: 9, status: 'locked', lessons: [] },
            { id: 'm4', icon: '🏗️', iconBg: '#FFF7ED', title: 'System Design for AI Applications', level: 'ADVANCED', totalLessons: 6, status: 'locked', lessons: [] },
        ]
    },
    selfProjects: {
        categories: ['GenAI', 'NLP', 'Computer Vision', 'MLOps', 'LLMs'],
        projects: [
            {
                id: 'p1',
                category: 'GenAI',
                title: 'RAG-Powered Enterprise Knowledge Base using',
                titleHighlight: 'LangChain + Pinecone',
                level: 'ADVANCED',
                description: 'Build a production-grade Retrieval Augmented Generation (RAG) system that allows companies to query their internal documents using natural language. Deploy on AWS with auto-scaling.',
                techStack: ['Python 3.12', 'LangChain', 'Pinecone', 'OpenAI GPT-4', 'FastAPI', 'AWS Lambda', 'Redis', 'Docker', 'Terraform'],
                aiPrompt: '',
                steps: [
                    { id: 's1', number: 1, title: 'Architecture Design', desc: 'Design the RAG pipeline: document ingestion → chunking → embedding → vector store → retrieval → generation.', status: 'upcoming' },
                    { id: 's2', number: 2, title: 'Document Ingestion Pipeline', desc: 'Build PDF/DOCX/TXT parsers with LangChain loaders. Handle multi-modal documents.', status: 'upcoming' },
                    { id: 's3', number: 3, title: 'Embedding & Indexing', desc: 'Use OpenAI text-embedding-3-small to embed chunks; index in Pinecone with metadata filters.', status: 'upcoming' },
                    { id: 's4', number: 4, title: 'Retrieval Strategy', desc: 'Implement hybrid search (dense + sparse BM25). Add re-ranking with Cohere.', status: 'upcoming' },
                    { id: 's5', number: 5, title: 'Generation with GPT-4', desc: 'Compose prompt with retrieved context. Add citation tracking and hallucination guards.', status: 'upcoming' },
                    { id: 's6', number: 6, title: 'Evaluation (RAGAS)', desc: 'Measure faithfulness, answer relevancy, context recall using the RAGAS framework.', status: 'upcoming' },
                    { id: 's7', number: 7, title: 'AWS Serverless Deployment', desc: 'Deploy FastAPI via AWS Lambda + API Gateway. Add Redis caching for repeated queries.', status: 'upcoming' },
                    { id: 's8', number: 8, title: 'Monitoring & Observability', desc: 'Set up LangSmith tracing, CloudWatch dashboards, latency & cost alerts.', status: 'upcoming' },
                ],
                references: [
                    { title: 'LangChain RAG Documentation', url: 'python.langchain.com' },
                    { title: 'Pinecone Vector Database Docs', url: 'docs.pinecone.io' },
                    { title: 'RAGAS Evaluation Framework', url: 'docs.ragas.io' },
                    { title: 'OpenAI Embeddings Guide', url: 'platform.openai.com' },
                    { title: 'AWS Lambda + FastAPI Deployment', url: 'aws.amazon.com/lambda' },
                ]
            }
        ]
    },
    certifications: {
        items: [
            { id: 'c1', initial: 'P', initialBg: '#FDF4FF', initialColor: '#7C3AED', title: 'Professional Machine Learning Engineer', provider: 'Google Cloud', url: 'cloud.google.com/certification/machine-learning-engineer', duration: '3 months', price: '$200 (exam)', status: 'Not Started' },
            { id: 'c2', initial: 'A', initialBg: '#FFF7ED', initialColor: '#C2410C', title: 'AWS Certified Machine Learning – Specialty', provider: 'Amazon Web Services', url: 'aws.amazon.com/certification/certified-machine-learning...', duration: '4 months', price: '$300 (exam)', status: 'Not Started' },
            { id: 'c3', initial: 'D', initialBg: '#EFF6FF', initialColor: '#1D4ED8', title: 'Databricks Certified ML Professional', provider: 'Databricks', url: 'databricks.com/learn/certification', duration: '2 months', price: '$200 (exam)', status: 'Not Started' },
            { id: 'c4', initial: 'H', initialBg: '#F0FDF4', initialColor: '#166534', title: 'HuggingFace NLP Course Certification', provider: 'Hugging Face', url: 'huggingface.co/learn/nlp-course', duration: '6 weeks', price: 'Free', status: 'Not Started' },
        ]
    }
};

/* ─────────────── SHARED: LEVEL STAGE PROMPT EDITOR (Medium / Advanced) ─────────────── */
function LevelStageEditor({ levelConfig, setLevelConfig, defaultConfig, levelLabel, stepNumber }) {
    const cfg = levelConfig || defaultConfig;
    const levelKey = levelLabel.toLowerCase(); // 'medium' or 'advanced'
    const defaultPrompt = levelKey === 'advanced' ? DEFAULT_FOCUS_AREAS_ADVANCED_PROMPT : DEFAULT_FOCUS_AREAS_MEDIUM_PROMPT;

    const [activePromptTab, setActivePromptTab] = useState('focus');
    const [prompts, setPrompts] = useState({
        focus: cfg.aiPrompt || cfg.focusAreasPrompt || defaultPrompt,
        self_projects: cfg.selfProjectsPrompt || DEFAULT_SELF_PROJECTS_PROMPT,
        certifications: cfg.certificationsPrompt || DEFAULT_CERTIFICATIONS_PROMPT
    });
    const [savingTab, setSavingTab] = useState(null);
    const [saveSuccessTab, setSaveSuccessTab] = useState(null);
    const [copiedTab, setCopiedTab] = useState(null);
    const textareaRef = React.useRef(null);

    // Sync external changes
    useEffect(() => {
        if (cfg?.aiPrompt || cfg?.focusAreasPrompt) {
            setPrompts(p => ({ ...p, focus: cfg.aiPrompt || cfg.focusAreasPrompt }));
        }
        if (cfg?.selfProjectsPrompt) {
            setPrompts(p => ({ ...p, self_projects: cfg.selfProjectsPrompt }));
        }
        if (cfg?.certificationsPrompt) {
            setPrompts(p => ({ ...p, certifications: cfg.certificationsPrompt }));
        }
    }, [cfg]);

    const PROMPT_TABS = [
        {
            id: 'focus',
            label: `1. Focus Areas (${levelLabel})`,
            endpoint: `PUT /crap/v2/sections/5/focus-areas/${levelKey}/prompt`,
            desc: `Generates 8-10 fundamental ${levelLabel}-level topics tailored to the student profile.`
        },
        {
            id: 'self_projects',
            label: '2. Self Projects List',
            endpoint: 'PUT /crap/v2/sections/5/self-projects/prompt',
            desc: `Generates 5 tailored portfolio project ideas for ${levelLabel} level.`
        },
        {
            id: 'certifications',
            label: '3. Certifications',
            endpoint: 'PUT /crap/v2/sections/5/certifications/prompt',
            desc: `Recommends top 5 certifications/courses with direct links for ${levelLabel} level.`
        }
    ];

    const currentTabInfo = PROMPT_TABS.find(t => t.id === activePromptTab) || PROMPT_TABS[0];
    const currentPromptVal = prompts[activePromptTab] || '';

    const handlePromptChange = (val) => {
        setPrompts(prev => ({ ...prev, [activePromptTab]: val }));
    };

    const insertVariable = (varTag) => {
        if (!textareaRef.current) {
            handlePromptChange((currentPromptVal || '') + ' ' + varTag);
            return;
        }
        const el = textareaRef.current;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const text = currentPromptVal || '';
        const newText = text.substring(0, start) + varTag + text.substring(end);
        handlePromptChange(newText);
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + varTag.length, start + varTag.length);
        }, 10);
    };

    const handleSavePrompt = async (tabId) => {
        const valToSave = prompts[tabId];
        setSavingTab(tabId);
        setSaveSuccessTab(null);
        try {
            if (tabId === 'focus') {
                await CrapEditorApi.saveFocusAreasPrompt(levelKey, valToSave);
                setLevelConfig(prev => ({
                    ...(prev || defaultConfig),
                    aiPrompt: valToSave,
                    focusAreasPrompt: valToSave
                }));
            } else if (tabId === 'self_projects') {
                await CrapEditorApi.saveSelfProjectsPrompt(valToSave);
                setLevelConfig(prev => ({
                    ...(prev || defaultConfig),
                    selfProjectsPrompt: valToSave
                }));
            } else if (tabId === 'certifications') {
                await CrapEditorApi.saveCertificationsPrompt(valToSave);
                setLevelConfig(prev => ({
                    ...(prev || defaultConfig),
                    certificationsPrompt: valToSave
                }));
            }
            setSaveSuccessTab(tabId);
            setTimeout(() => setSaveSuccessTab(null), 3000);
        } catch (err) {
            console.error(`Failed to save prompt for ${tabId}:`, err);
            alert('Failed to save prompt: ' + (err.response?.data?.message || err.message));
        } finally {
            setSavingTab(null);
        }
    };

    const handleCopy = (tabId) => {
        navigator.clipboard.writeText(prompts[tabId] || '');
        setCopiedTab(tabId);
        setTimeout(() => setCopiedTab(null), 2000);
    };

    const handleReset = (tabId) => {
        const defaults = {
            focus: defaultPrompt,
            self_projects: DEFAULT_SELF_PROJECTS_PROMPT,
            certifications: DEFAULT_CERTIFICATIONS_PROMPT
        };
        if (window.confirm(`Reset this prompt to the recommended default template?`)) {
            setPrompts(prev => ({ ...prev, [tabId]: defaults[tabId] }));
        }
    };

    const STUDENT_VARS = [
        { label: 'Course', tag: '{{course}}' },
        { label: 'Year', tag: '{{year}}' },
        { label: 'CGPA', tag: '{{cgpa}}' },
        { label: 'Career / Role', tag: '{{career}}' },
        { label: 'Entry Score', tag: '{{entry_score}}' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header Banner */}
            <div style={{ background: '#f5f0fb', borderRadius: 14, padding: '16px 20px', border: '1.5px solid #e8daf5', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <Sparkles size={16} color="#612D9B" />
                        Section {stepNumber}: {levelLabel} Level — AI Prompt Configuration
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#EDE9FE', color: '#581C87', border: '1px solid #DDD6FE', fontFamily: 'monospace' }}>
                            {currentTabInfo.endpoint}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0' }}>
                            Step {stepNumber} Active
                        </span>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                    Configure the AI prompts that generate {levelLabel} Level foundational topics, self-projects, and certifications tailored to the student.
                </div>
            </div>

            {/* Prompt sub-tab bar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: 'white', borderRadius: 10, padding: 4, border: '1.5px solid #e8e4f2', gap: 4 }}>
                {PROMPT_TABS.map(t => {
                    const isActive = activePromptTab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setActivePromptTab(t.id)}
                            style={{
                                padding: '9px 8px',
                                borderRadius: 8,
                                border: 'none',
                                background: isActive ? '#f3e8ff' : 'transparent',
                                color: isActive ? '#6b21a8' : '#64748b',
                                fontSize: 11,
                                fontWeight: 800,
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s',
                                borderBottom: isActive ? '2px solid #9333ea' : '2px solid transparent'
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Active Prompt Box */}
            <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1.5px solid #e8e4f2', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e2478' }}>
                            {currentTabInfo.label} Prompt Editor
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            {currentTabInfo.desc} Click variable chips below to insert placeholder tags into your prompt.
                        </div>
                    </div>
                    <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                        {(currentPromptVal || '').length} characters
                    </span>
                </div>

                {/* Variables Toolbelt */}
                <div style={{ background: '#faf8fc', borderRadius: 10, padding: '10px 14px', border: '1px solid #eedfee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase' }}>Insert Variables:</span>
                        {STUDENT_VARS.map(v => (
                            <button
                                key={v.tag}
                                type="button"
                                onClick={() => insertVariable(v.tag)}
                                style={{ background: 'white', color: '#612D9B', border: '1px solid #d8b4fe', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, fontFamily: 'monospace', cursor: 'pointer' }}
                            >
                                + {v.tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Textarea */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                            Prompt Body (JSON output expected):
                        </label>
                        <button
                            type="button"
                            onClick={() => handleCopy(activePromptTab)}
                            style={{ background: 'none', border: 'none', color: '#612D9B', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                            <Copy size={12} /> {copiedTab === activePromptTab ? 'Copied!' : 'Copy Prompt'}
                        </button>
                    </div>
                    <textarea
                        ref={textareaRef}
                        value={currentPromptVal}
                        onChange={(e) => handlePromptChange(e.target.value)}
                        rows={12}
                        style={{ padding: '14px 16px', borderRadius: 10, border: '1.5px solid #d8b4fe', fontSize: 12, fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace", outline: 'none', resize: 'vertical', lineHeight: 1.65, color: '#1e2478', background: '#fafafc' }}
                    />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <button
                        type="button"
                        onClick={() => handleReset(activePromptTab)}
                        style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                        Reset to Default
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {saveSuccessTab === activePromptTab && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#166534', background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '6px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <CheckCircle2 size={14} color="#166534" /> Saved successfully to backend!
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => handleSavePrompt(activePromptTab)}
                            disabled={savingTab === activePromptTab}
                            style={{ padding: '9px 24px', borderRadius: 8, border: 'none', background: '#612D9B', color: 'white', fontSize: 12, fontWeight: 800, cursor: savingTab === activePromptTab ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 2px 8px rgba(97,45,155,0.25)' }}
                        >
                            {savingTab === activePromptTab ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                            {savingTab === activePromptTab ? 'Saving...' : 'Save Prompt'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─────────────── SHARED: PREVIEW LEVEL STAGE (Medium / Advanced) ─────────────── */
function PreviewLevelStage({ levelConfig, defaultConfig, levelLabel, accentColor }) {
    const cfg = levelConfig || defaultConfig;
    const accent = accentColor || '#581C87';
    const [activeTab, setActiveTab] = useState('focus');
    const [expandedModuleId, setExpandedModuleId] = useState(null);
    const [activeCategory, setActiveCategory] = useState(cfg.selfProjects?.categories?.[0] || 'HealthCare');
    const [expandedStepId, setExpandedStepId] = useState(null);

    const tabs = [
        { id: 'focus', label: 'Focus Areas' },
        { id: 'projects', label: 'Self Projects' },
        { id: 'certs', label: 'Courses/ Certifications' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: "'Outfit','Inter',sans-serif" }}>
            {/* 3 Prominent Card Tabs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
                {tabs.map(t => {
                    const isActive = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                background: isActive ? '#F3EDFC' : 'white',
                                color: '#581C87',
                                borderRadius: 14,
                                border: isActive ? '1.5px solid #E9D5FF' : '1.5px solid #F0EBFA',
                                padding: '14px 16px',
                                fontWeight: 800,
                                fontSize: 14,
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: isActive ? '0 2px 8px rgba(88,28,135,0.06)' : '0 1px 3px rgba(0,0,0,0.02)'
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* ═════════════════ TAB 1: FOCUS AREAS ═════════════════ */}
            {activeTab === 'focus' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Top Banner Card */}
                    <div style={{ background: 'white', borderRadius: 16, padding: '24px', border: '1.5px solid #F0EBFA', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#111827', lineHeight: 1.2 }}>
                                {cfg.sectionTitle?.split(' ').slice(0, -3).join(' ') || cfg.sectionTitle}{' '}
                                <span style={{ color: '#581C87' }}>{cfg.sectionTitle?.split(' ').slice(-3).join(' ')}</span>
                            </h2>
                            <span style={{ fontSize: 10, fontWeight: 800, padding: '5px 14px', borderRadius: 999, background: cfg.sectionStatus === 'LOCKED' ? '#F1F5F9' : '#1E293B', color: cfg.sectionStatus === 'LOCKED' ? '#64748B' : 'white', letterSpacing: '0.5px' }}>
                                {cfg.sectionStatus || 'LOCKED'}
                            </span>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>{cfg.sectionDescription}</p>
                    </div>

                    {/* Learning Modules Section */}
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 14 }}>Learning Modules</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {cfg.focusAreas?.modules?.map(mod => {
                                const isExp = expandedModuleId === mod.id;
                                return (
                                    <div key={mod.id} style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                        <div onClick={() => setExpandedModuleId(isExp ? null : mod.id)}
                                            style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}>
                                            <div style={{ width: 44, height: 44, borderRadius: 12, background: mod.iconBg || '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{mod.icon}</div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{mod.title}</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: '#DBEAFE', color: '#1E40AF', letterSpacing: '0.4px' }}>{mod.level}</span>
                                                    <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>{mod.totalLessons} Lessons</span>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 700, color: '#6B7280' }}>
                                                Locked <ChevronDown size={16} />
                                            </div>
                                        </div>
                                        {isExp && (
                                            <div style={{ borderTop: '1px solid #F3F4F6', padding: '16px 0 0 0', marginTop: 16 }}>
                                                <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', fontStyle: 'italic' }}>
                                                    🔒 Complete the previous level to unlock these lessons.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════ TAB 2: SELF PROJECTS ═════════════════ */}
            {activeTab === 'projects' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {/* Category Filter Pills */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {cfg.selfProjects?.categories?.map(cat => {
                            const isCatActive = activeCategory === cat;
                            return (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(cat)}
                                    style={{
                                        padding: '6px 18px',
                                        borderRadius: 999,
                                        border: isCatActive ? '1.5px solid #581C87' : '1.5px solid #E5E7EB',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        background: isCatActive ? '#581C87' : 'white',
                                        color: isCatActive ? 'white' : '#4B5563',
                                        boxShadow: isCatActive ? '0 2px 6px rgba(88,28,135,0.15)' : 'none'
                                    }}
                                >
                                    {cat}
                                </button>
                            );
                        })}
                    </div>

                    {cfg.selfProjects?.projects?.map(proj => (
                        <div key={proj.id} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            {/* Project Header Banner */}
                            <div style={{ background: 'linear-gradient(180deg, #FBF9FE 0%, #FFFFFF 100%)', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '22px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#111827', lineHeight: 1.4, flex: 1, paddingRight: 16 }}>
                                        {proj.title} <span style={{ color: '#581C87' }}>{proj.titleHighlight}</span>
                                    </h3>
                                    <span style={{ fontSize: 10, fontWeight: 800, padding: '5px 14px', borderRadius: 999, background: '#F1F5F9', color: '#64748B', whiteSpace: 'nowrap', border: '1px solid #E2E8F0' }}>
                                        {proj.level}
                                    </span>
                                </div>
                                <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>{proj.description}</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {proj.techStack?.map((t, i) => (
                                        <span key={i} style={{ background: '#F1F5F9', color: '#334155', fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 8 }}>{t}</span>
                                    ))}
                                </div>
                            </div>

                            {/* Step-by-Step Guide */}
                            <div style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                    <Layers size={18} color="#581C87" />
                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Step-by-Step Guide</div>
                                    <span style={{ background: '#F3E8FF', color: '#6B21A8', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>{proj.steps?.length} Steps</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                    {proj.steps?.map((step, sIdx) => {
                                        const isExp = expandedStepId === step.id;
                                        return (
                                            <div key={step.id} style={{ borderBottom: sIdx < proj.steps.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                                                <div onClick={() => setExpandedStepId(isExp ? null : step.id)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', cursor: 'pointer' }}>
                                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#F1F5F9', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{step.number}</div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#64748B' }}>{step.title}</div>
                                                        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2, lineHeight: 1.5 }}>{step.desc}</div>
                                                    </div>
                                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' }}>UPCOMING</span>
                                                    <ChevronDown size={14} color="#9CA3AF" style={{ transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                                </div>
                                                {isExp && (
                                                    <div style={{ paddingBottom: 14, paddingLeft: 38 }}>
                                                        <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>{step.desc}</div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Reference Material */}
                            {proj.references && proj.references.length > 0 && (
                                <div style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                                        <LayoutGrid size={18} color="#581C87" />
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Reference Material</div>
                                        <span style={{ background: '#F3E8FF', color: '#6B21A8', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>{proj.references.length} Links</span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                        {proj.references.map((ref, rIdx) => (
                                            <div key={rIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 0', borderBottom: rIdx < proj.references.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ color: '#111', fontSize: 13 }}>•</span>
                                                    <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{ref.title}</span>
                                                </div>
                                                <span style={{ fontSize: 11, color: '#581C87', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 12, cursor: 'pointer' }}>
                                                    {ref.url} ↗
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ═════════════════ TAB 3: COURSES / CERTIFICATIONS ═════════════════ */}
            {activeTab === 'certs' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <span style={{ fontSize: 18, fontWeight: 900, color: '#111827' }}>Certifications</span>
                        <span style={{ background: '#EDE9FE', color: '#4C2A85', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>{cfg.certifications?.items?.length} Programs</span>
                    </div>
                    <div style={{ background: 'white', borderRadius: 16, border: '1.5px solid #F0EBFA', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                        {cfg.certifications?.items?.map((cert, cIdx) => (
                            <div key={cert.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 22px', borderBottom: cIdx < cfg.certifications.items.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                                <div style={{ width: 40, height: 40, borderRadius: '50%', background: cert.initialBg, color: cert.initialColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>{cert.initial}</div>
                                <div style={{ flex: 1.5, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{cert.title}</div>
                                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{cert.provider}</div>
                                </div>
                                <div style={{ flex: 1.2, minWidth: 0 }}>
                                    <span style={{ fontSize: 12, color: '#581C87', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{cert.url}</span>
                                </div>
                                <div style={{ minWidth: 90, textAlign: 'right' }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{cert.duration}</div>
                                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{cert.price}</div>
                                </div>
                                <div style={{ minWidth: 90, display: 'flex', justifyContent: 'center' }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 14px', borderRadius: 999, background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB', whiteSpace: 'nowrap' }}>{cert.status}</span>
                                </div>
                                <ChevronRight size={14} color="#9CA3AF" style={{ flexShrink: 0 }} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


/* ─────────────── SECTION 8: INTERNSHIPS PROMPT EDITOR (ADMIN CONFIG) ─────────────── */
function Section6InternshipsEditor({ internshipsConfig, setInternshipsConfig }) {
    const cfg = internshipsConfig || DEFAULT_INTERNSHIPS_CONFIG;
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [copied, setCopied] = useState(false);
    const textareaRef = React.useRef(null);

    const currentPrompt = cfg.aiPrompt || DEFAULT_INTERNSHIPS_PROMPT;

    const updatePrompt = (val) => {
        setInternshipsConfig(prev => ({
            ...(prev || DEFAULT_INTERNSHIPS_CONFIG),
            aiPrompt: val
        }));
    };

    const insertVariable = (varTag) => {
        if (!textareaRef.current) {
            updatePrompt((currentPrompt || '') + ' ' + varTag);
            return;
        }
        const el = textareaRef.current;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const text = currentPrompt || '';
        const newText = text.substring(0, start) + varTag + text.substring(end);
        updatePrompt(newText);
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + varTag.length, start + varTag.length);
        }, 10);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveSuccess(false);
        try {
            await CrapEditorApi.saveInternshipsPrompt(currentPrompt);
            updatePrompt(currentPrompt);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            console.error('Failed to save internships prompt:', err);
            alert('Failed to save prompt: ' + (err.response?.data?.message || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(currentPrompt);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleReset = () => {
        if (window.confirm('Reset this prompt to the default template?')) {
            updatePrompt(DEFAULT_INTERNSHIPS_PROMPT);
        }
    };

    const STUDENT_VARS = [
        { label: 'Job Role (From Section 3)', tag: '{{job}}' },
        { label: 'Course', tag: '{{course}}' },
        { label: 'Year', tag: '{{year}}' },
        { label: 'CGPA', tag: '{{cgpa}}' },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header Banner */}
            <div style={{ background: '#f5f0fb', borderRadius: 14, padding: '16px 20px', border: '1.5px solid #e8daf5', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <Sparkles size={16} color="#612D9B" />
                        Section 8: Internships — AI Prompt Configuration
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#EDE9FE', color: '#581C87', border: '1px solid #DDD6FE' }}>
                            PUT /crap/v2/sections/6/internships/prompt
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0' }}>
                            Step 8 Active
                        </span>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                    Configure the prompt that generates targeted internship opportunities. The backend resolves the student's chosen career role from Section 3 and automatically substitutes <code>{'{{job}}'}</code> at generation time.
                </div>
            </div>

            {/* Prompt Box */}
            <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1.5px solid #e8e4f2', display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#1e2478' }}>
                            Internships AI Generator Prompt
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            Output must contain the word "json" and return a JSON list of internships with name and link.
                        </div>
                    </div>
                    <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                        {currentPrompt.length} chars
                    </span>
                </div>

                {/* Variable Tags Toolbelt */}
                <div style={{ background: '#faf8fc', borderRadius: 10, padding: '10px 14px', border: '1px solid #eedfee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase' }}>Insert Variables:</span>
                        {STUDENT_VARS.map(v => (
                            <button
                                key={v.tag}
                                type="button"
                                onClick={() => insertVariable(v.tag)}
                                style={{
                                    background: 'white',
                                    color: '#612D9B',
                                    border: '1px solid #d8b4fe',
                                    borderRadius: 6,
                                    padding: '3px 8px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    fontFamily: 'monospace',
                                    cursor: 'pointer'
                                }}
                            >
                                + {v.tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Textarea */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                            Prompt Body:
                        </label>
                        <button
                            type="button"
                            onClick={handleCopy}
                            style={{ background: 'none', border: 'none', color: '#612D9B', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                            <Copy size={12} /> {copied ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                    <textarea
                        ref={textareaRef}
                        value={currentPrompt}
                        onChange={e => updatePrompt(e.target.value)}
                        rows={10}
                        placeholder="Tailor the internships to the user's chosen career/job: {{job}}. make sure the response is in json with each object having one intership name, link"
                        style={{
                            padding: '14px 16px',
                            borderRadius: 10,
                            border: '1.5px solid #d8b4fe',
                            fontSize: 12,
                            fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                            outline: 'none',
                            resize: 'vertical',
                            lineHeight: 1.65,
                            color: '#1e2478',
                            background: '#fafafc'
                        }}
                    />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
                    <button
                        type="button"
                        onClick={handleReset}
                        style={{
                            padding: '8px 18px',
                            borderRadius: 8,
                            border: '1.5px solid #e2e8f0',
                            background: 'white',
                            color: '#64748b',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer'
                        }}
                    >
                        Reset to Default
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {saveSuccess && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#166534', background: '#DCFCE7', border: '1px solid #BBF7D0', padding: '6px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <CheckCircle2 size={14} color="#166534" /> Saved prompt to backend!
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                padding: '9px 24px',
                                borderRadius: 8,
                                border: 'none',
                                background: saving ? '#a855f7' : '#612D9B',
                                color: 'white',
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: saving ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                boxShadow: '0 2px 8px rgba(97,45,155,0.25)'
                            }}
                        >
                            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                            {saving ? 'Saving...' : 'Save Internships Prompt'}
                        </button>
                    </div>
                </div>

                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0', fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                    💡 <strong>Backend Note:</strong> When a student opens Section 6, the server automatically appends their chosen job title to this prompt and invokes the AI generator once. Switch to <strong>Preview</strong> mode at the top to simulate the student internship listings table.
                </div>
            </div>
        </div>
    );
}

/* ─────────────── PREVIEW — SECTION 8: INTERNSHIPS (PIXEL PERFECT) ─────────────── */
function PreviewSection8Internships({ internshipsConfig }) {
    const cfg = internshipsConfig || DEFAULT_INTERNSHIPS_CONFIG;
    const listings = cfg.listings || DEFAULT_INTERNSHIPS_CONFIG.listings;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Outfit','Inter',sans-serif" }}>
            {/* Header Title */}
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#111827', lineHeight: 1.2 }}>
                Internship <span style={{ color: '#581C87' }}>Listings</span>
            </h2>

            {/* Table Container Card */}
            <div style={{
                background: 'white',
                borderRadius: 16,
                border: '1.5px solid #F0EBFA',
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.02)'
            }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 680 }}>
                        <thead>
                            <tr style={{ borderBottom: '1.5px solid #F1F5F9' }}>
                                <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    COMPANY
                                </th>
                                <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    JOB DESCRIPTION
                                </th>
                                <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    ROLE
                                </th>
                                <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    LOCATION
                                </th>
                                <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    DURATION
                                </th>
                                <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    STIPEND
                                </th>
                                <th style={{ padding: '14px 20px', fontSize: 11, fontWeight: 700, color: '#6B7280', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                                    URL
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {listings.map((item, idx) => (
                                <tr
                                    key={item.id || idx}
                                    style={{
                                        borderBottom: idx < listings.length - 1 ? '1px solid #F1F5F9' : 'none',
                                        transition: 'background 0.15s ease'
                                    }}
                                >
                                    {/* Company */}
                                    <td style={{ padding: '18px 20px', fontSize: 13, fontWeight: 800, color: '#111827', whiteSpace: 'nowrap' }}>
                                        {item.company}
                                    </td>

                                    {/* Job Description */}
                                    <td style={{ padding: '18px 20px', fontSize: 12, color: '#6B7280', lineHeight: 1.5, maxWidth: 220 }}>
                                        {item.jobDescription}
                                    </td>

                                    {/* Role */}
                                    <td style={{ padding: '18px 20px', fontSize: 12, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {item.role}
                                    </td>

                                    {/* Location */}
                                    <td style={{ padding: '18px 20px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                                        {item.location}
                                    </td>

                                    {/* Duration */}
                                    <td style={{ padding: '18px 20px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                                        {item.duration}
                                    </td>

                                    {/* Stipend */}
                                    <td style={{ padding: '18px 20px', fontSize: 12, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {item.stipend}
                                    </td>

                                    {/* Apply URL */}
                                    <td style={{ padding: '18px 20px', whiteSpace: 'nowrap' }}>
                                        <a
                                            href={item.applyUrl || '#'}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 800,
                                                color: '#1E3A8A',
                                                textDecoration: 'none',
                                                cursor: 'pointer'
                                            }}
                                            onClick={(e) => {
                                                if (!item.applyUrl || item.applyUrl === '#') e.preventDefault();
                                            }}
                                        >
                                            Apply Now
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

/* ─────────────── SECTION 7: YOUR CV (ADMIN NOTICE) ─────────────── */
function Section7CvAdminNotice() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header Banner */}
            <div style={{ background: '#f5f0fb', borderRadius: 14, padding: '16px 20px', border: '1.5px solid #e8daf5', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <FileText size={16} color="#612D9B" />
                        Section 9: Your CV — Automatic Derivation Notice
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: '#DCFCE7', color: '#166534', border: '1px solid #BBF7D0' }}>
                        Step 9 Active
                    </span>
                </div>
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                    As specified in the Heydu API documentation, <strong>Section 7 (Your CV) does not have an admin AI prompt endpoint</strong>. Student CV profiles are assembled automatically from their verified profile, Section 3 assessment responses, semester scores, and their direct edits via <code>GET /crap/v2/sections/7/cv</code> and <code>POST /crap/v2/sections/7/cv</code>.
                </div>
            </div>

            {/* Architecture Details Card */}
            <div style={{ background: 'white', borderRadius: 14, padding: 20, border: '1.5px solid #e8e4f2', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1e2478' }}>
                    How Section 7 CV Works for Students:
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span style={{ color: '#612D9B', fontWeight: 800 }}>1.</span>
                        <span><strong>Pre-filled Details:</strong> Full Name, Email, Department, University, CGPA, and Year of Study are derived directly from the student profile and Section 3 responses.</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span style={{ color: '#612D9B', fontWeight: 800 }}>2.</span>
                        <span><strong>Projects & Internships:</strong> Follow-up responses from Section 3 (Self Projects & Internships) are automatically mapped into initial CV entries.</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span style={{ color: '#612D9B', fontWeight: 800 }}>3.</span>
                        <span><strong>Student Editing & Download:</strong> Students can review, edit, and save their complete CV or download a tailored <code>.docx</code> document.</span>
                    </div>
                </div>

                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', border: '1px solid #e2e8f0', fontSize: 11, color: '#64748b', lineHeight: 1.5, marginTop: 4 }}>
                    💡 <strong>Tip:</strong> Switch to <strong>Preview</strong> mode in the top navigation bar to test the interactive student CV document preview and .docx export.
                </div>
            </div>
        </div>
    );
}

/* ─────────────── SECTION 7: YOUR CV DEFAULT CONFIG ─────────────── */
const DEFAULT_CV_CONFIG = {
    studentDetails: {
        fullName: 'Rahul Sharma',
        email: 'rahul.sharma@university.edu',
        phone: '+91 98765 43210',
        dob: '15/03/2002',
        department: 'Computer Science & Engineering',
        yearOfStudy: '3rd Year',
        college: 'Indian Institute of Technology, Delhi',
        cgpa: '8.7 / 10',
        technicalSkills: 'Python, JavaScript, React, Node.js, SQL, Git, Machine Learning',
        linkedin: 'linkedin.com/in/rahulsharma',
        portfolio: 'github.com/rahulsharma',
        careerObjective: 'Aspiring software engineer seeking opportunities to apply my skills in full-stack development and machine learning to solve real-world problems.'
    },
    internships: [],
    projects: [],
    certifications: []
};

/* ─────────────── PREVIEW — SECTION 9/10: YOUR CV (LIVE DYNAMIC CV PREVIEW) ─────────────── */
function PreviewSection9Cv({ cvConfig, isModalContent = false }) {
    const cfg = cvConfig || DEFAULT_CV_CONFIG;
    const sd = cfg.studentDetails || DEFAULT_CV_CONFIG.studentDetails;
    const internships = cfg.internships && cfg.internships.length > 0 ? cfg.internships : DEFAULT_CV_CONFIG.internships;
    const projects = cfg.projects && cfg.projects.length > 0 ? cfg.projects : DEFAULT_CV_CONFIG.projects;
    const certifications = cfg.certifications && cfg.certifications.length > 0 ? cfg.certifications : DEFAULT_CV_CONFIG.certifications;

    const handleDownloadDocx = () => {
        let textContent = `${(sd.fullName || 'STUDENT NAME').toUpperCase()}\n`;

        const contactParts = [sd.email, sd.phone, sd.linkedin, sd.portfolio].filter(Boolean);
        if (contactParts.length > 0) {
            textContent += `${contactParts.join(' | ')}\n\n`;
        }

        if (sd.careerObjective) {
            textContent += `CAREER OBJECTIVE\n${sd.careerObjective}\n\n`;
        }

        textContent += `EDUCATION\n`;
        if (sd.department) textContent += `B.Tech in ${sd.department}\n`;
        if (sd.college) textContent += `${sd.college}\n`;
        if (sd.yearOfStudy || sd.cgpa) {
            textContent += `${sd.yearOfStudy || ''}${sd.yearOfStudy && sd.cgpa ? ' | ' : ''}${sd.cgpa ? `CGPA: ${sd.cgpa}` : ''}\n\n`;
        }

        if (sd.technicalSkills) {
            textContent += `TECHNICAL SKILLS\n${sd.technicalSkills}\n\n`;
        }

        if (internships && internships.length > 0) {
            textContent += `EXPERIENCE\n`;
            internships.forEach(exp => {
                textContent += `${exp.role || 'Role'} — ${exp.company || 'Company'}\n`;
                if (exp.startDate || exp.endDate) textContent += `${exp.startDate || ''} – ${exp.endDate || ''}\n`;
                if (exp.description) textContent += `• ${exp.description}\n`;
                textContent += `\n`;
            });
        }

        if (projects && projects.length > 0) {
            textContent += `PROJECTS\n`;
            projects.forEach(proj => {
                textContent += `${proj.title || 'Project'}${proj.technologies ? ` — ${proj.technologies}` : ''}\n`;
                if (proj.description) textContent += `• ${proj.description}\n`;
                textContent += `\n`;
            });
        }

        if (certifications && certifications.length > 0) {
            textContent += `CERTIFICATIONS\n`;
            certifications.forEach(cert => {
                textContent += `${cert.name || 'Certification'}${cert.organization ? ` — ${cert.organization}` : ''}${cert.dateObtained ? ` (${cert.dateObtained})` : ''}\n`;
                if (cert.credentialUrl) textContent += `• ${cert.credentialUrl}\n`;
                textContent += `\n`;
            });
        }

        const blob = new Blob([textContent.trim()], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(sd.fullName || 'Student').replace(/\s+/g, '_')}_CV.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Outfit','Inter',sans-serif" }}>
            {/* Header Toolbar (only if not inside modal) */}
            {!isModalContent && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#111827', lineHeight: 1.2 }}>
                            CV <span style={{ color: '#581C87' }}>Preview</span>
                        </h2>
                        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                            Live preview of your generated, ATS-optimized student CV.
                        </div>
                    </div>
                    <button
                        onClick={handleDownloadDocx}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 22px',
                            borderRadius: 10,
                            background: '#581C87',
                            color: 'white',
                            border: 'none',
                            fontSize: 13,
                            fontWeight: 800,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(88,28,135,0.25)',
                            transition: 'all 0.2s'
                        }}
                    >
                        <Download size={16} /> Download .docx
                    </button>
                </div>
            )}

            {/* Document Canvas Sheet (A4-Style CV Paper) */}
            <div style={{
                background: 'white',
                borderRadius: 8,
                border: '1px solid #E5E7EB',
                padding: '36px 40px',
                maxWidth: 780,
                width: '100%',
                margin: '0 auto',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                boxSizing: 'border-box'
            }}>
                {/* 1. Header: Name & Contact Bar */}
                <div style={{ textAlign: 'left' }}>
                    <h1 style={{
                        margin: '0 0 8px 0',
                        fontSize: 22,
                        fontWeight: 900,
                        color: '#111827',
                        letterSpacing: '0.3px',
                        textTransform: 'uppercase'
                    }}>
                        {sd.fullName || 'RAHUL SHARMA'}
                    </h1>
                    <div style={{ fontSize: 11, color: '#4B5563', lineHeight: 1.5, wordBreak: 'break-word' }}>
                        {[
                            sd.email || 'rahul.sharma@university.edu',
                            sd.phone || '+91 98765 43210',
                            sd.linkedin || 'linkedin.com/in/rahulsharma',
                            sd.portfolio || 'github.com/rahulsharma'
                        ].filter(Boolean).join('   |   ')}
                    </div>
                </div>

                {/* Divider Line */}
                <div style={{ borderBottom: '1px solid #E5E7EB', margin: '14px 0' }} />

                {/* 2. CAREER OBJECTIVE */}
                {sd.careerObjective && (
                    <>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 900, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
                                CAREER OBJECTIVE
                            </div>
                            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                                {sd.careerObjective}
                            </div>
                        </div>
                        <div style={{ borderBottom: '1px solid #E5E7EB', margin: '14px 0' }} />
                    </>
                )}

                {/* 3. EDUCATION */}
                <div>
                    <div style={{ fontSize: 13, fontWeight: 900, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
                        EDUCATION
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                        B.Tech in {sd.department || 'Computer Science & Engineering'}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 1 }}>
                        {sd.college || 'Indian Institute of Technology, Delhi'}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 1 }}>
                        {sd.yearOfStudy || '3rd Year'}{sd.cgpa ? `   |   CGPA: ${sd.cgpa}` : ''}
                    </div>
                </div>

                {/* Divider Line */}
                <div style={{ borderBottom: '1px solid #E5E7EB', margin: '14px 0' }} />

                {/* 4. TECHNICAL SKILLS */}
                {sd.technicalSkills && (
                    <>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 900, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
                                TECHNICAL SKILLS
                            </div>
                            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                                {sd.technicalSkills}
                            </div>
                        </div>
                        <div style={{ borderBottom: '1px solid #E5E7EB', margin: '14px 0' }} />
                    </>
                )}

                {/* 5. EXPERIENCE / INTERNSHIPS */}
                {internships && internships.length > 0 && (
                    <>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 900, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                                EXPERIENCE
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {internships.map((exp, idx) => (
                                    <div key={exp.id || idx}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                                                {exp.role || 'Intern'} — <span style={{ fontWeight: 600 }}>{exp.company || 'Company'}</span>
                                            </div>
                                            {(exp.startDate || exp.endDate) && (
                                                <div style={{ fontSize: 11, color: '#6B7280' }}>
                                                    {exp.startDate || ''} – {exp.endDate || ''}
                                                </div>
                                            )}
                                        </div>
                                        {exp.description && (
                                            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, marginTop: 3 }}>
                                                • {exp.description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ borderBottom: '1px solid #E5E7EB', margin: '14px 0' }} />
                    </>
                )}

                {/* 6. PROJECTS */}
                {projects && projects.length > 0 && (
                    <>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 900, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                                PROJECTS
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {projects.map((proj, idx) => (
                                    <div key={proj.id || idx}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                                            {proj.title || 'Project'}{proj.technologies ? ` — ${proj.technologies}` : ''}
                                        </div>
                                        {proj.description && (
                                            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, marginTop: 3 }}>
                                                • {proj.description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ borderBottom: '1px solid #E5E7EB', margin: '14px 0' }} />
                    </>
                )}

                {/* 7. CERTIFICATIONS */}
                {certifications && certifications.length > 0 && (
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 900, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                            CERTIFICATIONS
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {certifications.map((cert, idx) => (
                                <div key={cert.id || idx}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>
                                            {cert.name || 'Certification'}{cert.organization ? ` — ${cert.organization}` : ''}
                                        </div>
                                        {cert.dateObtained && (
                                            <div style={{ fontSize: 11, color: '#6B7280' }}>
                                                {cert.dateObtained}
                                            </div>
                                        )}
                                    </div>
                                    {cert.credentialUrl && (
                                        <div style={{ fontSize: 11, color: '#581C87', marginTop: 2 }}>
                                            {cert.credentialUrl}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─────────────── TIMELINE EDITOR (ALL IN ONE) ─────────────── */
function TimelineEditor({
    items,
    onChange,
    section1,
    setSection1,
    modules,
    setModules,
    section3Questions,
    setSection3Questions,
    section3Prompts,
    setSection3Prompts,
    loadingS3,
    fetchSection3Questions,
    handleSaveS3Prompt,
    handleSaveAllS3Prompts,
    roadmapConfig,
    setRoadmapConfig,
    handleSaveRoadmap,
    entryLevelConfig,
    setEntryLevelConfig,
    mediumLevelConfig,
    setMediumLevelConfig,
    advancedLevelConfig,
    setAdvancedLevelConfig,
    internshipsConfig,
    setInternshipsConfig,
    cvConfig,
    setCvConfig,
    onActiveStepChange
}) {
    const [expanded, setExpanded] = useState(null);

    const handleExpandToggle = (idx, itemId) => {
        const nextExpanded = expanded === idx ? null : idx;
        setExpanded(nextExpanded);
        if (nextExpanded !== null && onActiveStepChange) {
            onActiveStepChange(itemId);
        }
    };

    // Section 1 handlers
    const [s1Uploading, setS1Uploading] = useState(false);
    const [s1Progress, setS1Progress] = useState(0);
    const [s1Error, setS1Error] = useState(null);
    const [s1Success, setS1Success] = useState(false);

    const handleS1Upload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('video/')) {
            setS1Error('Please select a valid video file (.mp4, .webm, etc.)');
            return;
        }

        setS1Uploading(true);
        setS1Progress(0);
        setS1Error(null);
        setS1Success(false);

        try {
            console.log('[S1Uploader] 1. Requesting Section 1 presigned URL for:', file.name, file.type, `${(file.size / 1024 / 1024).toFixed(2)} MB`);
            const presignRes = await CrapEditorApi.getVideoPresignedUrl({ fileName: file.name, contentType: file.type || 'video/mp4' });
            const presignData = presignRes.data?.data || presignRes.data;
            const url = presignData?.uploadUrl || presignData?.url || presignData?.presignedUrl;
            const key = presignData?.r2Key || presignData?.key;

            if (!url) {
                throw new Error('No upload URL returned from /crap/v2/uploads/video/presign');
            }

            console.log('[S1Uploader] 2. Uploading binary directly to Cloudflare R2...');
            const uploadRes = await CrapEditorApi.uploadFileToR2(url, file, (evt) => {
                if (evt.total) {
                    setS1Progress(Math.round((evt.loaded * 100) / evt.total));
                }
            });

            if (uploadRes.status < 200 || uploadRes.status >= 300) {
                throw new Error(`Cloudflare R2 rejected upload with status ${uploadRes.status}`);
            }

            console.log('[S1Uploader] 3. Upload successful! Saving R2 key to metadata...');
            // Auto-save the r2Key to metadata so it persists on page refresh
            const currentDesc = section1.introDescription || DEFAULT_SECTION1.introDescription;
            await CrapEditorApi.saveMetadata({
                introVideoR2Key: key,
                text: [{ key: 'introDescription', value: currentDesc }]
            });

            const localBlobUrl = URL.createObjectURL(file);
            setSection1({ ...section1, introVideoUrl: localBlobUrl, introVideoR2Key: key });
            setS1Success(true);
            setTimeout(() => setS1Success(false), 4000);
            console.log('[S1Uploader] 4. Metadata updated successfully with key:', key);
        } catch (err) {
            console.error('[S1Uploader] Upload failed:', err);
            const msg = err?.response?.data?.message || err?.message || 'Failed to upload video to Cloudflare R2.';
            setS1Error(msg);
        } finally {
            setS1Uploading(false);
        }
    };

    const addModule = () => {
        const nextOrder = modules.length + 1;
        setModules([
            ...modules,
            {
                name: `New Module ${nextOrder}`,
                videoR2Key: '',
                videoUrl: '',
                exercisePrompt: 'Generate 5-7 multiple-choice questions to assess skills for a {{course}} student in year {{year}} with a CGPA of {{cgpa}}. Return ONLY a JSON object: {"questions": [{"id": string, "question": string, "options": string[4]}]}',
                pathPrompt: 'Compare this {{course}} student\'s (year {{year}}, CGPA {{cgpa}}) exercise answers against the ideal path for their field. Return ONLY a JSON object: {"yourPath": string[5], "priyasPath": string[5]}',
                order: nextOrder
            }
        ]);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '18px' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#5a5f7d', textTransform: 'uppercase', marginBottom: 4 }}>Journey Steps</span>

            {items.map((item, idx) => (
                <div key={idx} style={{ borderRadius: 10, border: '1.5px solid #e8e4f2', overflow: 'hidden' }}>
                    <button onClick={() => handleExpandToggle(idx, item.id)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: expanded === idx ? '#f3eefa' : 'white', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                        <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#4C2A85', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                            {item.id}
                        </span>
                        <span style={{ flex: 1, fontWeight: 700, fontSize: 12, color: '#1e2478', textAlign: 'left' }}>{item.title || `Step ${item.id}`}</span>
                        <ChevronDown size={14} color="#612D9B" style={{ transform: expanded === idx ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>

                    {expanded === idx && (
                        <div style={{ padding: '16px', background: 'white', borderTop: '1px solid #f0ebfa', display: 'flex', flexDirection: 'column', gap: 16 }}>

                            {/* Step 1: WHAT IS C-RAP */}
                            {item.id === 1 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <S1VideoUploader
                                        section1={section1}
                                        setSection1={setSection1}
                                        s1Uploading={s1Uploading}
                                        s1Progress={s1Progress}
                                        s1Error={s1Error}
                                        s1Success={s1Success}
                                        handleS1Upload={handleS1Upload}
                                    />
                                    <div style={{ background: '#f9f6fb', borderRadius: 10, padding: 14, border: '1px solid #f0ebfa' }}>
                                        <InputField label="Intro Description" value={section1.introDescription}
                                            onChange={(v) => setSection1({ ...section1, introDescription: v })}
                                            placeholder="How your journey works..." type="textarea" />
                                    </div>
                                </div>
                            )}

                            {/* Step 2: PERSONALITY DEVELOPMENT */}
                            {item.id === 2 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#5a5f7d', textTransform: 'uppercase' }}>Modules</span>
                                        <button onClick={addModule}
                                            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#612D9B', background: '#f3eefa', border: '1px solid #d0c7e3', borderRadius: 20, padding: '4px 10px', cursor: 'pointer' }}>
                                            <Plus size={11} /> Add Module
                                        </button>
                                    </div>

                                    {modules.map((mod, mIdx) => (
                                        <ModuleEditor
                                            key={mIdx}
                                            mod={mod}
                                            mIdx={mIdx}
                                            onModuleChange={(updated) => setModules(modules.map((m, i) => i === mIdx ? updated : m))}
                                            onRemove={async () => {
                                                const targetId = mod.id || mod.slug;
                                                if (targetId) {
                                                    try {
                                                        console.log(`[CrapEditor] Deleting module: ${targetId}`);
                                                        await CrapEditorApi.deleteModule(targetId);
                                                        console.log(`[CrapEditor] Deleted module successfully: ${targetId}`);
                                                    } catch (err) {
                                                        console.error("Failed to delete module from backend", err);
                                                    }
                                                }
                                                setModules(modules.filter((_, i) => i !== mIdx));
                                            }}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Step 3: ASSESSMENT (Fetched from Backend + AI Prompt per Question) */}
                            {item.id === 3 && (
                                <Section3AssessmentEditor
                                    section3Questions={section3Questions}
                                    setSection3Questions={setSection3Questions}
                                    section3Prompts={section3Prompts}
                                    setSection3Prompts={setSection3Prompts}
                                    loadingS3={loadingS3}
                                    onRefreshS3={fetchSection3Questions}
                                    onSavePrompt={handleSaveS3Prompt}
                                    onSaveAllPrompts={handleSaveAllS3Prompts}
                                />
                            )}

                            {/* Step 4: ROADMAP (AI Prompt Configurator) */}
                            {item.id === 4 && (
                                <Section4RoadmapEditor
                                    roadmapConfig={roadmapConfig}
                                    setRoadmapConfig={setRoadmapConfig}
                                    onSaveRoadmap={handleSaveRoadmap}
                                />
                            )}

                            {/* Step 5: ENTRY LEVEL (Curriculum & AI Prompts) */}
                            {item.id === 5 && (
                                <Section5EntryLevelEditor
                                    entryLevelConfig={entryLevelConfig}
                                    setEntryLevelConfig={setEntryLevelConfig}
                                    mediumLevelConfig={mediumLevelConfig}
                                    setMediumLevelConfig={setMediumLevelConfig}
                                    advancedLevelConfig={advancedLevelConfig}
                                    setAdvancedLevelConfig={setAdvancedLevelConfig}
                                    roadmapConfig={roadmapConfig}
                                />
                            )}

                            {/* Step 6: MEDIUM LEVEL (Curriculum & Medium/Advanced AI Prompts) */}
                            {item.id === 6 && (
                                <LevelStageEditor
                                    levelConfig={mediumLevelConfig}
                                    setLevelConfig={setMediumLevelConfig}
                                    defaultConfig={DEFAULT_MEDIUM_LEVEL_CONFIG}
                                    levelLabel="Medium"
                                    stepNumber={6}
                                />
                            )}

                            {/* Step 7: ADVANCED LEVEL (AI Prompt Configuration) */}
                            {item.id === 7 && (
                                <LevelStageEditor
                                    levelConfig={advancedLevelConfig}
                                    setLevelConfig={setAdvancedLevelConfig}
                                    defaultConfig={DEFAULT_ADVANCED_LEVEL_CONFIG}
                                    levelLabel="Advanced"
                                    stepNumber={7}
                                />
                            )}

                            {/* Step 8: INTERNSHIPS (AI Prompt Configurator) */}
                            {item.id === 8 && (
                                <Section6InternshipsEditor
                                    internshipsConfig={internshipsConfig}
                                    setInternshipsConfig={setInternshipsConfig}
                                />
                            )}

                            {/* Step 9: YOUR CV (Automatic Derivation Info) */}
                            {item.id === 9 && (
                                <Section7CvAdminNotice />
                            )}

                            {/* Steps 10+: future stages */}
                            {item.id > 9 && (
                                <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
                                    This stage will be configured once previous modules are completed.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

/* ─────────────── LIVE PREVIEW — FULL TIMELINE ─────────────── */
function PreviewSection1({
    data,
    timelineItems,
    activeStepId,
    modules,
    section3Questions,
    section3Prompts,
    loadingS3,
    roadmapConfig,
    entryLevelConfig,
    mediumLevelConfig,
    advancedLevelConfig,
    internshipsConfig,
    cvConfig
}) {
    const [expandedId, setExpandedId] = useState(activeStepId || 1);

    useEffect(() => {
        if (activeStepId) {
            setExpandedId(activeStepId);
        }
    }, [activeStepId]);

    return (
        <div style={{ fontFamily: "'Outfit', 'Inter', sans-serif", backgroundColor: '#F4F0F8', minHeight: '100%', overflowX: 'hidden' }}>
            <header style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderBottom: '1.5px solid #E1D6EB', padding: '12px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontWeight: 900, fontSize: 20, color: '#3B1B77', letterSpacing: '-0.5px', lineHeight: 1 }}>C-RAP</div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.8px', marginTop: 3 }}>CAREER PREPARATION</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E1D6EB', borderRadius: 999, padding: '5px 14px 5px 8px', background: 'white' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <User size={15} color="#64748B" />
                    </div>
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>Srinivas</div>
                        <div style={{ fontSize: 11, color: '#94A3B8', lineHeight: 1.2 }}>B.Tech • CSE</div>
                    </div>
                    <ChevronDown size={14} color="#94A3B8" />
                </div>
            </header>

            <main style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
                {timelineItems.map((item, index) => {
                    const isLast = index === timelineItems.length - 1;
                    const isExpanded = expandedId === item.id;
                    return (
                        <div key={item.id} style={{ position: 'relative', display: 'flex' }}>
                            {!isLast && <div style={{ position: 'absolute', left: 15, top: 38, bottom: -1, width: 1.5, backgroundColor: '#E1D6EB', zIndex: 0 }} />}
                            <div style={{ flexShrink: 0, width: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 20, marginRight: 16, position: 'relative', zIndex: 10 }}>
                                {item.isLocked ? (
                                    <div style={{ width: 32, height: 32, borderRadius: 8, border: '1.5px solid #DDD5E8', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Lock size={14} color="#9ca3af" />
                                    </div>
                                ) : (
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#3B1B77', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                                        {item.id}
                                    </div>
                                )}
                            </div>

                            <div onClick={() => !item.isLocked && setExpandedId(isExpanded ? null : item.id)}
                                style={{ flexGrow: 1, borderBottom: !isLast ? '1.5px solid #E1D6EB' : 'none', paddingTop: 18, paddingBottom: 18, cursor: item.isLocked ? 'default' : 'pointer' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2px', color: item.subtitleColor || '#4F46E5', marginBottom: 3 }}>{item.subtitle}</div>
                                        <div style={{ fontWeight: 800, fontSize: 15, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{item.title}</div>
                                        {item.description && <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 3, lineHeight: 1.5 }}>{item.description}</div>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 999, background: STATUS_PILL[item.status]?.bg, color: STATUS_PILL[item.status]?.text, border: `1px solid ${STATUS_PILL[item.status]?.border}`, whiteSpace: 'nowrap' }}>
                                            {item.status}
                                        </span>
                                        <ChevronDown size={14} color="#9ca3af" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                                    </div>
                                </div>

                                {/* ── STEP 1: WHAT IS C-RAP ── */}
                                {isExpanded && item.id === 1 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <h3 style={{ fontWeight: 800, fontSize: 22, color: '#1E2478', marginBottom: 14, lineHeight: 1.3 }}>
                                            Start Your <span style={{ color: '#696DE5' }}>Career Journey</span> With C-RAP
                                        </h3>

                                        {/* Video + Description side-by-side card */}
                                        <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', border: '1.5px solid #F0EBFA', overflow: 'hidden', marginBottom: 16 }}>
                                            <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
                                                {/* Left: Video thumbnail */}
                                                <div style={{ position: 'relative', width: '55%', flexShrink: 0, background: '#0f172a', minHeight: 180 }}>
                                                    {data.introVideoUrl ? (
                                                        <video src={data.introVideoUrl} controls style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#94a3b8', textAlign: 'center', padding: 20, background: 'linear-gradient(135deg, rgba(76,42,133,0.3), rgba(30,18,50,0.8))', width: '100%', height: '100%', justifyContent: 'center', boxSizing: 'border-box', minHeight: 180 }}>
                                                            <Cpu size={28} color="#8F6BE8" style={{ opacity: 0.6 }} />
                                                            <span style={{ fontSize: 10, fontWeight: 600, maxWidth: 160 }}>Upload an intro video in the editor</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Right: Description */}
                                                <div style={{ flex: 1, padding: '20px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#696DE5', letterSpacing: '0.3px' }}>Watch First · 1 minute</div>
                                                    <div style={{ fontWeight: 800, fontSize: 15, color: '#111', lineHeight: 1.3 }}>What is C-RAP?</div>
                                                    <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
                                                        {data.introDescription || 'How your journey works, and what you unlock at each stage — from character building to your personal roadmap.'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Explore Your Career Path */}
                                        <div style={{ marginTop: 4 }}>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: '#696DE5', marginBottom: 12 }}>Explore Your Career Path</div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                                                {STATIC_CAREER_CARDS.map((card, idx) => (
                                                    <div key={idx} style={{ background: 'white', borderRadius: 12, border: '1.5px solid #E1D6EB', padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
                                                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F0EBFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{card.icon}</div>
                                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', lineHeight: 1.3 }}>{card.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ── STEP 2: PERSONALITY DEVELOPMENT — Full Module Preview ── */}
                                {isExpanded && item.id === 2 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewModules modules={modules} />
                                    </div>
                                )}

                                {/* ── STEP 3: ASSESSMENT — Dynamic AI Assessment Walkthrough ── */}
                                {isExpanded && item.id === 3 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewSection3
                                            questions={section3Questions}
                                            prompts={section3Prompts}
                                            loadingQuestions={loadingS3}
                                        />
                                    </div>
                                )}

                                {/* ── STEP 4: ROADMAP — S-Curve Interactive Career Highway ── */}
                                {isExpanded && item.id === 4 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewRoadmapSection roadmapConfig={roadmapConfig} />
                                    </div>
                                )}

                                {/* ── STEP 5: ENTRY LEVEL ── */}
                                {isExpanded && item.id === 5 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewSection5EntryLevel entryLevelConfig={entryLevelConfig} />
                                    </div>
                                )}

                                {/* ── STEP 6: MEDIUM LEVEL ── */}
                                {isExpanded && item.id === 6 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewLevelStage
                                            levelConfig={mediumLevelConfig}
                                            defaultConfig={DEFAULT_MEDIUM_LEVEL_CONFIG}
                                            levelLabel="Medium"
                                            accentColor="#0369a1"
                                        />
                                    </div>
                                )}

                                {/* ── STEP 7: ADVANCED LEVEL ── */}
                                {isExpanded && item.id === 7 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewLevelStage
                                            levelConfig={advancedLevelConfig}
                                            defaultConfig={DEFAULT_ADVANCED_LEVEL_CONFIG}
                                            levelLabel="Advanced"
                                            accentColor="#7C3AED"
                                        />
                                    </div>
                                )}

                                {/* ── STEP 8: INTERNSHIPS ── */}
                                {isExpanded && item.id === 8 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewSection8Internships internshipsConfig={internshipsConfig} />
                                    </div>
                                )}

                                {/* ── STEP 9: YOUR CV (Document Preview & Download) ── */}
                                {isExpanded && item.id === 9 && (
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 14 }}>
                                        <div style={{ borderTop: '1.5px solid #E1D6EB', marginBottom: 14 }} />
                                        <PreviewSection9Cv cvConfig={cvConfig} />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </main>
        </div>
    );
}

/* ─────────────── LIVE PREVIEW — SECTION 3 (DYNAMIC ASSESSMENT TREE & REAL COLLEGE / DEPT INTEGRATION) ─────────────── */
function PreviewSection3({ questions, prompts, loadingQuestions }) {
    const [currentQuestionId, setCurrentQuestionId] = useState('q1');
    const [answers, setAnswers] = useState({});
    const [history, setHistory] = useState([]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [generatingAI, setGeneratingAI] = useState(false);
    const [aiGeneratedOptions, setAiGeneratedOptions] = useState({});
    const [customFormInputs, setCustomFormInputs] = useState({});
    const [selectedMultiOptions, setSelectedMultiOptions] = useState([]);

    // Live College, Department, Specialization State
    const [colleges, setColleges] = useState([]);
    const q1OptionsFromBank = (questions || []).find(q => q.id === 'q1')?.options;
    const defaultDegreePrograms = q1OptionsFromBank && q1OptionsFromBank.length > 0
        ? q1OptionsFromBank.map(opt => typeof opt === 'string' ? opt : opt.text || opt.id)
        : ['B.Tech', 'BBA', 'B.Com', 'BCA', 'B.Sc', 'MBA', 'M.Tech', 'MCA', 'Other'];

    // const [degrees, setDegrees] = useState(defaultDegreePrograms);
    const [selectedCollege, setSelectedCollege] = useState(null);
    const [selectedDegree] = useState(defaultDegreePrograms[0] || 'B.Tech');
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [specialisations, setSpecialisations] = useState([]);
    const [selectedSpecialisation, setSelectedSpecialisation] = useState(null);
    const [loadingDepts, setLoadingDepts] = useState(false);
    const [loadingSpecs, setLoadingSpecs] = useState(false);
    // const [collegeSearch, setCollegeSearch] = useState('');

    // Fetch initial list of colleges on mount
    useEffect(() => {
        const loadInstitutions = async () => {
            try {
                const colRes = await UserApi.getInstitutions(true);
                const colList = Array.isArray(colRes?.data) ? colRes.data
                    : Array.isArray(colRes?.data?.items) ? colRes.data.items
                        : Array.isArray(colRes?.data?.institutions) ? colRes.data.institutions
                            : [];
                if (colList.length > 0) {
                    setColleges(colList);
                    setSelectedCollege(colList[0]);
                } else {
                    const fallbackCols = [
                        { id: 'inst_1', name: 'Indian Institute of Technology, Delhi' },
                        { id: 'inst_2', name: 'National Institute of Technology, Trichy' },
                        { id: 'inst_3', name: 'BITS Pilani' },
                        { id: 'inst_4', name: 'Vellore Institute of Technology' },
                        { id: 'inst_5', name: 'Delhi Technological University' }
                    ];
                    setColleges(fallbackCols);
                    setSelectedCollege(fallbackCols[0]);
                }
            } catch (err) {
                console.warn('[PreviewSection3] Failed to fetch colleges, using fallback demo list:', err);
                const fallbackCols = [
                    { id: 'inst_1', name: 'Indian Institute of Technology, Delhi' },
                    { id: 'inst_2', name: 'National Institute of Technology, Trichy' },
                    { id: 'inst_3', name: 'BITS Pilani' },
                    { id: 'inst_4', name: 'Vellore Institute of Technology' },
                    { id: 'inst_5', name: 'Delhi Technological University' }
                ];
                setColleges(fallbackCols);
                setSelectedCollege(fallbackCols[0]);
            }
        };

        loadInstitutions();
    }, []);

    // When selected college changes, live-fetch departments for that college!
    useEffect(() => {
        if (!selectedCollege?.id) return;
        const fetchDepts = async () => {
            setLoadingDepts(true);
            try {
                const deptRes = await CrapEditorApi.getDepartments(selectedCollege.id);
                const deptList = Array.isArray(deptRes?.data?.data) ? deptRes.data.data
                    : Array.isArray(deptRes?.data) ? deptRes.data
                        : [];
                if (deptList.length > 0) {
                    setDepartments(deptList);
                    setSelectedDepartment(deptList[0]);
                } else {
                    const fallbackDepts = [
                        { id: 'dept_civil', name: 'Civil Engineering' },
                        { id: 'dept_cse', name: 'Computer Science & Engineering' },
                        { id: 'dept_ece', name: 'Electronics & Communication Engineering' },
                        { id: 'dept_mech', name: 'Mechanical Engineering' },
                        { id: 'dept_it', name: 'Information Technology' },
                        { id: 'dept_eee', name: 'Electrical & Electronics Engineering' }
                    ];
                    setDepartments(fallbackDepts);
                    setSelectedDepartment(fallbackDepts[0]);
                }
            } catch (err) {
                console.warn('[PreviewSection3] Departments fetch fallback:', err);
                const fallbackDepts = [
                    { id: 'dept_civil', name: 'Civil Engineering' },
                    { id: 'dept_cse', name: 'Computer Science & Engineering' },
                    { id: 'dept_ece', name: 'Electronics & Communication Engineering' },
                    { id: 'dept_mech', name: 'Mechanical Engineering' },
                    { id: 'dept_it', name: 'Information Technology' },
                    { id: 'dept_eee', name: 'Electrical & Electronics Engineering' }
                ];
                setDepartments(fallbackDepts);
                setSelectedDepartment(fallbackDepts[0]);
            } finally {
                setLoadingDepts(false);
            }
        };

        fetchDepts();
    }, [selectedCollege]);

    // When selected department changes, live-fetch specializations for that department!
    useEffect(() => {
        if (!selectedCollege?.id || !selectedDepartment?.id) return;
        const fetchSpecs = async () => {
            setLoadingSpecs(true);
            try {
                const specRes = await CrapEditorApi.getSpecialisations(selectedCollege.id, selectedDepartment.id);
                const specList = Array.isArray(specRes?.data?.data) ? specRes.data.data
                    : Array.isArray(specRes?.data) ? specRes.data
                        : [];
                if (specList.length > 0) {
                    setSpecialisations(specList);
                    setSelectedSpecialisation(specList[0]);
                } else {
                    const isCivil = (selectedDepartment.name || '').toLowerCase().includes('civil');
                    const isCs = (selectedDepartment.name || '').toLowerCase().includes('computer') || (selectedDepartment.name || '').toLowerCase().includes('information');
                    const isMech = (selectedDepartment.name || '').toLowerCase().includes('mech');
                    const isEce = (selectedDepartment.name || '').toLowerCase().includes('elec') || (selectedDepartment.name || '').toLowerCase().includes('ece');

                    const fallbackSpecs = isCivil ? [
                        { id: 'spec_struct', name: 'Structural Engineering' },
                        { id: 'spec_const', name: 'Construction Technology & Project Management' },
                        { id: 'spec_env', name: 'Environmental & Water Resources Engineering' },
                        { id: 'spec_trans', name: 'Transportation & Highway Engineering' },
                        { id: 'spec_geo', name: 'Geotechnical Engineering' },
                        { id: 'spec_core_civil', name: 'Core Civil Engineering' }
                    ] : isCs ? [
                        { id: 'spec_aiml', name: 'Artificial Intelligence & Machine Learning' },
                        { id: 'spec_ds', name: 'Data Science & Big Data Analytics' },
                        { id: 'spec_cyber', name: 'Cyber Security & Cloud Defense' },
                        { id: 'spec_fullstack', name: 'Full-Stack Software Architecture' },
                        { id: 'spec_core_cs', name: 'Core Computer Systems & Algorithms' }
                    ] : isMech ? [
                        { id: 'spec_robotics', name: 'Robotics & Automation' },
                        { id: 'spec_cad', name: 'CAD/CAM & Product Design' },
                        { id: 'spec_thermal', name: 'Thermal & Fluid Engineering' },
                        { id: 'spec_auto', name: 'Automotive Engineering' },
                        { id: 'spec_core_mech', name: 'Core Mechanical Engineering' }
                    ] : isEce ? [
                        { id: 'spec_vlsi', name: 'VLSI Design & Microelectronics' },
                        { id: 'spec_embedded', name: 'Embedded Systems & IoT' },
                        { id: 'spec_telecom', name: 'Signal Processing & Telecommunications' },
                        { id: 'spec_core_ece', name: 'Core Electronics & Hardware' }
                    ] : [
                        { id: 'spec_general', name: 'Core Domain Engineering' },
                        { id: 'spec_applied', name: 'Applied Technology & Management' }
                    ];
                    setSpecialisations(fallbackSpecs);
                    setSelectedSpecialisation(fallbackSpecs[0]);
                }
            } catch (err) {
                console.warn('[PreviewSection3] Specialisations fetch fallback:', err);
                const fallbackSpecs = [
                    { id: 'spec_struct', name: 'Structural Engineering' },
                    { id: 'spec_const', name: 'Construction Technology & Management' },
                    { id: 'spec_core', name: 'Core Civil Systems' }
                ];
                setSpecialisations(fallbackSpecs);
                setSelectedSpecialisation(fallbackSpecs[0]);
            } finally {
                setLoadingSpecs(false);
            }
        };

        fetchSpecs();
    }, [selectedCollege, selectedDepartment]);

    // Dynamic AI option generation for ai_select questions using question's prompt + prior answers
    const fetchAiOptionsForQuestion = async (q) => {
        if (!q) return;
        setGeneratingAI(true);
        try {
            const priorQA = history.map(hId => {
                const prevQ = questions.find(item => item.id === hId);
                return {
                    question: prevQ?.question || hId,
                    answer: answers[hId]
                };
            });

            const deptName = selectedDepartment?.name || answers['q2'] || answers['q1'] || 'General';
            const collegeName = selectedCollege?.name || answers['q1'] || '';
            const yearVal = answers['q3'] || '';
            const cgpaVal = answers['q5'] || '';
            const targetRole = answers['job_role_pick'] || answers['career_stream_ai'] || '';

            const promptTemplate = prompts[q.id] || q.prompt || DEFAULT_SECTION3_PROMPTS[q.id] || `Evaluate student prior answers: {prior_answers}. Generate 4-5 tailored choices for "${q.question}". Return JSON: {"options": [{"id": string, "text": string}]}`;
            let promptToSend = promptTemplate
                .replace(/{prior_answers}/g, JSON.stringify(priorQA))
                .replace(/{{course}}/g, deptName)
                .replace(/{{college}}/g, collegeName)
                .replace(/{{year}}/g, yearVal)
                .replace(/{{cgpa}}/g, cgpaVal)
                .replace(/{{targetRole}}/g, targetRole);

            promptToSend += `\nStudent Context: Department/Course=${deptName}, College=${collegeName}, Year=${yearVal}, CGPA=${cgpaVal}\nPrior Answers: ${JSON.stringify(priorQA)}`;

            const aiRes = await CrapEditorApi.generateAssessmentQuestion('assessment-modules', { prompt: promptToSend });
            const aiData = aiRes.data?.data || aiRes.data;

            let finalOptions = null;

            // If backend returned a jobId (202 Async Job)
            if (aiData?.jobId) {
                console.log(`[PreviewSection3] Polling async AI job: ${aiData.jobId}...`);
                const jobResult = await CrapEditorApi.pollJobUntilDone(aiData.jobId);
                if (typeof jobResult === 'string') {
                    try {
                        const parsed = JSON.parse(jobResult);
                        finalOptions = parsed.options || parsed.items || parsed;
                    } catch {
                        finalOptions = [{ id: 'opt1', text: jobResult }];
                    }
                } else if (jobResult) {
                    finalOptions = jobResult.options || jobResult.items || jobResult;
                }
            } else if (aiData?.options || Array.isArray(aiData?.items)) {
                finalOptions = aiData.options || aiData.items;
            }

            if (finalOptions && Array.isArray(finalOptions)) {
                setAiGeneratedOptions(prev => ({
                    ...prev,
                    [q.id]: finalOptions
                }));
            }
        } catch (err) {
            console.warn('[PreviewSection3] AI generation fallback to tailored streams:', err);
            // High quality fallback options based on department
            const deptName = selectedDepartment?.name || 'Specialization Field';
            if (q.id === 'career_stream_ai') {
                setAiGeneratedOptions(prev => ({
                    ...prev,
                    [q.id]: [
                        { id: 'cs_1', text: `Industry Track & Direct Specialization in ${deptName}` },
                        { id: 'cs_2', text: `Consulting, Strategy & Operations` },
                        { id: 'cs_3', text: `Research & Higher Academic Pathways` },
                        { id: 'cs_4', text: `Public Sector & Interdisciplinary Roles` }
                    ]
                }));
            } else if (q.id === 'study_plan') {
                setAiGeneratedOptions(prev => ({
                    ...prev,
                    [q.id]: [
                        { id: 'pg_1', text: `Master's / Postgrad in ${deptName}` },
                        { id: 'pg_2', text: 'MBA in Management & Strategic Operations' },
                        { id: 'pg_3', text: 'Interdisciplinary Applied Research & Advanced Studies' },
                        { id: 'pg_4', text: 'Public Policy / Strategic Domain Leadership' }
                    ]
                }));
            } else if (q.id === 'job_role_pick') {
                setAiGeneratedOptions(prev => ({
                    ...prev,
                    [q.id]: [
                        { id: 'role_1', text: `Lead Analyst / Specialist in ${deptName}` },
                        { id: 'role_2', text: 'Strategic Operations & Management Associate' },
                        { id: 'role_3', text: 'Policy & Program Research Lead' },
                        { id: 'role_4', text: 'Project & Operations Coordinator' }
                    ]
                }));
            }
        } finally {
            setGeneratingAI(false);
        }
    };

    // Handler for Step 1 (College & Department)
    const handleStep1Submit = () => {
        const colName = selectedCollege?.name || 'Selected Institution';
        const deptName = selectedDepartment?.name || 'Engineering Department';
        const fullAnswer = `${colName} — ${deptName}`;
        const newAnswers = { ...answers, q1: fullAnswer };
        setAnswers(newAnswers);
        setHistory(prev => [...prev, 'q1']);
        setCurrentQuestionId('q2');

        CrapEditorApi.saveAssessmentProgress('assessment-modules', {
            currentQuestionId: 'q2',
            answers: newAnswers,
            completed: false
        }).catch(e => console.warn(e));
    };

    // Handler for Step 2 (Department & Specialisation)
    const handleStep2Submit = () => {
        const deptName = selectedDepartment?.name || 'Engineering Department';
        const specName = selectedSpecialisation?.name ? ` (${selectedSpecialisation.name})` : '';
        const fullAnswer = `${deptName}${specName}`;
        const newAnswers = { ...answers, q2: fullAnswer };
        setAnswers(newAnswers);
        setHistory(prev => [...prev, 'q2']);
        setCurrentQuestionId('q3');

        CrapEditorApi.saveAssessmentProgress('assessment-modules', {
            currentQuestionId: 'q3',
            answers: newAnswers,
            completed: false
        }).catch(e => console.warn(e));
    };

    const handleSelectChoice = (option) => {
        const optText = typeof option === 'string' ? option : (option.text || option.id);
        const nextQId = typeof option === 'object' && option.nextQuestionId ? option.nextQuestionId
            : currentQuestionId === 'q3' ? 'q5'
                : currentQuestionId === 'q5' ? 'q6'
                    : currentQuestionId === 'q6' ? (optText.toLowerCase().includes('job') ? 'career_stream_ai' : optText.toLowerCase().includes('further') ? 'q7' : 'self_projects')
                        : currentQuestionId === 'career_stream_ai' ? 'self_projects'
                            : currentQuestionId === 'q7' ? (optText.toLowerCase().includes('yes') ? 'countries_ai' : 'study_plan')
                                : currentQuestionId === 'study_plan' ? 'self_projects'
                                    : currentQuestionId === 'top_or_mid_uni' ? 'self_projects'
                                        : currentQuestionId === 'self_projects' ? (optText.toLowerCase().includes('yes') ? 'self_projects_detail' : 'internships')
                                            : currentQuestionId === 'internships' ? (optText.toLowerCase().includes('yes') ? 'internship_detail' : 'courses')
                                                : currentQuestionId === 'courses' ? (optText.toLowerCase().includes('yes') ? 'courses_detail' : 'job_role_known')
                                                    : currentQuestionId === 'job_role_known' ? (optText.toLowerCase().includes('know') ? 'job_role_pick' : 'spend')
                                                        : currentQuestionId === 'job_role_pick' ? 'spend'
                                                            : currentQuestionId === 'spend' ? 'likes_ai'
                                                                : null;

        const newAnswers = { ...answers, [currentQuestionId]: optText };
        setAnswers(newAnswers);
        setHistory(prev => [...prev, currentQuestionId]);

        CrapEditorApi.saveAssessmentProgress('assessment-modules', {
            currentQuestionId: nextQId || currentQuestionId,
            answers: newAnswers,
            completed: !nextQId
        }).catch(e => console.warn('Progress sync warning:', e));

        if (nextQId) {
            setCurrentQuestionId(nextQId);
            setSelectedMultiOptions([]);
            const nextNode = (questions || []).find(q => q.id === nextQId);
            if (nextQId === 'career_stream_ai' || nextQId === 'study_plan' || nextQId === 'countries_ai' || nextQId === 'job_role_pick' || nextQId === 'likes_ai' || (nextNode && nextNode.type === 'ai_select')) {
                fetchAiOptionsForQuestion(nextNode || { id: nextQId, question: 'Tailored AI Assessment' });
            }
        } else {
            setIsCompleted(true);
        }
    };

    const handleMultiSelectToggle = (option) => {
        const optText = typeof option === 'string' ? option : (option.text || option.id);
        setSelectedMultiOptions(prev =>
            prev.includes(optText) ? prev.filter(x => x !== optText) : [...prev, optText]
        );
    };

    const handleMultiSelectSubmit = () => {
        if (selectedMultiOptions.length === 0) return;
        const nextQId = currentQuestionId === 'countries_ai' ? 'top_or_mid_uni' : 'self_projects';
        const newAnswers = { ...answers, [currentQuestionId]: selectedMultiOptions };
        setAnswers(newAnswers);
        setHistory(prev => [...prev, currentQuestionId]);

        CrapEditorApi.saveAssessmentProgress('assessment-modules', {
            currentQuestionId: nextQId,
            answers: newAnswers,
            completed: false
        }).catch(e => console.warn('Progress sync warning:', e));

        setCurrentQuestionId(nextQId);
        setSelectedMultiOptions([]);
    };

    const handleFormSubmit = () => {
        const formAnswer = customFormInputs[currentQuestionId] || {};
        const newAnswers = { ...answers, [currentQuestionId]: formAnswer };
        setAnswers(newAnswers);
        setHistory(prev => [...prev, currentQuestionId]);

        const nextQId = currentQuestionId === 'self_projects_detail' ? 'internships'
            : currentQuestionId === 'internship_detail' ? 'courses'
                : currentQuestionId === 'courses_detail' ? 'job_role_known'
                    : 'job_role_known';

        CrapEditorApi.saveAssessmentProgress('assessment-modules', {
            currentQuestionId: nextQId,
            answers: newAnswers,
            completed: false
        }).catch(e => console.warn('Progress sync warning:', e));

        setCurrentQuestionId(nextQId);
    };

    const handleRestart = () => {
        setAnswers({});
        setHistory([]);
        setIsCompleted(false);
        setCustomFormInputs({});
        setSelectedMultiOptions([]);
        setCurrentQuestionId('q1');

        CrapEditorApi.saveAssessmentProgress('assessment-modules', {
            currentQuestionId: 'q1',
            answers: {},
            completed: false
        }).catch(e => console.warn(e));
    };

    const currentQObj = (questions || []).find(q => q.id === currentQuestionId) || {
        id: currentQuestionId,
        question: currentQuestionId === 'q1' ? 'What are you studying?'
            : currentQuestionId === 'q2' ? 'Which course / branch?'
                : currentQuestionId === 'q3' ? 'Which year are you studying?'
                    : currentQuestionId === 'q5' ? 'What is your Current GPA?'
                        : currentQuestionId === 'q6' ? 'After Graduation, what are your future plans?'
                            : currentQuestionId === 'career_stream_ai' ? 'Which career stream are you interested in?'
                                : currentQuestionId === 'q7' ? 'Are you looking for overseas studies?'
                                    : currentQuestionId === 'countries_ai' ? 'For your interested stream, these are the best 5 countries. Which top 2 do you pick?'
                                        : currentQuestionId === 'top_or_mid_uni' ? 'Are you looking for Top universities or Mid-level universities?'
                                            : currentQuestionId === 'study_plan' ? 'What are you planning to study?'
                                                : currentQuestionId === 'self_projects' ? 'Any self projects done?'
                                                    : currentQuestionId === 'internships' ? 'Any internships?'
                                                        : currentQuestionId === 'courses' ? 'Any courses / certifications beyond your college?'
                                                            : currentQuestionId === 'job_role_known' ? 'Do you know what job role you are interested in?'
                                                                : currentQuestionId === 'job_role_pick' ? 'Identify the job role you want to pursue'
                                                                    : currentQuestionId === 'spend' ? 'How much do you spend on courses / certifications?'
                                                                        : currentQuestionId === 'likes_ai' ? 'What do you like most?'
                                                                            : 'Assessment Question'
    };


    return (
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: '24px 28px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 20, border: '1.5px solid #F0EBFA' }}>
            {/* Header Bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1.5px solid #F0EBFA', paddingBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: '#F3EEFA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Sparkles size={18} color="#612D9B" />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#696DE5', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dynamic Assessment Live Preview</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#1E2478' }}>Personalized Roadmap Assessment</div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 99, background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0' }}>
                        {history.length} Answered
                    </span>
                    <button
                        onClick={handleRestart}
                        style={{ fontSize: 11, fontWeight: 700, color: '#612D9B', background: '#F3EEFA', border: '1px solid #D0C7E3', borderRadius: 20, padding: '4px 12px', cursor: 'pointer' }}
                    >
                        Restart Assessment
                    </button>
                </div>
            </div>

            {/* Assessment Progress Flow */}
            {!isCompleted && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {/* Active Question Title Card */}
                    <div style={{ background: '#FAF8FC', borderRadius: 14, padding: '18px 20px', border: '1.5px solid #EEDFEE', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: '#612D9B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Question ID: <code>{currentQuestionId}</code>
                            </span>
                            {(prompts[currentQuestionId] || currentQuestionId.includes('ai')) && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: '#059669', background: '#ECFDF5', border: '1px solid #A7F3D0', padding: '2px 8px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                    <Sparkles size={9} /> DeepSeek AI Generation Active
                                </span>
                            )}
                        </div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1E2478', lineHeight: 1.3 }}>
                            {currentQObj.question}
                        </h3>
                    </div>

                    {/* ── STEP 1: WHAT ARE YOU STUDYING (COLLEGE, LIVE DEPARTMENT & DEGREE) ── */}
                    {currentQuestionId === 'q1' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#1E40AF', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <School size={20} color="#2563EB" style={{ flexShrink: 0 }} />
                                <div>
                                    <strong>Live Institution Connected:</strong> Choose your college and select your department (e.g. <em>Civil Engineering, Computer Science</em>). Subsequent specialization choices will load live from your department.
                                </div>
                            </div>

                            {/* 1. College Selection */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>
                                    1. Select Your College / University:
                                </label>
                                <select
                                    value={selectedCollege?.id || ''}
                                    onChange={(e) => {
                                        const found = colleges.find(c => String(c.id) === String(e.target.value));
                                        if (found) setSelectedCollege(found);
                                    }}
                                    style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #CBD5E1', fontSize: 13, fontWeight: 700, color: '#1E2478', outline: 'none', background: 'white', boxSizing: 'border-box' }}
                                >
                                    {colleges.map(c => (
                                        <option key={c.id} value={c.id}>{c.name || c.institutionName || `Institution ${c.id}`}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 2. Department Selection (Fetched Live from College) */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>
                                        2. Choose Your Department (Live from {selectedCollege?.name || 'College'}):
                                    </label>
                                    {loadingDepts && <span style={{ fontSize: 11, color: '#581C87', fontWeight: 600 }}>Loading departments...</span>}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
                                    {departments.map((dept, dIdx) => {
                                        const isSelected = selectedDepartment?.id === dept.id;
                                        return (
                                            <div
                                                key={dIdx}
                                                onClick={() => setSelectedDepartment(dept)}
                                                style={{
                                                    padding: '14px 16px',
                                                    borderRadius: 10,
                                                    border: isSelected ? '2px solid #581C87' : '1.5px solid #CBD5E1',
                                                    background: isSelected ? '#FAF5FF' : 'white',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    boxShadow: isSelected ? '0 2px 8px rgba(88,28,135,0.12)' : 'none',
                                                    transition: 'all 0.15s ease'
                                                }}
                                            >
                                                <div style={{ width: 18, height: 18, borderRadius: '50%', border: isSelected ? '5px solid #581C87' : '2px solid #94A3B8', background: 'white' }} />
                                                <span style={{ fontSize: 13, fontWeight: isSelected ? 800 : 600, color: isSelected ? '#581C87' : '#1E293B' }}>
                                                    {dept.name}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                <button
                                    onClick={handleStep1Submit}
                                    style={{ background: '#581C87', color: 'white', border: 'none', borderRadius: 99, padding: '12px 28px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    Continue to Branch / Specialization →
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── STEP 2: WHICH COURSE / BRANCH / SPECIALIZATION (q2) ── */}
                    {currentQuestionId === 'q2' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            <div style={{ background: '#FAF5FF', border: '1px solid #D8B4FE', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#581C87', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <GraduationCap size={20} color="#7E22CE" style={{ flexShrink: 0 }} />
                                <div>
                                    <strong>Department:</strong> {selectedDepartment?.name || 'Department'} • <strong>College:</strong> {selectedCollege?.name || 'College'}
                                </div>
                            </div>

                            {/* Specialization Grid */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>
                                        Choose Specialization / Branch in {selectedDepartment?.name || 'Department'}:
                                    </label>
                                    {loadingSpecs && <span style={{ fontSize: 11, color: '#581C87', fontWeight: 600 }}>Loading specializations...</span>}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                                    {specialisations.map((spec, sIdx) => {
                                        const isSelected = selectedSpecialisation?.id === spec.id;
                                        return (
                                            <div
                                                key={sIdx}
                                                onClick={() => setSelectedSpecialisation(spec)}
                                                style={{
                                                    padding: '14px 18px',
                                                    borderRadius: 10,
                                                    border: isSelected ? '2px solid #581C87' : '1.5px solid #CBD5E1',
                                                    background: isSelected ? '#FAF5FF' : 'white',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    boxShadow: isSelected ? '0 2px 8px rgba(88,28,135,0.12)' : 'none',
                                                    transition: 'all 0.15s ease'
                                                }}
                                            >
                                                <div style={{ width: 20, height: 20, borderRadius: '50%', border: isSelected ? '6px solid #581C87' : '2px solid #94A3B8', background: 'white', flexShrink: 0 }} />
                                                <span style={{ fontSize: 13, fontWeight: isSelected ? 800 : 600, color: isSelected ? '#581C87' : '#1E293B', lineHeight: 1.3 }}>
                                                    {spec.name}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                                <button
                                    onClick={handleStep2Submit}
                                    style={{ background: '#581C87', color: 'white', border: 'none', borderRadius: 99, padding: '12px 28px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    Continue to Year of Study →
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── STANDARD MCQs, AI OPTIONS & FORMS (q3 onwards) ── */}
                    {currentQuestionId !== 'q1' && currentQuestionId !== 'q2' && (
                        <div>
                            {generatingAI ? (
                                <div style={{ padding: 40, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                                    <Loader size={28} color="#581C87" className="spin" />
                                    <span style={{ fontSize: 13, color: '#581C87', fontWeight: 700 }}>DeepSeek AI is evaluating answers and generating tailored streams...</span>
                                </div>
                            ) : currentQuestionId.includes('detail') ? (
                                /* Form Field Questions */
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {(currentQuestionId === 'self_projects_detail' ? [
                                        { id: 'objective', label: 'Project Objective' },
                                        { id: 'tech', label: 'Tech Used / Frameworks' },
                                        { id: 'status', label: 'Project Status (Completed / In Progress)' }
                                    ] : currentQuestionId === 'internship_detail' ? [
                                        { id: 'role', label: 'Internship Role' },
                                        { id: 'responsibilities', label: 'Key Responsibilities' },
                                        { id: 'duration', label: 'Duration (Months)' }
                                    ] : [
                                        { id: 'courseName', label: 'Course Name' },
                                        { id: 'provider', label: 'Provider / Platform' },
                                        { id: 'status', label: 'Status' }
                                    ]).map(fld => (
                                        <div key={fld.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>{fld.label}</label>
                                            <input
                                                type="text"
                                                placeholder={`Enter ${fld.label}...`}
                                                value={customFormInputs[currentQuestionId]?.[fld.id] || ''}
                                                onChange={(e) => setCustomFormInputs({
                                                    ...customFormInputs,
                                                    [currentQuestionId]: {
                                                        ...(customFormInputs[currentQuestionId] || {}),
                                                        [fld.id]: e.target.value
                                                    }
                                                })}
                                                style={{ padding: '10px 14px', borderRadius: 10, border: '1.5px solid #CBD5E1', fontSize: 13, outline: 'none' }}
                                            />
                                        </div>
                                    ))}
                                    <button
                                        onClick={handleFormSubmit}
                                        style={{ alignSelf: 'flex-end', background: '#581C87', color: 'white', border: 'none', borderRadius: 99, padding: '11px 26px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                                    >
                                        Continue →
                                    </button>
                                </div>
                            ) : currentQuestionId === 'countries_ai' ? (
                                /* Multi-Select Overseas Countries */
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                                        {(aiGeneratedOptions.countries_ai || [
                                            { id: 'c1', text: 'United States (Top STEM programs)' },
                                            { id: 'c2', text: 'Germany (Tuition-Free Tech Universities)' },
                                            { id: 'c3', text: 'United Kingdom (1-Year Focused Masters)' },
                                            { id: 'c4', text: 'Canada (Post-Study Work Friendly)' },
                                            { id: 'c5', text: 'Australia (High Quality Research)' }
                                        ]).map((cOpt, idx) => {
                                            const optText = typeof cOpt === 'string' ? cOpt : cOpt.text;
                                            const isSelected = selectedMultiOptions.includes(optText);
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleMultiSelectToggle(cOpt)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px',
                                                        borderRadius: 12, border: isSelected ? '2px solid #581C87' : '1.5px solid #E2E8F0',
                                                        background: isSelected ? '#FAF5FF' : 'white', cursor: 'pointer'
                                                    }}
                                                >
                                                    <div style={{ width: 20, height: 20, borderRadius: 6, border: isSelected ? '2px solid #581C87' : '2px solid #CBD5E1', background: isSelected ? '#581C87' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {isSelected && <Check size={14} color="white" />}
                                                    </div>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: isSelected ? '#581C87' : '#1E2478' }}>
                                                        {optText}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            disabled={selectedMultiOptions.length === 0}
                                            onClick={handleMultiSelectSubmit}
                                            style={{
                                                background: selectedMultiOptions.length === 0 ? '#E5E7EB' : '#581C87',
                                                color: selectedMultiOptions.length === 0 ? '#9CA3AF' : 'white',
                                                border: 'none', borderRadius: 99, padding: '11px 26px', fontSize: 13, fontWeight: 700,
                                                cursor: selectedMultiOptions.length === 0 ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            Continue with Selected ({selectedMultiOptions.length}) →
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* Standard & AI Dynamic Choice Grid */
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
                                    {(aiGeneratedOptions[currentQuestionId] || (
                                        currentQuestionId === 'q3' ? [
                                            { id: '1st', text: '1st Year' },
                                            { id: '2nd', text: '2nd Year' },
                                            { id: '3rd', text: '3rd Year' },
                                            { id: '4th', text: '4th Year' }
                                        ] : currentQuestionId === 'q5' ? [
                                            { id: 'gpa_1', text: 'Below 6.0' },
                                            { id: 'gpa_2', text: '6.0 – 7.0' },
                                            { id: 'gpa_3', text: '7.0 – 8.0' },
                                            { id: 'gpa_4', text: '8.0 – 9.0' },
                                            { id: 'gpa_5', text: 'Above 9.0' }
                                        ] : currentQuestionId === 'q6' ? [
                                            { id: 'job', text: 'Job Placements (Industry Role)' },
                                            { id: 'studies', text: 'Further Studies (Masters / MBA)' },
                                            { id: 'not_sure', text: 'Not Sure Yet' }
                                        ] : currentQuestionId === 'q7' ? [
                                            { id: 'yes', text: 'Yes, looking for overseas studies' },
                                            { id: 'no', text: 'No, domestic programs only' }
                                        ] : currentQuestionId === 'study_plan' ? [
                                            { id: 'pg_1', text: `Master's / Postgrad in ${selectedDepartment?.name || 'Department Specialization'}` },
                                            { id: 'pg_2', text: 'MBA in Management & Strategic Operations' },
                                            { id: 'pg_3', text: 'Interdisciplinary Applied Research & Advanced Studies' },
                                            { id: 'pg_4', text: 'Public Policy / Strategic Domain Leadership' }
                                        ] : currentQuestionId === 'self_projects' ? [
                                            { id: 'yes', text: 'Yes, built projects outside curriculum' },
                                            { id: 'no', text: 'No projects yet' }
                                        ] : currentQuestionId === 'internships' ? [
                                            { id: 'yes', text: 'Yes, completed internship experience' },
                                            { id: 'no', text: 'No internships yet' }
                                        ] : currentQuestionId === 'courses' ? [
                                            { id: 'yes', text: 'Yes, certifications / online courses done' },
                                            { id: 'no', text: 'No external courses' }
                                        ] : currentQuestionId === 'job_role_known' ? [
                                            { id: 'know', text: 'Yes, I know my target role' },
                                            { id: 'few', text: '2-3 roles in mind' },
                                            { id: 'unknown', text: 'Need AI guidance to explore' }
                                        ] : currentQuestionId === 'job_role_pick' ? [
                                            { id: 'role_1', text: `Lead Analyst / Specialist in ${selectedDepartment?.name || 'Domain'}` },
                                            { id: 'role_2', text: 'Strategic Operations & Management Associate' },
                                            { id: 'role_3', text: 'Policy & Program Research Lead' },
                                            { id: 'role_4', text: 'Project & Operations Coordinator' }
                                        ] : currentQuestionId === 'spend' ? [
                                            { id: '0', text: '₹0 (Free resources only)' },
                                            { id: '3000', text: '₹3,000' },
                                            { id: '5000', text: '₹5,000' },
                                            { id: '10000', text: '₹10,000+' }
                                        ] : currentQuestionId === 'likes_ai' ? [
                                            { id: 'l1', text: 'Building End-to-End GenAI & RAG Applications' },
                                            { id: 'l2', text: 'Scalable Microservices & Backend Systems' },
                                            { id: 'l3', text: 'Interactive User Interfaces & Product Design' },
                                            { id: 'l4', text: 'Data Pipelines & Distributed Machine Learning' }
                                        ] : [
                                            { id: 'opt_1', text: 'Option A' },
                                            { id: 'opt_2', text: 'Option B' }
                                        ]
                                    )).map((opt, oIdx) => {
                                        const optText = typeof opt === 'string' ? opt : (opt.text || opt.name || opt.id);
                                        const letter = String.fromCharCode(65 + oIdx);

                                        return (
                                            <div
                                                key={oIdx}
                                                onClick={() => handleSelectChoice(opt)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    padding: '16px 18px',
                                                    borderRadius: 12,
                                                    border: '2px solid #E5DDF2',
                                                    background: '#FFFFFF',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.borderColor = '#581C87';
                                                    e.currentTarget.style.backgroundColor = '#FAF7FE';
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.borderColor = '#E5DDF2';
                                                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#F3EEFA', color: '#581C87', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                                                    {letter}
                                                </div>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: '#1E2478', lineHeight: 1.4 }}>
                                                    {optText}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Prior Answers Summary */}
                    {history.length > 0 && (
                        <div style={{ marginTop: 10, borderTop: '1px dashed #E5DDF2', paddingTop: 14 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#736885', textTransform: 'uppercase', marginBottom: 8 }}>
                                Prior Answers Shaping Assessment:
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {history.map((hId, idx) => (
                                    <div key={idx} style={{ fontSize: 11, background: '#F5F3FF', border: '1px solid #D9CBF0', padding: '4px 10px', borderRadius: 99, color: '#4C2A85' }}>
                                        <strong>{hId}:</strong> {typeof answers[hId] === 'object' ? JSON.stringify(answers[hId]) : answers[hId]}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Completed Screen */}
            {isCompleted && (
                <div style={{ textAlign: 'center', padding: '30px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckCircle2 size={32} color="#16A34A" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1E2478' }}>
                        Assessment Flow Completed!
                    </h3>
                    <p style={{ margin: 0, fontSize: 13, color: '#6B7280', maxWidth: 460, lineHeight: 1.6 }}>
                        All response branches were traversed and personalized roadmap data has been populated from your dynamic assessment prompts.
                    </p>
                    <button
                        onClick={handleRestart}
                        style={{ marginTop: 8, background: '#581C87', color: 'white', border: 'none', borderRadius: 99, padding: '12px 30px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                        Retake Assessment Preview
                    </button>
                </div>
            )}
        </div>
    );
}

/* ─────────────── LIVE PREVIEW — SECTION 2 (REAL AI API INTEGRATION) ─────────────── */
function PreviewModules({ modules }) {
    const [activeMod, setActiveMod] = useState(0);
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'exercise' | 'result' | 'prompts'

    // Exercise Navigation
    const [activeExerciseIndex, setActiveExerciseIndex] = useState(0); // 0 to 4 (5 exercises)
    const [completedExercises, setCompletedExercises] = useState(new Set());
    const [videoWatched] = useState(true);

    // AI Generation & Polling State
    const [loadingAI, setLoadingAI] = useState(false);
    const [aiStatusMessage, setAiStatusMessage] = useState('');
    const [activeExerciseId, setActiveExerciseId] = useState(null);

    // Question State within the active exercise (5 questions per exercise)
    const [exerciseQuestions, setExerciseQuestions] = useState([]);
    const [currentQIndex, setCurrentQIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [exerciseResults, setExerciseResults] = useState({});

    if (!modules || modules.length === 0) {
        return (
            <div style={{ padding: 40, textAlign: 'center', backgroundColor: 'white', borderRadius: 16, margin: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                <BookOpen size={48} color="#C4B8D8" style={{ marginBottom: 12 }} />
                <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#1e2478' }}>No Section 2 Modules</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>Add your first module in Step 2 to preview how students experience it.</p>
            </div>
        );
    }

    const mod = modules[activeMod] || modules[0];
    const totalExercises = 5;

    // Trigger Real DeepSeek AI Exercise Generation via API
    const startExercise = async (index) => {
        setActiveExerciseIndex(index);
        setCurrentQIndex(0);
        setSelectedAnswers({});
        setActiveTab('exercise');
        setLoadingAI(true);
        setAiStatusMessage(`Invoking DeepSeek AI for Exercise ${index + 1}...`);

        try {
            if (mod.id) {
                // Call real endpoint
                const genRes = await CrapEditorApi.generateExercise(mod.id);
                const jobId = genRes.data?.data?.jobId;
                if (jobId) {
                    setAiStatusMessage('AI is generating 5 contextual questions. Polling queue...');
                    // Poll job
                    let completed = false;
                    let pollAttempts = 0;
                    while (!completed && pollAttempts < 15) {
                        await new Promise(r => setTimeout(r, 2000));
                        pollAttempts++;
                        const jobRes = await CrapEditorApi.getJobStatus(jobId);
                        const jobData = jobRes.data?.data;
                        if (jobData?.status === 'completed' || jobData?.result) {
                            completed = true;
                            const result = jobData.result || {};
                            setActiveExerciseId(result.exerciseId || jobId);
                            if (Array.isArray(result.questions) && result.questions.length > 0) {
                                setExerciseQuestions(result.questions);
                                setLoadingAI(false);
                                return;
                            }
                        } else if (jobData?.status === 'failed') {
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.warn('[Preview] Real AI generation failed or backend returned offline status. Using dynamic fallback structure:', err);
        }

        // Contextual Fallback based on module's actual name and exercise index
        setExerciseQuestions([
            {
                id: `ex${index + 1}_q1`,
                question: `In ${mod.name || 'this module'}, when facing unexpected project or exam deadlines, what is your initial move?`,
                options: [
                    'Glanced at it, told myself there is "so much time"',
                    'Made a detailed study plan in notebook — felt productive just for making it',
                    'Built a backward calendar from each deadline, allocating extra time to weak spots'
                ]
            },
            {
                id: `ex${index + 1}_q2`,
                question: `How do you systematically structure your weekly ${mod.name || 'skill'} review?`,
                options: [
                    'Wait until final announcements before opening reference material',
                    'Revise weekly from month 1, actively flagging bottlenecks as you go',
                    'Only read summary notes the evening after a lecture'
                ]
            },
            {
                id: `ex${index + 1}_q3`,
                question: `During the 3 days leading up to a critical evaluation milestone in ${mod.name || 'this course'}:`,
                options: [
                    'Frantic 14-hour cram sessions, chai, snacks, sleep at 4 AM',
                    'Calm structured revision and mock tests, sleep by 10:30 PM, 7 hours rest',
                    'Stay up all night on group calls with peers'
                ]
            },
            {
                id: `ex${index + 1}_q4`,
                question: 'How do you evaluate and eliminate cognitive distractions while executing high-focus tasks?',
                options: [
                    'Place phone in another room or enforce strict Pomodoro focus blocks',
                    'Keep phone on desk face up with vibration active',
                    'Switch between social tabs and study materials continuously'
                ]
            },
            {
                id: `ex${index + 1}_q5`,
                question: 'What is your primary action after receiving evaluation results and feedback?',
                options: [
                    'Short 2-week burst of motivation, but no lasting systemic adjustment',
                    'Refine your operational framework permanently for the next cycle',
                    'Blame external factors and move on without analysis'
                ]
            }
        ]);
        setLoadingAI(false);
    };

    const handleSelectOption = (qId, optionText) => {
        setSelectedAnswers(prev => ({
            ...prev,
            [qId]: optionText
        }));
    };

    const handleNextQuestion = async () => {
        if (currentQIndex + 1 < exerciseQuestions.length) {
            setCurrentQIndex(currentQIndex + 1);
        } else {
            // Submit & Generate Comparison Path
            setLoadingAI(true);
            setAiStatusMessage('Synthesizing comparison path with DeepSeek AI...');

            let finalYourPath = [
                'Schedule announced - made a study plan on paper, felt productive',
                'Only ~30% revised going in - weekend catch-ups, no weekly rhythm',
                'Last 3 days: 14-hour cram sessions, sleep at 4 AM',
                'Walked in calm - rehearsal under pressure, but pressure was avoidable',
                'After results: 2-week motivation burst, no lasting system change'
            ];
            let finalPriyasPath = [
                'Schedule announced - backward calendar built in 30 minutes',
                'Weekly revision from month 1 - weak subjects flagged by week 3',
                'One full revision done 5 days early; last 5 days for past papers',
                '7 hours sleep nightly - walked in revising, not learning for the first time',
                'After results: refines the system permanently for next semester'
            ];

            try {
                if (mod.id) {
                    const pathPayload = activeExerciseId
                        ? { exerciseId: activeExerciseId, selectedAnswers }
                        : { questions: exerciseQuestions, selectedAnswers };

                    const pathRes = await CrapEditorApi.generatePath(mod.id, pathPayload);
                    const jobId = pathRes.data?.data?.jobId;
                    if (jobId) {
                        let completed = false;
                        let pollAttempts = 0;
                        while (!completed && pollAttempts < 15) {
                            await new Promise(r => setTimeout(r, 2000));
                            pollAttempts++;
                            const jobRes = await CrapEditorApi.getJobStatus(jobId);
                            const jobData = jobRes.data?.data;
                            if (jobData?.status === 'completed' || jobData?.result) {
                                completed = true;
                                const result = jobData.result || {};
                                if (Array.isArray(result.yourPath)) finalYourPath = result.yourPath;
                                if (Array.isArray(result.priyasPath)) finalPriyasPath = result.priyasPath;
                                break;
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn('[Preview] Path API generation failed or offline. Using contextual paths:', err);
            }

            const answeredList = exerciseQuestions.map((q, idx) => ({
                questionText: q.question,
                selected: selectedAnswers[q.id] || 'No answer selected',
                tag: idx % 2 === 0 ? 'Ideal' : 'Normal',
                idealExplanation: 'Calm backward planning and consistent weekly checkpoints prevent last-minute emergencies.'
            }));

            const idealCount = answeredList.filter(a => a.tag === 'Ideal').length;
            const scoreLabel = idealCount >= 4 ? 'Mostly "Ideal" Student' : idealCount >= 2 ? 'Mostly "Normal" Student' : 'Needs Optimization';

            setExerciseResults(prev => ({
                ...prev,
                [activeExerciseIndex]: {
                    score: `${idealCount}/${exerciseQuestions.length}`,
                    scoreLabel,
                    answers: answeredList,
                    pathComparison: {
                        yourPath: finalYourPath,
                        priyasPath: finalPriyasPath
                    }
                }
            }));

            setCompletedExercises(prev => new Set([...prev, activeExerciseIndex]));
            setLoadingAI(false);
            setActiveTab('result');
        }
    };

    const nextExercise = () => {
        if (activeExerciseIndex + 1 < totalExercises) {
            startExercise(activeExerciseIndex + 1);
        } else {
            setActiveTab('overview');
        }
    };

    const currentQuestion = exerciseQuestions[currentQIndex];
    const currentResult = exerciseResults[activeExerciseIndex];

    return (
        <div style={{ fontFamily: "'Outfit', 'Inter', sans-serif", backgroundColor: '#F3EDF7', minHeight: '100%', padding: '24px 16px', position: 'relative' }}>
            <div style={{ maxWidth: 1050, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>

                {/* Module selector tabs */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingBottom: 4 }}>
                    {modules.map((m, i) => {
                        const isActive = activeMod === i;
                        return (
                            <button
                                key={m.id || i}
                                onClick={() => {
                                    setActiveMod(i);
                                    setActiveTab('overview');
                                    setActiveExerciseIndex(0);
                                    setCurrentQIndex(0);
                                }}
                                style={{
                                    padding: '8px 18px',
                                    borderRadius: 999,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    border: 'none',
                                    backgroundColor: isActive ? '#4C2A85' : 'white',
                                    color: isActive ? 'white' : '#4C2A85',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6
                                }}
                            >
                                <span style={{ opacity: 0.7 }}>#{m.order || i + 1}</span>
                                {m.name || `Module ${i + 1}`}
                            </button>
                        );
                    })}
                </div>

                {/* Dual Column Layout: Left Stepper + Right Panel matching Figma */}
                <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

                    {/* LEFT SIDEBAR NAVIGATION (Intro Video + Exercise 1..5) */}
                    <div style={{ width: 230, backgroundColor: 'white', borderRadius: 16, padding: '20px 16px', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 16, flexShrink: 0 }}>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{mod.name || 'Module Preview'}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
                                5 Exercises Configured
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
                            <div style={{ position: 'absolute', left: 11, top: 12, bottom: 12, width: 2, backgroundColor: '#E5DDF2', zIndex: 0 }} />

                            {/* Intro Video step */}
                            <div
                                onClick={() => setActiveTab('overview')}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', cursor: 'pointer', position: 'relative', zIndex: 1 }}
                            >
                                {videoWatched ? (
                                    <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#00BA63', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Check size={12} color="white" strokeWidth={3} />
                                    </div>
                                ) : (
                                    <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #4C2A85', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4C2A85' }} />
                                    </div>
                                )}
                                <span style={{ fontSize: 13, fontWeight: activeTab === 'overview' ? 700 : 500, color: videoWatched ? '#00BA63' : '#4C2A85' }}>Introduction Video</span>
                            </div>

                            {/* 5 Exercises */}
                            {Array.from({ length: totalExercises }).map((_, i) => {
                                const isDone = completedExercises.has(i);
                                const isCurrent = (activeTab === 'exercise' || activeTab === 'result') && activeExerciseIndex === i;
                                return (
                                    <div
                                        key={i}
                                        onClick={() => startExercise(i)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', cursor: 'pointer', position: 'relative', zIndex: 1 }}
                                    >
                                        {isDone ? (
                                            <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#00BA63', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <Check size={12} color="white" strokeWidth={3} />
                                            </div>
                                        ) : isCurrent ? (
                                            <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #4C2A85', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4C2A85' }} />
                                            </div>
                                        ) : (
                                            <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #C4B8D8', backgroundColor: '#EDE8F4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <span style={{ fontSize: 10, fontWeight: 700, color: '#9577B9' }}>{i + 1}</span>
                                            </div>
                                        )}
                                        <span style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500, color: isDone ? '#00BA63' : isCurrent ? '#4C2A85' : '#736885' }}>
                                            Exercise {i + 1}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* RIGHT MAIN PANEL */}
                    <div style={{ flex: 1, backgroundColor: 'white', borderRadius: 16, padding: 28, boxShadow: '0 4px 20px rgba(0,0,0,0.04)', minHeight: 520, display: 'flex', flexDirection: 'column' }}>

                        {/* Header matching student Figma view */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1.5px solid #f0ebfa', paddingBottom: 14, marginBottom: 20 }}>
                            <div>
                                <span style={{ backgroundColor: '#4C2A85', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 12px', borderRadius: 99 }}>
                                    {activeTab === 'overview' ? 'Intro Video' : `Exercise ${activeExerciseIndex + 1}`}
                                </span>
                                <h2 style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 800, color: '#111' }}>
                                    {activeTab === 'overview' ? `Getting Started with ${mod.name || 'Module'}` : `Exercise ${activeExerciseIndex + 1}: ${mod.name || 'Module'}`}
                                </h2>
                            </div>
                        </div>

                        {/* 1. INTRO VIDEO VIEW */}
                        {activeTab === 'overview' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
                                    Learn how effective planning, prioritization, and consistency can help you achieve your goals faster. Watch the video, then complete the 5 exercises in order.
                                </p>
                                <div style={{ borderRadius: 14, overflow: 'hidden', border: '1.5px solid #E5DDF2', backgroundColor: '#0f172a', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {(mod.videoUrl || mod.introVideoUrl) ? (
                                        <video
                                            key={mod.videoUrl || mod.introVideoUrl}
                                            src={mod.videoUrl || mod.introVideoUrl}
                                            controls
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <div style={{ textAlign: 'center', color: '#999', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                                            <PlayCircle size={54} color="#8F6BE8" />
                                            <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>No Intro Video Uploaded</div>
                                            <div style={{ fontSize: 12, color: '#94a3b8', maxWidth: 280 }}>Upload an intro video in the left editor panel to see it displayed here.</div>
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => startExercise(0)}
                                    style={{ alignSelf: 'flex-end', backgroundColor: '#4C2A85', color: 'white', padding: '12px 28px', borderRadius: 99, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                                >
                                    Mark as Watched & Start Exercise 1 →
                                </button>
                            </div>
                        )}

                        {/* 2. INTERACTIVE EXERCISE VIEW (Question 1..5 matching FIGMA) */}
                        {activeTab === 'exercise' && loadingAI && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 14 }}>
                                <Loader size={34} color="#4C2A85" className="spin" />
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#4C2A85' }}>{aiStatusMessage || 'Generating Questions with AI...'}</div>
                                <div style={{ fontSize: 12, color: '#9ca3af' }}>DeepSeek model is generating tailored assessment items</div>
                            </div>
                        )}

                        {activeTab === 'exercise' && !loadingAI && currentQuestion && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                                {/* Scenario Card */}
                                <div style={{ background: '#FAF8FC', borderRadius: 12, padding: '18px 20px', border: '1.5px solid #EEDFEE' }}>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: '#4C2A85', textTransform: 'uppercase', marginBottom: 6 }}>Scenario</div>
                                    <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
                                        {currentQuestion.scenario || `Evaluating real-world application & tactical decision making for ${mod.name || 'this module'}.`}
                                    </p>
                                </div>

                                {/* Question Header & Counter */}
                                <div style={{ background: '#F8FAFC', borderRadius: 10, padding: '12px 16px', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 24, height: 24, borderRadius: 6, border: '1.5px solid #4C2A85', color: '#4C2A85', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                                        {currentQIndex + 1}
                                    </div>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1E2478' }}>{currentQuestion.question}</span>
                                </div>

                                {/* Options List */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {currentQuestion.options.map((opt, oIdx) => {
                                        const letter = String.fromCharCode(65 + oIdx);
                                        const optText = typeof opt === 'string' ? opt : (opt.text || opt.label || '');
                                        const isSelected = selectedAnswers[currentQuestion.id] === optText || selectedAnswers[currentQuestion.id]?.text === optText;
                                        return (
                                            <div
                                                key={oIdx}
                                                onClick={() => handleSelectOption(currentQuestion.id, optText)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    padding: '14px 18px',
                                                    borderRadius: 12,
                                                    border: `2px solid ${isSelected ? '#4C2A85' : '#E5DDF2'}`,
                                                    backgroundColor: isSelected ? '#F5EDF8' : 'white',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: isSelected ? '#4C2A85' : '#4C2A85', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                                                    {letter}
                                                </div>
                                                <span style={{ fontSize: 13, color: '#333', fontWeight: 500, lineHeight: 1.4 }}>{optText}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={handleNextQuestion}
                                    disabled={!selectedAnswers[currentQuestion.id]}
                                    style={{
                                        alignSelf: 'flex-end',
                                        backgroundColor: selectedAnswers[currentQuestion.id] ? '#4C2A85' : '#c4b5da',
                                        color: 'white',
                                        padding: '12px 30px',
                                        borderRadius: 99,
                                        border: 'none',
                                        fontWeight: 700,
                                        fontSize: 13,
                                        cursor: selectedAnswers[currentQuestion.id] ? 'pointer' : 'not-allowed',
                                        marginTop: 6
                                    }}
                                >
                                    {currentQIndex + 1 < exerciseQuestions.length ? 'Next Question →' : 'Submit & View Evaluation ✓'}
                                </button>
                            </div>
                        )}

                        {/* 3. EXERCISE RESULTS & COMPARISON ROADMAP (Matching FIGMA Result Screen) */}
                        {activeTab === 'result' && currentResult && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

                                {/* Score & Badge Banner */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', padding: '16px 20px', borderRadius: 16, border: '1.5px solid #F0EBFA', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                        {/* Circular Score Gauge */}
                                        <div style={{ width: 56, height: 56, borderRadius: '50%', border: '4px solid #4C2A85', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, color: '#1E2478' }}>
                                            {currentResult.score}
                                            <span style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, marginTop: -2 }}>Score</span>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: '#1E2478' }}>{currentResult.scoreLabel}</div>
                                            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                                {currentResult.answers.map((ans, aIdx) => (
                                                    <div key={aIdx} style={{ width: 18, height: 18, borderRadius: '50%', background: ans.tag === 'Ideal' ? '#00BA63' : '#ef4444', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                                                        {ans.tag === 'Ideal' ? '✓' : '✕'}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ background: '#FEF3C7', color: '#92400E', padding: '8px 16px', borderRadius: 12, fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        👍 Good Effort!
                                    </div>
                                </div>

                                {/* Answers Accordion List */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: '#1E2478' }}>Your Answers</div>
                                    {currentResult.answers.map((ans, aIdx) => (
                                        <div key={aIdx} style={{ background: '#FAF8FC', borderRadius: 12, border: '1.5px solid #EEDFEE', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ fontWeight: 800, color: '#4C2A85', fontSize: 12 }}>Q{aIdx + 1}</span>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1E2478' }}>{ans.questionText}</span>
                                                </div>
                                                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: ans.tag === 'Ideal' ? '#DCFCE7' : ans.tag === 'Lazy' ? '#FEE2E2' : '#FEF3C7', color: ans.tag === 'Ideal' ? '#166534' : ans.tag === 'Lazy' ? '#991B1B' : '#92400E' }}>
                                                    {ans.tag}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#6B7280' }}>You chose: <strong style={{ color: '#1E2478' }}>"{ans.selected}"</strong></div>
                                            {ans.tag !== 'Ideal' && (
                                                <div style={{ background: '#F5F3FF', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #4C2A85', fontSize: 12, color: '#4C2A85', lineHeight: 1.4, marginTop: 4 }}>
                                                    🎯 <strong>Ideal response:</strong> {ans.idealExplanation}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Your Timeline vs Priya's Timeline (2 Columns) */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#1E2478' }}>Your Timeline vs. Priya’s Timeline</div>
                                        <div style={{ fontSize: 12, color: '#6B7280' }}>Same exam, same 2 weeks - two very paths to exam hall.</div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                        {/* Your Path */}
                                        <div style={{ background: '#FFF7ED', borderRadius: 14, padding: 18, border: '1.5px solid #FFEDD5' }}>
                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#C2410C', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#EA580C', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>S</div>
                                                Your Path
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                {currentResult.pathComparison.yourPath.map((item, idx) => (
                                                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#431407', lineHeight: 1.4 }}>
                                                        <span style={{ color: '#EA580C', fontSize: 16, lineHeight: 1 }}>•</span>
                                                        <span>{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Priya's Path */}
                                        <div style={{ background: '#F0FDF4', borderRadius: 14, padding: 18, border: '1.5px solid #DCFCE7' }}>
                                            <div style={{ fontSize: 14, fontWeight: 800, color: '#15803D', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#16A34A', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>P</div>
                                                Priya's Path
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                {currentResult.pathComparison.priyasPath.map((item, idx) => (
                                                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#14532D', lineHeight: 1.4 }}>
                                                        <span style={{ color: '#16A34A', fontSize: 16, lineHeight: 1 }}>•</span>
                                                        <span>{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer buttons */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 10 }}>
                                    <button
                                        onClick={() => setActiveTab('overview')}
                                        style={{ background: 'white', color: '#4C2A85', border: '1.5px solid #D0C7E3', borderRadius: 99, padding: '10px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                                    >
                                        ← Not Now
                                    </button>
                                    <button
                                        onClick={nextExercise}
                                        style={{ background: '#4C2A85', color: 'white', border: 'none', borderRadius: 99, padding: '10px 26px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                                    >
                                        {activeExerciseIndex + 1 < totalExercises ? `Next Exercise ${activeExerciseIndex + 2} →` : 'Complete Module ✓'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* 4. PROMPTS VIEW */}
                        {activeTab === 'prompts' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: '#1e2478', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Cpu size={14} color="#612D9B" /> Exercise Prompt (5-7 MCQs Generator)
                                    </div>
                                    <div style={{ fontSize: 12, color: '#334155', fontFamily: 'monospace', background: 'white', padding: 12, borderRadius: 8, border: '1px solid #cbd5e1', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                        {mod.exercisePrompt || 'No exercise prompt set'}
                                    </div>
                                </div>

                                <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: '#1e2478', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Cpu size={14} color="#612D9B" /> Path Comparison Prompt ("Your Path" vs "Priya's Path")
                                    </div>
                                    <div style={{ fontSize: 12, color: '#334155', fontFamily: 'monospace', background: 'white', padding: 12, borderRadius: 8, border: '1px solid #cbd5e1', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                        {mod.pathPrompt || 'No path comparison prompt set'}
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

            </div>
        </div>
    );
}

/* ─────────────── MAIN CRAP EDITOR ─────────────── */
export default function CrapEditor() {
    const [section1, setSection1] = useState(DEFAULT_SECTION1);
    const [modules, setModules] = useState(DEFAULT_MODULES);
    const [timelineItems, setTimelineItems] = useState(DEFAULT_TIMELINE);
    const [activeTimelineStep, setActiveTimelineStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [saveError, setSaveError] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [loading, setLoading] = useState(true);
    const ACCENT = '#612D9B';

    // Section 3 Assessment State
    const [section3Questions, setSection3Questions] = useState([]);
    const [section3Prompts, setSection3Prompts] = useState(() => {
        try {
            const saved = localStorage.getItem('crap_v2_section3_prompts');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });
    const [loadingS3, setLoadingS3] = useState(false);

    // Section 4 Roadmap State
    const [roadmapConfig, setRoadmapConfig] = useState(() => {
        try {
            const saved = localStorage.getItem('crap_v2_roadmap_config');
            return saved ? JSON.parse(saved) : DEFAULT_ROADMAP_CONFIG;
        } catch {
            return DEFAULT_ROADMAP_CONFIG;
        }
    });

    const handleSaveRoadmap = async (newConfig) => {
        setRoadmapConfig(newConfig);
        try {
            localStorage.setItem('crap_v2_roadmap_config', JSON.stringify(newConfig));
        } catch (e) {
            console.warn('Failed to save roadmap config to localStorage', e);
        }

        // Also sync prompt to backend if configured
        if (newConfig?.roadmapPrompt) {
            try {
                await CrapEditorApi.saveRoadmapPrompt(newConfig.roadmapPrompt);
                console.log('[CrapEditor] Section 4 Roadmap prompt saved to backend');
            } catch (err) {
                console.warn('[CrapEditor] Failed to save Section 4 prompt to backend:', err);
            }
        }

        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
    };

    // Section 5 Entry Level State
    const [entryLevelConfig, setEntryLevelConfig] = useState(() => {
        try {
            const saved = localStorage.getItem('crap_v2_entry_level_config');
            return saved ? JSON.parse(saved) : DEFAULT_ENTRY_LEVEL_CONFIG;
        } catch {
            return DEFAULT_ENTRY_LEVEL_CONFIG;
        }
    });

    const handleSetEntryLevelConfig = (val) => {
        const next = typeof val === 'function' ? val(entryLevelConfig) : val;
        setEntryLevelConfig(next);
        try {
            localStorage.setItem('crap_v2_entry_level_config', JSON.stringify(next));
        } catch (e) {
            console.warn('Failed to save entry level config', e);
        }
        setIsDirty(true);
    };

    // Section 6 Medium Level State
    const [mediumLevelConfig, setMediumLevelConfigRaw] = useState(() => {
        try {
            const saved = localStorage.getItem('crap_v2_medium_level_config');
            return saved ? JSON.parse(saved) : DEFAULT_MEDIUM_LEVEL_CONFIG;
        } catch { return DEFAULT_MEDIUM_LEVEL_CONFIG; }
    });
    const setMediumLevelConfig = (val) => {
        const next = typeof val === 'function' ? val(mediumLevelConfig) : val;
        setMediumLevelConfigRaw(next);
        try { localStorage.setItem('crap_v2_medium_level_config', JSON.stringify(next)); } catch (e) { console.warn(e); }
        setIsDirty(true);
    };

    // Section 7 Advanced Level State
    const [advancedLevelConfig, setAdvancedLevelConfigRaw] = useState(() => {
        try {
            const saved = localStorage.getItem('crap_v2_advanced_level_config');
            return saved ? JSON.parse(saved) : DEFAULT_ADVANCED_LEVEL_CONFIG;
        } catch { return DEFAULT_ADVANCED_LEVEL_CONFIG; }
    });
    const setAdvancedLevelConfig = (val) => {
        const next = typeof val === 'function' ? val(advancedLevelConfig) : val;
        setAdvancedLevelConfigRaw(next);
        try { localStorage.setItem('crap_v2_advanced_level_config', JSON.stringify(next)); } catch (e) { console.warn(e); }
        setIsDirty(true);
    };

    // Section 8 Internships State
    const [internshipsConfig, setInternshipsConfigRaw] = useState(() => {
        try {
            const saved = localStorage.getItem('crap_v2_internships_config');
            return saved ? JSON.parse(saved) : DEFAULT_INTERNSHIPS_CONFIG;
        } catch { return DEFAULT_INTERNSHIPS_CONFIG; }
    });
    const setInternshipsConfig = (val) => {
        const next = typeof val === 'function' ? val(internshipsConfig) : val;
        setInternshipsConfigRaw(next);
        try { localStorage.setItem('crap_v2_internships_config', JSON.stringify(next)); } catch (e) { console.warn(e); }
        setIsDirty(true);
    };

    // Section 9/10 CV State
    const [cvConfig, setCvConfigRaw] = useState(() => {
        try {
            const saved = localStorage.getItem('crap_v2_cv_config');
            return saved ? JSON.parse(saved) : DEFAULT_CV_CONFIG;
        } catch { return DEFAULT_CV_CONFIG; }
    });
    const setCvConfig = (val) => {
        const next = typeof val === 'function' ? val(cvConfig) : val;
        setCvConfigRaw(next);
        try { localStorage.setItem('crap_v2_cv_config', JSON.stringify(next)); } catch (e) { console.warn(e); }
        setIsDirty(true);
    };

    const fetchSection3Questions = async () => {
        setLoadingS3(true);
        try {
            console.log('[CrapEditor] Fetching Section 3 questions for moduleId: assessment-modules...');
            const qRes = await CrapEditorApi.getSection3Questions('assessment-modules');
            const resData = qRes.data?.data || qRes.data;
            const items = Array.isArray(resData?.items) ? resData.items
                : Array.isArray(resData?.questions) ? resData.questions
                    : Array.isArray(resData) ? resData
                        : [];
            const sorted = orderQuestionsLogically(items);
            setSection3Questions(sorted);

            // Extract prompts from backend if present
            const backendPrompts = {};
            sorted.forEach(q => {
                if (q.prompt) {
                    backendPrompts[q.id] = q.prompt;
                }
            });
            if (Object.keys(backendPrompts).length > 0) {
                setSection3Prompts(prev => ({ ...backendPrompts, ...prev }));
            }
            console.log('[CrapEditor] Successfully fetched Section 3 questions:', sorted.length);
        } catch (err) {
            console.error('[CrapEditor] Failed to fetch Section 3 questions:', err);
        } finally {
            setLoadingS3(false);
        }
    };

    const handleSaveS3Prompt = async (questionId, prompt) => {
        const updated = { ...section3Prompts, [questionId]: prompt };
        setSection3Prompts(updated);
        try {
            localStorage.setItem('crap_v2_section3_prompts', JSON.stringify(updated));
        } catch (e) {
            console.warn('LocalStorage save failed:', e);
        }

        // Save prompt directly to backend question record
        const res = await CrapEditorApi.updateSection3Question('assessment-modules', questionId, { prompt });
        console.log(`[CrapEditor] Saved prompt to backend for question ${questionId}`);

        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
        return res;
    };

    const handleSaveAllS3Prompts = async () => {
        try {
            localStorage.setItem('crap_v2_section3_prompts', JSON.stringify(section3Prompts));
        } catch (e) {
            console.warn('LocalStorage save failed:', e);
        }

        // Save each modified prompt to backend
        try {
            const promises = Object.entries(section3Prompts).map(([qId, prompt]) =>
                CrapEditorApi.updateSection3Question('assessment-modules', qId, { prompt }).catch(err =>
                    console.warn(`[CrapEditor] Error saving prompt for ${qId}:`, err)
                )
            );
            await Promise.all(promises);
            console.log('[CrapEditor] All prompts synced with backend');
        } catch (err) {
            console.warn('[CrapEditor] Batch prompt save warning:', err);
        }

        setIsDirty(false);
        setSaveStatus('success');
        setTimeout(() => setSaveStatus(null), 3000);
    };

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                const metaRes = await CrapEditorApi.getMetadata();
                const metaData = metaRes.data?.data || metaRes.data;
                if (metaData) {
                    const introDescriptionObj = metaData.text?.find(t => t.key === 'introDescription');
                    setSection1({
                        introVideoUrl: metaData.introVideo?.url || metaData.introVideo || '',
                        introVideoR2Key: metaData.introVideo?.r2Key || metaData.introVideoR2Key || '',
                        introDescription: introDescriptionObj ? introDescriptionObj.value : DEFAULT_SECTION1.introDescription
                    });
                }
                // Load Section 2 modules
                try {
                    console.log("[CrapEditor] Fetching Section 2 modules list...");
                    const modulesRes = await CrapEditorApi.getSection2Modules();
                    const resData = modulesRes.data?.data || modulesRes.data;
                    console.log("[CrapEditor] Raw modules API response:", JSON.stringify(resData, null, 2));
                    const modulesData = Array.isArray(resData?.items) ? resData.items
                        : Array.isArray(resData?.modules) ? resData.modules
                            : Array.isArray(resData) ? resData
                                : [];
                    if (modulesData && modulesData.length > 0) {
                        const mappedModules = modulesData.map((m, idx) => ({
                            id: m.id,
                            sectionId: m.sectionId || '2',
                            name: m.name || '',
                            videoR2Key: m.videoR2Key || m.introVideoR2Key || '',
                            // Capture video URL from any field the API might return it under
                            videoUrl: m.videoUrl || m.introVideoUrl || m.video?.url || m.video?.presignedUrl || m.presignedUrl || '',
                            exercisePrompt: m.exercisePrompt || '',
                            pathPrompt: m.pathPrompt || '',
                            order: m.order ?? (idx + 1)
                        })).sort((a, b) => (a.order || 0) - (b.order || 0));
                        setModules(mappedModules);
                    }
                } catch (modErr) {
                    console.error("[CrapEditor] Failed to load Section 2 modules from API", modErr);
                }

                // Load Section 3 Assessment questions
                await fetchSection3Questions();

                // Load Prompts for Roadmap, Focus Areas (easy/medium/advanced), Self Projects, Certifications, and Internships
                try {
                    const [roadPromptRes, easyRes, medRes, hardRes, selfProjRes, certsRes, internRes] = await Promise.allSettled([
                        CrapEditorApi.getRoadmapPrompt(),
                        CrapEditorApi.getFocusAreasPrompt('easy'),
                        CrapEditorApi.getFocusAreasPrompt('medium'),
                        CrapEditorApi.getFocusAreasPrompt('advanced'),
                        CrapEditorApi.getSelfProjectsPrompt(),
                        CrapEditorApi.getCertificationsPrompt(),
                        CrapEditorApi.getInternshipsPrompt()
                    ]);

                    if (roadPromptRes.status === 'fulfilled') {
                        const val = roadPromptRes.value.data?.data?.prompt || roadPromptRes.value.data?.prompt;
                        if (val) setRoadmapConfig(prev => ({ ...prev, roadmapPrompt: val }));
                    }
                    if (easyRes.status === 'fulfilled') {
                        const val = easyRes.value.data?.data?.prompt || easyRes.value.data?.prompt;
                        if (val) setEntryLevelConfig(prev => ({ ...prev, aiPrompt: val }));
                    }
                    if (medRes.status === 'fulfilled') {
                        const val = medRes.value.data?.data?.prompt || medRes.value.data?.prompt;
                        if (val) setMediumLevelConfig(prev => ({ ...prev, aiPrompt: val }));
                    }
                    if (hardRes.status === 'fulfilled') {
                        const val = hardRes.value.data?.data?.prompt || hardRes.value.data?.prompt;
                        if (val) setAdvancedLevelConfig(prev => ({ ...prev, aiPrompt: val }));
                    }
                    if (selfProjRes.status === 'fulfilled') {
                        const val = selfProjRes.value.data?.data?.prompt || selfProjRes.value.data?.prompt;
                        if (val) setEntryLevelConfig(prev => ({ ...prev, selfProjectsPrompt: val }));
                    }
                    if (certsRes.status === 'fulfilled') {
                        const val = certsRes.value.data?.data?.prompt || certsRes.value.data?.prompt;
                        if (val) setEntryLevelConfig(prev => ({ ...prev, certificationsPrompt: val }));
                    }
                    if (internRes.status === 'fulfilled') {
                        const val = internRes.value.data?.data?.prompt || internRes.value.data?.prompt;
                        if (val) setInternshipsConfig(prev => ({ ...prev, aiPrompt: val }));
                    }
                } catch (pErr) {
                    console.warn('[CrapEditor] Could not fetch saved prompts:', pErr);
                }
            } catch (err) {
                console.error("Failed to load initial metadata", err);
            } finally {
                setLoading(false);
            }
        };
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setSaveStatus(null);
        setSaveError('');
        try {
            // 1. Save metadata (Section 1)
            await CrapEditorApi.saveMetadata({
                introVideoR2Key: section1.introVideoR2Key,
                text: [{ key: 'introDescription', value: section1.introDescription }]
            });

            // 2. Save Section 2 modules
            for (let i = 0; i < modules.length; i++) {
                const mod = modules[i];
                const payload = {
                    name: mod.name,
                    videoR2Key: mod.videoR2Key || '',
                    exercisePrompt: mod.exercisePrompt || '',
                    pathPrompt: mod.pathPrompt || '',
                    order: mod.order ?? (i + 1)
                };

                if (mod.id) {
                    try {
                        await CrapEditorApi.updateSection2Module(mod.id, payload);
                    } catch (updateErr) {
                        console.error(`Failed to update module ${mod.id}`, updateErr);
                        throw updateErr;
                    }
                } else {
                    try {
                        const createRes = await CrapEditorApi.createSection2Module(payload);
                        const created = createRes.data?.data || createRes.data;
                        if (created?.id) {
                            mod.id = created.id;
                            if (created.videoUrl) mod.videoUrl = created.videoUrl;
                        }
                    } catch (createErr) {
                        console.error('Failed to create module', createErr);
                        throw createErr;
                    }
                }
            }

            // 2.5 Save Section 3 Assessment prompts if present
            if (section3Prompts && Object.keys(section3Prompts).length > 0) {
                const s3Promises = Object.entries(section3Prompts).map(([qId, promptVal]) => {
                    if (promptVal) {
                        return CrapEditorApi.updateSection3Question('assessment-modules', qId, { prompt: promptVal }).catch(err => {
                            console.warn(`[CrapEditor] Could not sync prompt for question ${qId}:`, err);
                        });
                    }
                    return Promise.resolve();
                });
                await Promise.all(s3Promises);
            }

            // 3. Save Section 4 Roadmap prompt if present
            if (roadmapConfig?.roadmapPrompt) {
                try {
                    await CrapEditorApi.saveRoadmapPrompt(roadmapConfig.roadmapPrompt);
                } catch (rErr) {
                    console.warn('[CrapEditor] Could not sync roadmap prompt:', rErr);
                }
            }

            // 4. Save Section 5 prompts
            if (entryLevelConfig?.aiPrompt) {
                try {
                    await CrapEditorApi.saveFocusAreasPrompt('easy', entryLevelConfig.aiPrompt);
                } catch (s5Err) {
                    console.warn('[CrapEditor] Could not sync focus areas easy prompt:', s5Err);
                }
            }
            if (mediumLevelConfig?.aiPrompt) {
                try {
                    await CrapEditorApi.saveFocusAreasPrompt('medium', mediumLevelConfig.aiPrompt);
                } catch (mErr) {
                    console.warn('[CrapEditor] Could not sync focus areas medium prompt:', mErr);
                }
            }
            if (advancedLevelConfig?.aiPrompt) {
                try {
                    await CrapEditorApi.saveFocusAreasPrompt('advanced', advancedLevelConfig.aiPrompt);
                } catch (hErr) {
                    console.warn('[CrapEditor] Could not sync focus areas advanced prompt:', hErr);
                }
            }
            if (entryLevelConfig?.selfProjectsPrompt) {
                try {
                    await CrapEditorApi.saveSelfProjectsPrompt(entryLevelConfig.selfProjectsPrompt);
                } catch (spErr) {
                    console.warn('[CrapEditor] Could not sync self projects prompt:', spErr);
                }
            }
            if (entryLevelConfig?.certificationsPrompt) {
                try {
                    await CrapEditorApi.saveCertificationsPrompt(entryLevelConfig.certificationsPrompt);
                } catch (certErr) {
                    console.warn('[CrapEditor] Could not sync certifications prompt:', certErr);
                }
            }

            // 5. Save Section 6 Internships prompt if present
            if (internshipsConfig?.aiPrompt) {
                try {
                    await CrapEditorApi.saveInternshipsPrompt(internshipsConfig.aiPrompt);
                } catch (s6Err) {
                    console.warn('[CrapEditor] Could not sync internships prompt:', s6Err);
                }
            }

            setSaveStatus('success');
            setIsDirty(false);
            setTimeout(() => setSaveStatus(null), 3000);
        } catch (error) {
            console.error('Save failed', error);
            const msg = error?.response?.data?.message || error.message || 'Save failed. Check your connection.';
            setSaveError(msg);
            setSaveStatus('error');
        } finally {
            setSaving(false);
        }
    };

    const handleActiveStepChange = (itemId) => {
        setActiveTimelineStep(itemId);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 'calc(100vh - 40px)', fontFamily: "'Outfit', 'Inter', sans-serif", background: '#faf9ff' }}>

            {/* ─── TOP BAR ─── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', background: 'white', borderBottom: '1px solid #f0ebfa', flexShrink: 0, gap: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                {/* Left: Title */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, #612D9B, #9F6FD5)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(97,45,155,0.2)' }}>
                        <Edit3 size={15} color="white" />
                    </div>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#1e2478', lineHeight: 1.2, letterSpacing: '-0.3px' }}>C-RAP Admin Workspace</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.2 }}>
                            Content & AI Prompt Configuration Hub
                        </div>
                    </div>
                </div>

                {/* Right: Save button & Cloud Sync Status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                    {saveStatus === 'success' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#059669', fontWeight: 600, animation: 'fadeIn 0.2s' }}>
                            <Check size={14} /> Saved to cloud
                        </div>
                    )}
                    {saveStatus === 'error' && (
                        <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, maxWidth: 220 }}>
                            ✕ {saveError}
                        </div>
                    )}
                    {isDirty && (
                        <button onClick={handleSave} disabled={saving}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '8px 18px', borderRadius: 999,
                                background: saving ? '#9ca3af' : ACCENT,
                                color: 'white', border: 'none',
                                fontSize: 12, fontWeight: 700,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: saving ? 'none' : '0 2px 10px rgba(97,45,155,0.25)',
                            }}
                            onMouseEnter={(e) => !saving && (e.currentTarget.style.transform = 'scale(1.02)')}
                            onMouseLeave={(e) => !saving && (e.currentTarget.style.transform = 'scale(1)')}>
                            {saving
                                ? <><Loader size={12} className="spin" /> Syncing...</>
                                : <><Save size={12} /> Save Changes</>
                            }
                        </button>
                    )}
                    {!isDirty && saveStatus !== 'success' && (
                        <div style={{ fontSize: 11, color: '#612D9B', fontWeight: 600, background: '#f5faf8', padding: '6px 12px', borderRadius: 99, border: '1px solid #e8e4f2' }}>
                            ✓ Cloud Synced
                        </div>
                    )}
                </div>
            </div>

            {/* ─── MAIN CONTENT AREA (Dedicated Editor View) ─── */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12 }}>
                        <Loader size={28} color={ACCENT} className="spin" />
                        <span style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600 }}>Loading workspace...</span>
                    </div>
                ) : (
                    <div style={{ height: '100%', overflowY: 'auto', background: '#faf9ff' }}>
                        <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 20px' }}>
                            <TimelineEditor
                                items={timelineItems}
                                onChange={(v) => { setTimelineItems(v); setIsDirty(true); }}
                                section1={section1}
                                setSection1={(v) => { setSection1(v); setIsDirty(true); }}
                                modules={modules}
                                setModules={(v) => { setModules(v); setIsDirty(true); }}
                                section3Questions={section3Questions}
                                setSection3Questions={setSection3Questions}
                                section3Prompts={section3Prompts}
                                setSection3Prompts={setSection3Prompts}
                                loadingS3={loadingS3}
                                fetchSection3Questions={fetchSection3Questions}
                                handleSaveS3Prompt={handleSaveS3Prompt}
                                handleSaveAllS3Prompts={handleSaveAllS3Prompts}
                                roadmapConfig={roadmapConfig}
                                setRoadmapConfig={(v) => { setRoadmapConfig(v); setIsDirty(true); }}
                                handleSaveRoadmap={handleSaveRoadmap}
                                entryLevelConfig={entryLevelConfig}
                                setEntryLevelConfig={handleSetEntryLevelConfig}
                                mediumLevelConfig={mediumLevelConfig}
                                setMediumLevelConfig={setMediumLevelConfig}
                                advancedLevelConfig={advancedLevelConfig}
                                setAdvancedLevelConfig={setAdvancedLevelConfig}
                                internshipsConfig={internshipsConfig}
                                setInternshipsConfig={setInternshipsConfig}
                                cvConfig={cvConfig}
                                setCvConfig={setCvConfig}
                                onActiveStepChange={handleActiveStepChange}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
